import React, { useState, useEffect, useCallback, useRef } from 'react';
import BrainLoader from '../../components/BrainLoader';
import {
  Check, Clock, Circle, CheckCircle2, ClipboardCheck, KeyRound, Users,
  FileSignature, Wallet, ShieldCheck, Receipt, Bed, Maximize2, Tag, MapPin,
  ArrowUpRight, ArrowDownRight, ChevronRight, ChevronLeft, FileText, CreditCard, Phone, Mail,
  Download, LayoutGrid, Home, Wrench, Package, Info, RefreshCw,
  ChevronDown, Building2, TrendingUp, Sparkles, Eye,
  Copy, Hammer, Landmark, CalendarDays, BedDouble, Upload, PlusCircle, Briefcase,
} from 'lucide-react';
import { theme } from '../shared/theme';
import { M, T, eyebrow } from '../shared/pmTokens';
import ProjectTimeline from '../../components/ProjectTimeline';
import Segmented, { Tab } from '../shared/Segmented';
import { portalFetch, PORTAL_API } from '../shared/api';
import { DEMO_PM_UNITS } from '../demo/demoPmData';
import { buildSamplePdf } from '../demo/demoPdf';
import { FALLBACK_PROJECT_IMAGES } from '../../components/PropertyView';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { useLanguage } from '../../components/LanguageContext';
import { appLocale, appLanguage, trData } from '../../i18n/locale';
import { useAutoRefresh } from '../shared/useAutoRefresh';
import { readPmUnits, writePmUnits } from '../shared/pmCache';
import { AirbnbPortfolioSection, AirbnbUnitTab, useAirbnbIncome } from './AirbnbIncome';
import { unitCategory, hasBothCategories } from '../../utils/unitCategory';
import { leaseBook } from '../../components/portfolioMetrics';
import {
  OWNER_UNIT, LIFECYCLE_STEPS, RENT_PAYMENTS, LifecycleStep, RentPayment, RentStatus,
} from '../shared/mockData';
import '../shared/portals.css';

// ============================================================
// Property Management (Owner) — managd-styled, tabbed.
//   Top tabs:  Dashboard · Properties · Documents · Expenses
//   Open a property → top sub-tabs:
//              Overview · Inspection · Maintenance · Inventory
// Reads the local emulator via the dev API; falls back to mock
// if the API is down. Inspection / Maintenance / Inventory have
// no backend fields yet, so they show clearly-labelled sample
// data with clean empty states — ready to wire up later.
// ============================================================

const DEFAULT_OWNER_ID = 'mirco-owner-001';

// ------------------------------------------------------------------
// Brand palette. The corporate guide allows exactly three colours —
// #D6B160 gold, #FFFFFF, #000000 — with WHITE as the ground and gold as
// the only accent. So there are no navies, no greens and no greys of
// their own here: every neutral is black at reduced alpha, which the
// guide sanctions for small type, and every dark surface is #000 with a
// gold wash over it ("alpha over black is fine").
//
// #D6B160 on white is only ~2.3:1, so gold can never carry small text.
// `goldInk` / `goldDeep` are the guide's own darkened golds, used
// wherever something gold-toned has to be read at small sizes.
//
// success / warning / danger stay: the guide permits functional state
// colours, and a ~60-year-old reader depends on them to tell a rented
// unit from a vacant one at a glance.
// ------------------------------------------------------------------
const IMG = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=60';

// Backend statuses arrive lowercase ("active"), which reads as unfinished
// next to the mapped labels. Title-case anything we don't translate.
const titleCase = (s: string) => s.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1));

// ------------------------------------------------------------------
// Per-unit facts (pmRoutes → buildUnitFacts)
//
// Everything here is computed by the backend from a named source, so the
// screen can always say WHERE a number came from. Absent on demo/mock units
// and on any older payload, so every read goes through factsOf() and every
// display checks for null instead of printing a zero.
// ------------------------------------------------------------------
interface UnitFacts {
  serviceCharge?: {
    psf: number | null; annual: number | null; source: string | null; sqft: number | null;
    bookedAsExpense?: boolean;
    /** false until handover — Mollak bills from then, so it is not a cost yet. */
    payableYet?: boolean;
  };
  construction?: {
    offplan: boolean | null; percent: number | null; percentSource?: string | null;
    status?: string | null; handover?: string | null; handoverSource?: string | null;
    developer?: string | null; lastSync?: string | null;
  };
  expectedRent?: { available: boolean; annual?: number; monthly?: number; psf?: number; sampleSize?: number; confidence?: string } | null;
  mortgage?: { status?: 'yes' | 'no' | null; lender?: string | null; outstanding?: number | null; monthlyPayment?: number | null; updatedAt?: string | null };
  letting?: { mode?: 'long_term' | 'short_term' | null; brand?: string | null };
  photo?: { url: string; kind: 'unit' | 'project' } | null;
  netIncome?: { annualRent: number; expenses: number; serviceCharge: number; net: number; serviceChargeKnown: boolean };
}

const factsOf = (u: any): UnitFacts => (u && u.facts) || {};

/**
 * Under construction is a DLD-derived fact (project registry percent + status,
 * handover date, developer feed — see dldService.isUnitOffplan), NOT the
 * hand-entered occupancy flag: half the off-plan units in the portfolio are
 * marked "Vacant", which is equally true of a tower that is 30% built.
 */
const isUnderConstruction = (u: any) => factsOf(u).construction?.offplan === true;

/** "Q3 2027" — quarters are how handover is quoted in Dubai. */
const quarterOf = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
};

const AED = (n: number) => `AED ${Math.round(n).toLocaleString('en-AE')}`;

// Where a figure came from, in the owner's words. An unrecognised source is
// dropped rather than printed raw — "env" means nothing to a reader.
const sourceLabel = (source: string | null | undefined, t: (k: string) => string): string => {
  switch (source) {
    case 'unit': return t('pmSourceUnit');
    case 'dld_project_registry': return t('pmSourceDldRegistry');
    case 'dld_project_progress': return t('pmSourceDldProgress');
    case 'construction_feed': return t('pmSourceFeed');
    case 'dld_index': return t('pmSourceIndex');
    case 'invoice': return t('pmSourceInvoice');
    case 'manual': return t('pmSourceManual');
    default: return '';
  }
};

const confidenceLabel = (c: string | undefined, t: (k: string) => string) =>
  c === 'high' ? t('pmConfHigh') : c === 'medium' ? t('pmConfMedium') : c === 'low' ? t('pmConfLow') : '';

// Clipboard, with the execCommand fallback that older in-app webviews need —
// navigator.clipboard is undefined outside a secure context.
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/**
 * Read an image file as a data URL, downscaled to fit `maxPx` on its long
 * edge and re-encoded until it is under `maxBytes`.
 *
 * Necessary rather than nice: a unit with no Drive folder and no Storage
 * bucket stores media as base64 in Firestore, where the backend rejects
 * anything over 1MB — and every photo a phone takes is 3-5MB. Videos are
 * passed through untouched (they need Drive or Storage either way).
 */
async function fileToUploadableDataUrl(file: File, maxPx = 1600, maxBytes = 900000): Promise<string> {
  const readAsDataUrl = () => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
  const raw = await readAsDataUrl();
  if (!file.type.startsWith('image/') || raw.length * 0.75 <= maxBytes) return raw;

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('decode failed'));
    i.src = raw;
  });
  const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return raw;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  for (const q of [0.82, 0.7, 0.6, 0.5]) {
    const out = canvas.toDataURL('image/jpeg', q);
    if (out.length * 0.75 <= maxBytes) return out;
  }
  return canvas.toDataURL('image/jpeg', 0.4);
}

// A small gold-tinted tag: construction, short-let, mortgage.
const FactChip: React.FC<{ icon?: React.ComponentType<any>; label: string; tone?: 'gold' | 'plain' }> = ({ icon: Icon, label, tone = 'gold' }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap',
    fontSize: T.micro, fontWeight: 800,
    color: tone === 'gold' ? M.goldDeep : M.ink2,
    background: tone === 'gold' ? M.tint : M.track,
    border: `1px solid ${tone === 'gold' ? M.tintEdge : 'transparent'}`,
    padding: '5px 11px', borderRadius: 99,
  }}>
    {Icon && <Icon size={13} />} {label}
  </span>
);

// Copy-to-clipboard control for the property name. Owners read these names out
// to banks, Ejari and their own accountants, so the name has to leave the app
// as text — a name you can only photograph is not a name you can use.
const CopyNameButton: React.FC<{ name: string; onDark?: boolean }> = ({ name, onDark }) => {
  const { t } = useLanguage();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      title={t('pmCopyName')}
      aria-label={t('pmCopyName')}
      onClick={async (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (await copyText(name)) { setDone(true); window.setTimeout(() => setDone(false), 1600); }
      }}
      className="pt-tap"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0, cursor: 'pointer',
        border: 'none', borderRadius: 99, padding: done ? '5px 11px' : '6px 8px',
        fontSize: T.micro, fontWeight: 800,
        background: onDark ? 'rgba(255,255,255,0.16)' : M.tint,
        color: onDark ? '#fff' : M.goldDeep,
      }}
    >
      {done ? <><Check size={14} /> {t('pmCopied')}</> : <Copy size={15} />}
    </button>
  );
};

// One consistent status chip (with a colour dot) instead of ad-hoc pills.
//
// `unit` is optional and only used to answer one question the stored status
// cannot: a unit in a tower that has not handed over is neither rented nor
// vacant — it is under construction, and calling it "Vacant" told owners a
// building exists that does not.
const StatusPill: React.FC<{ status?: string; unit?: any }> = ({ status, unit }) => {
  const { t } = useLanguage();
  if (unit && isUnderConstruction(unit)) {
    const pct = factsOf(unit).construction?.percent;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap', fontSize: T.micro, fontWeight: 800, color: M.goldDeep, background: M.tint, border: `1px solid ${M.tintEdge}`, padding: '5px 11px', borderRadius: 99 }}>
        <Hammer size={13} />
        {t('pmUnderConstruction')}{pct != null ? ` · ${Math.round(pct)}%` : ''}
      </span>
    );
  }
  const rented = status === 'Rented';
  const color = rented ? M.okInk : M.ink3;
  const bg = rented ? M.okBg : M.track;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap', fontSize: T.micro, fontWeight: 800, color, background: bg, padding: '6px 12px', borderRadius: 99 }}>
      <span style={{ width: 8, height: 8, borderRadius: 99, background: color }} />
      {rented ? t('pmRented') : (status === 'Vacant' ? t('vacant') : (status ? trData(titleCase(status)) : t('vacant')))}
    </span>
  );
};

const STEP_ICONS: Record<string, React.ComponentType<any>> = {
  snagging: ClipboardCheck, handover: KeyRound, placement: Users, contracts: FileSignature, rental: Wallet,
};
const STEP_META: Record<string, { label: string; description: string }> = {
  snagging: { label: 'Snagging', description: 'Defect inspection & fixes.' },
  handover: { label: 'Handover', description: 'Keys received, DEWA activated.' },
  placement: { label: 'Tenant Placement', description: 'Tenant found & Ejari.' },
  contracts: { label: 'Contracts', description: 'Tenancy contract signed.' },
  rental: { label: 'Rental Tracking', description: 'Cheque schedule & payouts.' },
};
// Translation keys for the same steps. STEP_META's English text stays as the
// view-model default (it is also produced by the shared mock data); StepRow
// prefers these keys so the step reads in the investor's language.
const STEP_LABEL_KEYS: Record<string, { label: string; description: string }> = {
  snagging: { label: 'pmStepSnagging', description: 'pmStepSnaggingDesc' },
  handover: { label: 'pmStepHandover', description: 'pmStepHandoverDesc' },
  placement: { label: 'pmStepPlacement', description: 'pmStepPlacementDesc' },
  contracts: { label: 'pmStepContracts', description: 'pmStepContractsDesc' },
  rental: { label: 'pmStepRental', description: 'pmStepRentalDesc' },
};

interface Expense { id: string; type: string; description: string; amount: number; status: string; date?: string; period?: string; }
interface TenantInfo { id?: string; name?: string; emiratesId?: string; phone?: string; email?: string; ejariNo?: string; contractStart?: string; contractEnd?: string; }
interface DocVM { id: string; name: string; type: string; date?: string; fileUrl?: string; }
interface UnitVM {
  id: string; name: string; community: string; propertyType: string; size: string;
  price: string; status: string; image: string; ownerName: string;
  steps: LifecycleStep[]; payments: RentPayment[]; expenses: Expense[];
  documents: DocVM[];
  tenancyHistory: { name: string; start?: string; end?: string; renewed: boolean; reason?: string | null }[];
  annualRent: number; nextPayment?: RentPayment; tenant?: TenantInfo;
  details: { label: string; value: string }[]; rentalIndex: { label: string; value: string }[];
  source: 'live' | 'mock';
  inspectionReports: any[]; maintenance: any[]; media: any[];
  /** Where the hero image came from, so a project photo is labelled as one. */
  photoKind: 'unit' | 'project' | 'stock';
  /** The raw unit, kept for the backend-computed `facts` block. */
  unit?: any;
}

function fmt(d?: string): string {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(appLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

// Format either an ISO string or a Firestore timestamp ({ _seconds } / { seconds }).
function fmtTs(v: any): string {
  if (!v) return '';
  if (typeof v === 'string') return fmt(v);
  const secs = v._seconds ?? v.seconds;
  if (typeof secs === 'number') return fmt(new Date(secs * 1000).toISOString());
  return '';
}

/**
 * The same value as a number, for sorting. fmtTs returns a display string, and
 * "05 Mar 2026" does not sort — ordering a portfolio-wide list by it put March
 * above May. Undated records return 0 so they sink to the bottom rather than
 * floating to the top as NaN.
 */
function tsOf(v: any): number {
  if (!v) return 0;
  if (typeof v === 'string') { const n = Date.parse(v); return isNaN(n) ? 0 : n; }
  const secs = v._seconds ?? v.seconds;
  return typeof secs === 'number' ? secs * 1000 : 0;
}

// Normalise a backend maintenance record to the card shape.
// `t` is passed in — this is a plain helper, so it must not call the hook.
function normMaint(m: any, t: (k: string) => string) {
  return {
    id: m.id, title: m.title || t('pmMaintenance'), category: m.category || t('pmGeneral'),
    status: m.status || 'open', cost: Number(m.cost) || 0, detail: m.detail || '',
    date: fmtTs(m.createdAt || m.date), handledBy: m.assignedTeam ? t('pmInternalMaintTeam') : undefined,
    hasFile: m.hasFile, fileUrl: m.fileUrl, fileName: m.fileName,
    aiReport: m.aiReport, aiGenerated: m.aiGenerated,
  };
}
// `label` stays English because callers compare it; `key` is what gets shown.
function maintStatus(s: string) {
  const x = (s || '').toLowerCase();
  if (x === 'completed' || x === 'resolved') return { label: 'Completed', key: 'completed', color: theme.success, bg: theme.successBg };
  if (x === 'in_progress' || x === 'in_review') return { label: 'In progress', key: 'inProgress', color: theme.warning, bg: theme.warningBg };
  return { label: 'Open', key: 'pmStatusOpen', color: M.goldInk, bg: M.tint };
}

/**
 * The placeholder this screen renders against BEFORE its unit has loaded.
 *
 * It states nothing. No tenant, no rent, no status, no documents — because
 * whatever sits here is, for a moment, presented to an owner as the facts of
 * their property. A MOCK_VM used to play this role, carrying a tenant
 * ("Ahmed Khan"), an Emirates ID, an annual rent and a cheque schedule. When
 * /pm/unit/:id did not answer — the dev API down, PM_MODULE_ENABLED off, a
 * cold backend — the catch swallowed the error and left it on screen: a
 * vacant unit reported a tenant and AED 149k collected, with a plausible
 * Emirates ID against it. It has been deleted rather than left for someone to
 * reach for again; silence is the only safe fallback for money.
 */
const EMPTY_VM: UnitVM = {
  id: '', name: '', community: '', propertyType: '', size: '', price: '',
  status: '', image: '', ownerName: '',
  steps: [], payments: [], expenses: [], annualRent: 0,
  tenant: undefined, documents: [], tenancyHistory: [],
  details: [], rentalIndex: [], source: 'mock',
  inspectionReports: [], maintenance: [], media: [], photoKind: 'stock',
};


// fileUrl values from the API are absolute paths from the origin
// (dev: /api/dev/pm/..., prod: /api/pm/...), so strip the API suffix
// (/api or /api/dev) off PORTAL_API to get the origin.
const MEDIA_BASE = PORTAL_API.replace(/\/api(\/dev)?$/, '');
// `t` is passed in — this is a plain helper, so it must not call the hook.
function buildVM(unit: any, owner: any, tenant: any, documents: any[], t: (k: string) => string, maintenance?: any[], media?: any[]): UnitVM {
  const lc = unit.lifecycle || {};
  const order = ['snagging', 'handover', 'placement', 'contracts', 'rental'];
  // The backend seeds all five steps as 'pending' and only ever advances one
  // when somebody POSTs /unit/:id/lifecycle by hand. Nobody does, so a unit that
  // is visibly let — tenant on file, contract dates, cheques being collected —
  // still reported 0% and "Snagging" as its next move. The lifecycle is not
  // secret knowledge: a tenancy proves the keys were handed over, a tenant was
  // placed and a contract was signed, because none of those are possible
  // otherwise. So infer the floor from the evidence and let any explicitly
  // recorded step (done/active, with its real date) override it.
  //
  // Off-plan units are excluded: for those the early steps are genuinely ahead,
  // and a handover date is the one thing an owner must not see faked.
  const tenanted = !!(tenant && (tenant.name || tenant.contractStart || tenant.contractEnd));
  const hasContract = !!(tenant && (tenant.contractStart || tenant.contractEnd || tenant.ejariNo));
  const hasRent = Array.isArray(unit.rentSchedule) && unit.rentSchedule.length > 0;
  const inferred: Record<string, 'done' | 'active'> = {};
  if (tenanted && !isUnderConstruction(unit)) {
    inferred.snagging = 'done'; inferred.handover = 'done'; inferred.placement = 'done';
    if (hasContract) inferred.contracts = 'done';
    // Rental tracking is the steady state a let property lives in, not a task
    // that completes — it stays 'active' for as long as the tenancy runs.
    if (hasRent || hasContract) inferred.rental = 'active';
  }
  const steps: LifecycleStep[] = order.map((key) => {
    const recorded = lc[key]?.status as any;
    const status = (recorded && recorded !== 'pending') ? recorded : (inferred[key] || recorded || 'pending');
    return {
      key, label: STEP_META[key].label, description: STEP_META[key].description,
      status, date: lc[key]?.date ? fmt(lc[key].date) : undefined,
    };
  });
  const payments: RentPayment[] = (unit.rentSchedule || []).map((r: any, i: number) => ({
    id: r.chequeNo || `r${i}`, chequeNo: r.chequeNo || `CHQ-${i + 1}`, amount: r.amount,
    dueDate: fmt(r.dueDate), status: (r.status as RentStatus) || 'scheduled',
    method: appLanguage() === 'de'
      ? (unit.rentSchedule.length === 1 ? '1 Scheck' : `${unit.rentSchedule.length} Schecks`)
      : (unit.rentSchedule.length === 1 ? '1 Cheque' : `${unit.rentSchedule.length} Cheques`),
  }));
  const details = [
    unit.propertyNo && { label: t('pmPropertyNo'), value: String(unit.propertyNo) },
    unit.plotNo && { label: t('pmPlotNo'), value: String(unit.plotNo) },
    unit.areaSqm && { label: t('area'), value: `${unit.areaSqm} sqm` },
    unit.usage && { label: t('pmUsage'), value: String(unit.usage) },
    unit.location && { label: t('location'), value: String(unit.location) },
  ].filter(Boolean) as { label: string; value: string }[];
  // The rent the owner is actually on: the tenancy's own figure, else the sum
  // of the cheque schedule. Hoisted because the Rental Index block shows it.
  const annualRent = tenant?.annualRent || Number(unit.rentSchedule?.reduce?.((s: number, r: any) => s + (r.amount || 0), 0)) || 0;
  // Exactly the fields Dubai's DLD Rental Index form asks for, in its order,
  // so the owner reads them off this screen instead of hunting the contract.
  // Empty values stay EMPTY here rather than becoming '—': "what does the form
  // still need?" is a question worth answering, but answering it with a grid of
  // seven cells where five are dashes reads as a broken screen. The view keeps
  // the intent and splits the two states — values as values, gaps named once as
  // a short "still needed" line.
  const rentalIndex: { label: string; value: string }[] = [
    { label: t('pmDewaPremises'), value: String(unit.dewaPremisesNo || '') },
    { label: t('pmEjariNo'), value: String(tenant?.ejariNo || unit.ejariNo || '') },
    { label: t('pmMakaniNo'), value: String(unit.makaniNo || '') },
    { label: t('pmContractStart'), value: fmt(tenant?.contractStart) },
    { label: t('pmContractEnd'), value: fmt(tenant?.contractEnd) },
    { label: t('pmCurrentAnnualRent'), value: annualRent ? `AED ${annualRent.toLocaleString('en-AE')}` : '' },
    { label: t('propertyType'), value: String(unit.propertyType || '') },
  ];
  // Hero image, best evidence first: a photo someone actually took of this
  // unit, then the curated project shot, then the unit's Dubai Sell listing
  // photo (or one of the same project) that the backend resolved. The stock
  // image is the last resort and is never labelled as the property.
  const mediaList = (media || []).map((m: any) => ({ id: m.id, type: m.type, name: m.name, src: m.url || (m.fileUrl ? MEDIA_BASE + m.fileUrl : '') }));
  const ownPhoto = mediaList.find((m: any) => m.type !== 'video' && m.src);
  const mapPhoto = unitPhoto(unit);
  const factsPhoto = (unit.facts || {}).photo;
  const hero: { url: string; kind: 'unit' | 'project' | 'stock' } =
    ownPhoto ? { url: ownPhoto.src, kind: 'unit' }
      : mapPhoto ? { url: mapPhoto, kind: 'project' }
        : factsPhoto && factsPhoto.url ? { url: factsPhoto.url, kind: factsPhoto.kind === 'unit' ? 'unit' : 'project' }
          : { url: IMG, kind: 'stock' };
  return {
    id: unit.id, name: unit.name, community: unit.community || unit.location || '',
    propertyType: unit.propertyType || t('pmFlat'),
    size: unit.sizeSqft ? `${unit.sizeSqft} Sqft` : unit.areaSqm ? `${unit.areaSqm} sqm` : '',
    price: unitValue(unit) ? `AED ${unitValue(unit).toLocaleString('en-AE')}` : '',
    status: unit.status || 'Active', image: hero.url, photoKind: hero.kind, unit,
    ownerName: owner?.name || t('pmOwner'),
    steps, payments,
    expenses: (unit.expenses || []).map((e: any, i: number) => ({
      id: e.id || `e${i}`, type: e.type || e.category || t('other'), description: e.description || '',
      amount: Number(e.amount) || 0, status: expenseStatus(e), date: e.date ? fmt(e.date) : undefined, period: e.period,
    })),
    annualRent,
    nextPayment: payments.find((p) => p.status !== 'approved' && p.status !== 'paid'),
    tenant: tenant ? { ...tenant, contractStart: fmt(tenant.contractStart), contractEnd: fmt(tenant.contractEnd) } : undefined,
    documents: (documents || []).map((d: any, i: number) => ({
      id: d.id || `doc${i}`, name: d.name, type: d.documentType || d.type || t('pmDocument'), date: d.date ? fmt(d.date) : undefined,
      // Carried through so the unit's Documents tab can actually open the file.
      // Dropping it here left every row in the disabled state DocRow shows for
      // a document with nothing behind it.
      fileUrl: d.fileUrl,
    })),
    tenancyHistory: unit.tenancyHistory || [],
    details, rentalIndex, source: 'live',
    inspectionReports: unit.inspectionReports || [],
    maintenance: maintenance || [],
    media: mediaList,
  };
}

// ------------------------------------------------------------------
// Sample data for the new per-property tabs (no backend fields yet).
// ------------------------------------------------------------------
// Built with `t` so the placeholder rows read in the investor's language.
// `result` / `condition` stay as English data values — code compares them.
const sampleInspectionReports = (t: (k: string) => string) => [
  { id: 's1', type: t('pmSampleInsp1Type'), date: '2025-02-12', result: 'Pass', notes: t('pmSampleInsp1Notes') },
  { id: 's2', type: t('pmSampleInsp2Type'), date: '2025-08-01', result: 'Pass', notes: t('pmSampleInsp2Notes') },
];
const sampleMaintenance = (t: (k: string) => string) => [
  { id: 'm1', title: t('pmSampleMaint1'), category: t('pmSampleMaint1Cat'), status: 'completed' as const, date: '05 Jun 2025', cost: 450, handledBy: 'CoolAir Technical' },
  { id: 'm2', title: t('pmSampleMaint2'), category: t('pmSampleMaint2Cat'), status: 'completed' as const, date: '18 Mar 2025', cost: 220, handledBy: t('pmInternalTeam') },
  { id: 'm3', title: t('pmSampleMaint3'), category: t('pmSampleMaint2Cat'), status: 'in_progress' as const, date: '20 Jul 2025', cost: 900, handledBy: t('pmSampleMaint3By') },
];
// Statuses only — used for the "open requests" count, so no translation needed.
const SAMPLE_MAINTENANCE_STATUS = ['completed', 'completed', 'in_progress'] as const;
const sampleInventory = (t: (k: string) => string) => [
  { room: t('pmRoomLiving'), items: [ { name: t('pmItemSofa'), qty: 1, condition: 'Good' }, { name: t('pmItemTv'), qty: 1, condition: 'Good' }, { name: t('pmItemCoffeeTable'), qty: 1, condition: 'Fair' } ] },
  { room: t('pmRoomKitchen'), items: [ { name: t('pmItemFridge'), qty: 1, condition: 'Good' }, { name: t('pmItemOven'), qty: 1, condition: 'Good' }, { name: t('pmItemWasher'), qty: 1, condition: 'Good' } ] },
  { room: t('pmRoomMaster'), items: [ { name: t('pmItemBed'), qty: 1, condition: 'Good' }, { name: t('pmItemWardrobe'), qty: 1, condition: 'Good' }, { name: t('pmItemCurtains'), qty: 2, condition: 'Fair' } ] },
];

// ------------------------------------------------------------------
// Reusable bits
// ------------------------------------------------------------------

const StatBox: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ flex: 1, background: M.wash, borderRadius: 14, padding: '14px 10px', textAlign: 'center' }}>
    <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
    <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 2, fontWeight: 600 }}>{label}</div>
  </div>
);

const EmptyState: React.FC<{ icon: React.ComponentType<any>; title: string; text: string }> = ({ icon: Icon, title, text }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px' }}>
    <div style={{ width: 64, height: 64, borderRadius: 20, background: M.track, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
      <Icon size={30} color={M.ink3} />
    </div>
    <div style={{ fontSize: T.primary, fontWeight: 800, color: M.black }}>{title}</div>
    <p style={{ fontSize: T.secondary, color: M.ink2, marginTop: 6, lineHeight: 1.5, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>{text}</p>
  </div>
);

const SampleNote: React.FC = () => {
  const { t } = useLanguage();
  return (
  <div style={{ display: 'flex', gap: 10, padding: '12px 14px', borderRadius: 12, background: theme.warningBg, color: M.goldDeep, marginBottom: 16, alignItems: 'center' }}>
    <Info size={18} style={{ flexShrink: 0 }} />
    <span style={{ fontSize: T.micro, lineHeight: 1.45 }}>{t('pmSampleNote')}</span>
  </div>
  );
};

const condColor = (c: string) => (c === 'Good' ? theme.success : c === 'Fair' ? theme.warning : theme.danger);
const condBg = (c: string) => (c === 'Good' ? theme.successBg : c === 'Fair' ? theme.warningBg : theme.dangerBg);
const condKey = (c: string) => (c === 'Good' ? 'pmCondGood' : c === 'Fair' ? 'pmCondFair' : 'pmCondPoor');

interface Props {
  currentInvestor?: any;
  units?: any[];
  /**
   * Code-gated showcase (portals/demo/DemoShell): use the sample portfolio
   * instead of fetching. Its /pm/owner/:id/units call is blocked in demo mode,
   * so without this the tab shows "No Properties Yet" — which reads as a
   * broken feature rather than a deliberate sample.
   */
  demoMode?: boolean;
  /**
   * Opens the portal's chat widget with a prefilled message. Supplied by
   * InvestorPortal; absent in the standalone preview and the EA console, where
   * the prompts still render but without their call-to-action button (a dead
   * button would be worse than none).
   */
  onAsk?: (message: string) => void;
  /**
   * Render ONE section, with no tab bar of its own.
   *
   * The owner shell no longer shows this module as a five-tab screen. Its
   * sections were split across the new four-tab bar: the unit list is the
   * Properties tab, tenancies and expenses are the Rent and Costs halves of
   * Money, and the old Dashboard tab is gone because the shell already has a
   * Home. Each caller asks for the one piece it owns.
   *
   * Omitted, the module renders exactly as before, tab bar and all — which is
   * what the EA console (portals/ea/EAAdminConsole) and the standalone preview
   * (OwnerPMPreview) still mount, and what the sales showcase films.
   */
  section?: 'dashboard' | 'properties' | 'tenants' | 'maintenance' | 'documents' | 'expenses';
  /**
   * The shell's AI Smart Upload handler (InvestorPortal.handleSmartUpload) and
   * its in-flight flag. Threaded down to a unit's Documents tab so the same
   * upload an owner knows from the Asset Desk works from the property they are
   * standing in. Absent in the EA console and the standalone preview, where the
   * button is simply not rendered rather than rendered dead.
   */
  onSmartUpload?: (e: React.ChangeEvent<HTMLInputElement>, unitId?: string) => void;
  smartUploading?: boolean;
  /**
   * Rendered under the summary strip on the Properties section. The shell puts
   * the two portfolio-wide reports here, so they are reached FROM the list of
   * properties instead of sitting beside it as two more bottom-bar tabs.
   */
  headerExtra?: React.ReactNode;
}

// `label` values are translation keys, resolved by <Segmented>.
const MAIN_TABS: Tab[] = [
  { key: 'dashboard', label: 'dashboard', icon: LayoutGrid },
  { key: 'properties', label: 'properties', icon: Home },
  // Tenancies read ACROSS the portfolio. Held per property, they answer "is
  // this unit alright?" but never "who owes me money?" — which is what someone
  // managing from abroad asks. Maintenance stays inside each property: it is
  // already a sub-tab there, and a second copy here earned nothing.
  { key: 'tenants', label: 'pmTenants', icon: Users },
  // Maintenance ACROSS the portfolio. It is a sub-tab of each property too,
  // and that used to be the only place it lived — which meant an owner with
  // ten units had to open ten properties to find out whether anything was
  // waiting on them. Per property answers "is this unit alright?"; this
  // answers "is anything on fire anywhere?", which is the question someone
  // managing from abroad actually opens the app with.
  { key: 'maintenance', label: 'pmMaintenance', icon: Wrench },
  { key: 'documents', label: 'documents', icon: FileText },
  { key: 'expenses', label: 'expenses', icon: Wallet },
];

const UNIT_TABS: Tab[] = [
  { key: 'overview', label: 'pmOverview', icon: Home },
  { key: 'inspection', label: 'pmInspection', icon: ClipboardCheck },
  { key: 'maintenance', label: 'pmMaintenance', icon: Wrench },
  // What this unit costs to hold. Expenses were only ever readable across the
  // whole portfolio, grouped by unit — so an owner standing on one property
  // had to leave it to find out what that property had cost them.
  { key: 'expenses', label: 'expenses', icon: Wallet },
  // Inventory is hidden until there is something real behind it. Nothing in
  // the product records it — no field on the unit, no entry in the admin
  // console — so the tab could only ever show sampleInventory(): a sofa, a
  // fridge and a king bed invented for every property on every account.
  // Restore this line (and its render branch below) once units carry an
  // `inventory` array; InventoryTab already prefers real items and falls back
  // to an empty state rather than the sample.
  // { key: 'inventory', label: 'pmInventory', icon: Package },
  // This unit's own paperwork. `vm.documents` was built all along and never
  // rendered here — the only way to a unit's files was the portfolio-wide
  // Documents tab, which is no longer a destination in the owner shell.
  { key: 'documents', label: 'documents', icon: FileText },
];

// Compact AED for tiles: 149000 -> "149k", but small values stay exact
// rather than collapsing to a meaningless "0k".
const kAED = (n: number) => (Math.abs(n) >= 1000 ? `${Math.round(n / 1000)}k` : String(Math.round(n)));

// Portfolio-scale money: 22014800 -> "22.0M", 850000 -> "850k".
const mAED = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return kAED(n);
};

// What the owner paid for the unit. `purchasePrice` is only set on
// PM-created units — the Asset Desk records the figure as `purchaseValue`
// (which is what InvestorPortal and PropertyView read), with `propertyValue`
// as an older alias. Reading only `purchasePrice` made every existing unit
// look as though it had no price at all.
function unitValue(u: any): number {
  return Number(u?.purchasePrice) || Number(u?.purchaseValue) || Number(u?.propertyValue) || 0;
}

// Gross yield = annual rent / purchase price. Returns null unless BOTH are
// real numbers, so a missing purchase price shows nothing instead of 0%.
function grossYield(rent: number, price: number): number | null {
  if (!(rent > 0) || !(price > 0)) return null;
  return (rent / price) * 100;
}

// A cheque due inside the next `days` and not yet collected.
function dueWithin(units: any[], days: number): number {
  const now = Date.now();
  const until = now + days * 86400000;
  return units.reduce((sum, u) => sum + (u.rentSchedule || []).reduce((a: number, r: any) => {
    if (chequeCollected(r)) return a;
    const t = r?.dueDate ? Date.parse(r.dueDate) : NaN;
    return !isNaN(t) && t >= now && t <= until ? a + (r.amount || 0) : a;
  }, 0), 0);
}

// per-unit financial metrics
//
// Prefers the backend's netIncome block, which knows two things this file
// cannot: the tenancy's own annual rent (not just the cheque schedule, which
// is empty on plenty of leased units) and the building's service charge. The
// local computation stays as the fallback for sample/demo units, which carry
// no facts.
function unitMetrics(u: any) {
  const f = factsOf(u).netIncome;
  if (f) {
    return {
      rent: f.annualRent,
      expenses: f.expenses,
      serviceCharge: f.serviceCharge,
      serviceChargeKnown: f.serviceChargeKnown,
      net: f.net,
      value: unitValue(u),
    };
  }
  const rent = (u.rentSchedule || []).reduce((s: number, r: any) => s + (r.amount || 0), 0);
  const expenses = (u.expenses || []).reduce((s: number, e: any) => s + (e.amount || 0), 0);
  return { rent, expenses, serviceCharge: 0, serviceChargeKnown: false, net: rent - expenses, value: unitValue(u) };
}

/**
 * Three numbers above the property list, replacing the old Dashboard tab.
 *
 * That tab was the second "Dashboard" in the shell — the bottom bar already
 * had one — and an owner opening a module called Property Management does not
 * want a landing page, they want their properties. The figures worth keeping
 * off it fit in one strip above the list they describe.
 */
/** A titled rule between the stacked halves of the management screen. */
const SectionDivider: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '26px 16px 2px' }}>
    <span style={{ fontSize: T.sectionTitle, fontWeight: 800, color: M.black, whiteSpace: 'nowrap' }}>{label}</span>
    <span style={{ flex: 1, height: 1, background: M.hair }} />
  </div>
);

const PortfolioStrip: React.FC<{ units: any[] }> = ({ units }) => {
  const { t } = useLanguage();
  const totalValue = units.reduce((a, u) => a + unitValue(u), 0);
  const rented = units.filter((u) => u.status === 'Rented').length;
  const priced = units.filter((u) => unitValue(u) > 0).length;

  return (
    <div style={{ padding: '20px 16px 14px', background: '#fff', borderBottom: `1px solid ${M.hair}` }}>
      <div style={{ fontSize: T.micro, color: M.ink3, fontWeight: 600 }}>
        {t('pmPortfolioValue')}
        {/* Never imply the total covers units that carry no price at all. */}
        {priced < units.length && (
          <span style={{ color: M.ink4 }}> · {priced}/{units.length}</span>
        )}
      </div>
      <div style={{ fontSize: T.money, fontWeight: 800, color: M.black, letterSpacing: '-0.8px', lineHeight: 1.1, marginTop: 2 }}>
        {totalValue > 0
          ? <><span style={{ fontSize: T.money - 12, color: M.gold, fontWeight: 800, marginRight: 4 }}>AED</span>{mAED(totalValue)}</>
          : <span style={{ color: M.ink4 }}>—</span>}
      </div>
      {/* The two counts sit UNDER the figure, not beside it in equal boxes:
          the portfolio value is the headline and the rest is context. Three
          equal cells gave a purchase price the same weight as a unit count. */}
      <div style={{ display: 'flex', gap: 18, marginTop: 12 }}>
        <StripFact value={String(units.length)} label={t('properties')} />
        <span style={{ width: 1, background: M.hair }} />
        <StripFact value={`${rented}/${units.length}`} label={t('pmOccupancy')} />
      </div>
    </div>
  );
};

const StripFact: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <span style={{ fontSize: T.statValue - 2, fontWeight: 800, color: M.black }}>{value}</span>
    <span style={{ fontSize: T.micro, color: M.ink3, marginLeft: 6, fontWeight: 600 }}>{label}</span>
  </div>
);

// ---------------- Orchestrator: tabs + property detail ----------------
const PropertyManagementView: React.FC<Props> = ({ currentInvestor, units: ownerUnits, demoMode, onAsk, section, headerExtra, onSmartUpload, smartUploading }) => {
  const { t } = useLanguage();
  const ownerId = currentInvestor?.id || DEFAULT_OWNER_ID;
  // First paint comes from the device, not the network. On a cold backend the
  // fetch below can take ~28s (see the comment on it); rendering last time's
  // portfolio immediately means the owner reads their numbers while that
  // happens, instead of watching a skeleton. The fetch still runs and still
  // wins — this only changes when the screen stops being empty.
  // Lazy initialiser, not a bare value: the argument form re-reads localStorage
  // and re-parses the whole portfolio on EVERY render and then throws the
  // result away, which is a strange thing to ship in a performance fix.
  const [pmUnits, setPmUnits] = useState<any[] | null>(
    () => (demoMode ? DEMO_PM_UNITS : readPmUnits(currentInvestor?.id || DEFAULT_OWNER_ID)),
  );
  /**
   * What the screen actually renders.
   *
   * PM enrichment is an UPGRADE, not a prerequisite. This module fetches from
   * PORTAL_API, which in development is the local dev API on :4002 (emulator)
   * while InvestorPortal's own `units` come from API_URL — the live backend.
   * So a developer signed in to a real account has real properties in memory
   * and an unreachable PM endpoint, and anything keyed solely off the PM call
   * renders "No Properties Yet" over a portfolio that is right there.
   *
   * The same holds in production whenever PM cannot answer: the module is
   * gated behind PM_MODULE_ENABLED (server.js), and a unit is only returned if
   * it carries `ownerId` or `investorId` matching this account. Neither is a
   * reason to tell an owner they have no properties.
   *
   * Derived rather than synced into state on purpose: `ownerUnits` arrives
   * AFTER the investor fetch resolves, which is usually after this component
   * has already mounted and run its own effect once. Copying it into state
   * would need a second effect to catch that, and would go stale.
   */
  const units = React.useMemo(() => {
    if (demoMode) return DEMO_PM_UNITS;
    if (pmUnits && pmUnits.length) return pmUnits;
    // Tagged, because an investor-shaped unit carries no tenancy at all. Without
    // this the card prints "Vacant" under a unit whose status says Rented — an
    // assertion we cannot make when the enrichment call never answered.
    if (ownerUnits && ownerUnits.length) return ownerUnits.map((u) => ({ ...u, __tenancyUnknown: true }));
    return pmUnits;
  }, [demoMode, pmUnits, ownerUnits]);
  const [selected, setSelected] = useState<string | null>(null);
  // Which sub-tab the opened property lands on. 'overview' for every ordinary
  // tap; the maintenance board sets it to 'maintenance' so a tap on a request
  // arrives AT that request, rather than on the property's front page with the
  // owner left to find it again.
  const [selectedSub, setSelectedSub] = useState<string>('overview');
  const openUnit = useCallback((unitId: string, sub: string = 'overview') => {
    setSelectedSub(sub);
    setSelected(unitId);
  }, []);
  const [tab, setTab] = useState<string>('dashboard');
  // Long-term rentals | Airbnb. The switch exists only for an owner with at
  // least one Airbnb apartment; everyone else sees the management screen
  // exactly as before.
  const airbnb = useAirbnbIncome((units || []).map((u: any) => String(u.id)), demoMode);
  const [rentalKind, setRentalKind] = useState<string>('longterm');
  const showAirbnb = rentalKind === 'airbnb' && airbnb.units.length > 0;
  // All | Apartments | Offices. Shown only to an owner holding both kinds;
  // the statement, tenants and property list below all follow the choice.
  const [category, setCategory] = useState<string>('all');
  const mixed = hasBothCategories(units || []);
  const catUnits = React.useMemo(
    () => (!units || !mixed || category === 'all' ? units : units.filter((u: any) => unitCategory(u) === category)),
    [units, mixed, category],
  );
  const categorySwitch = mixed ? (
    <Segmented
      tabs={[
        { key: 'all', label: 'pmCatAll', icon: LayoutGrid, count: (units || []).length },
        { key: 'residential', label: 'pmCatApartments', icon: Home, count: (units || []).filter((u: any) => unitCategory(u) === 'residential').length },
        { key: 'commercial', label: 'pmCatOffices', icon: Briefcase, count: (units || []).filter((u: any) => unitCategory(u) === 'commercial').length },
      ]}
      value={category}
      onChange={setCategory}
    />
  ) : null;
  // Bumping this re-runs the fetch(es) so admin updates reflect live.
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // The last load did not come back. Distinct from an empty portfolio.
  const [failed, setFailed] = useState(false);
  // A request is already on the wire. The auto-refresh below fires every 30s
  // and on every window focus; on a cold backend a single call can outlast
  // that interval, so without this guard the tab piles refreshes onto a
  // request that has not answered yet — each one repeating the same fan-out
  // and making the first one slower still.
  const inFlight = useRef(false);
  const bump = useCallback(() => { if (!inFlight.current) setReloadKey((k) => k + 1); }, []);

  useEffect(() => {
    // Showcase keeps the sample portfolio: a blocked call would resolve to an
    // empty payload and replace it with "No Properties Yet" mid-demo.
    if (demoMode) return;

    let alive = true;
    (async () => {
      if (reloadKey > 0) setRefreshing(true);
      inFlight.current = true;
      try {
        // No explicit timeout: this used to override the default with 25000,
        // which is BELOW the cold-start window. The backend runs at
        // min-instances=0 (docs/DEPLOY-BACKEND-PROMPT.md, rule 7), so a cold
        // container takes ~28s to answer its first request. The first open of
        // Property Management after an idle period was therefore aborted a few
        // seconds before the server replied — and the catch below then set
        // units to [], turning a slow load into "No Properties Yet" until the
        // 30s auto-refresh happened to hit a warm instance. The policy now
        // lives in one place (portals/shared/api.ts) rather than in a number
        // picked per call site.
        const { units } = await portalFetch<any>(`/pm/owner/${ownerId}/units`);
        if (alive) { setPmUnits(units || []); setFailed(false); }
        // Seed the next open. Written after the state update so a payload that
        // fails to render is never the one we keep.
        //
        // Only REAL PM rows are cached. Caching an empty answer would pin
        // "no properties" for the next open even once PM can answer, and the
        // investor-shaped fallback units are not PM payloads — they carry no
        // tenancy, media or facts, so storing them under the PM key would make
        // the next cold start render a stripped-down portfolio.
        if (units && units.length) writePmUnits(ownerId, units);
      } catch {
        // A failed call is NOT an empty portfolio. Showing the owner "No
        // Properties Yet" because the network timed out reads as data loss.
        // Keep the skeleton on the first load and say it could not be reached
        // — but only when there is genuinely nothing to show. With the
        // investor payload in hand the properties render; what is missing is
        // the PM enrichment, not the portfolio.
        if (alive) setFailed(!(ownerUnits && ownerUnits.length));
      }
      finally { if (alive) { setRefreshing(false); } inFlight.current = false; }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, reloadKey, demoMode]);

  // No polling in the showcase — nothing can change, and every tick would be a
  // blocked request.
  useAutoRefresh(bump, { intervalMs: 30000, enabled: !demoMode });

  if (units === null) {
    // Nothing has ever loaded AND the last attempt failed → say so, with a way
    // back. Previously this case set units to [] and rendered the "No
    // Properties Yet" empty state, which tells an owner with a portfolio that
    // their portfolio is gone.
    if (failed) {
      return (
        <div className="pm-premium pt-fade" style={{ padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: T.cardTitle, color: M.black, marginBottom: 6, fontWeight: 700 }}>{t('pmLoadFailedTitle')}</div>
          <div style={{ fontSize: T.secondary, color: M.ink2, marginBottom: 18, lineHeight: 1.5 }}>{t('pmLoadFailedBody')}</div>
          <button
            onClick={() => { setFailed(false); setReloadKey((k) => k + 1); }}
            className="pt-tap"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: M.gold, border: 'none', borderRadius: 99, padding: '9px 20px', color: '#fff', fontSize: T.secondary, fontWeight: 800, cursor: 'pointer' }}
          >
            <RefreshCw size={15} /> {t('pmRefresh')}
          </button>
        </div>
      );
    }
    // Skeleton shimmer while the portfolio loads (premium loading state).
    return (
      <div className="pm-premium" style={{ padding: '18px 16px' }}>
        <BrainLoader task="imbTaskPortfolio" />
        <div className="pt-skel" style={{ height: 150, marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} className="pt-skel" style={{ height: 104 }} />)}
        </div>
        <div className="pt-skel" style={{ height: 200 }} />
      </div>
    );
  }

  // A property is open → full detail screen with its own sub-tabs.
  if (selected) {
    return <UnitDetail unitId={selected} initialSub={selectedSub} reloadKey={reloadKey} demoMode={demoMode} onBack={() => setSelected(null)} ownerName={currentInvestor?.name} onAsk={onAsk} investor={currentInvestor} onSmartUpload={onSmartUpload} smartUploading={smartUploading} />;
  }

  const refresh = (
    <button
      onClick={bump}
      title={t('pmRefresh')}
      aria-label={t('pmRefresh')}
      disabled={refreshing}
      className="pt-tap"
      style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(214,177,96,0.35)', borderRadius: 99, padding: '5px 11px', color: M.gold, fontSize: T.micro, fontWeight: 800, cursor: refreshing ? 'default' : 'pointer', flexShrink: 0 }}
    >
      <RefreshCw size={15} className={refreshing ? 'pt-rotate' : undefined} /> {refreshing ? t('pmRefreshing') : t('pmRefresh')}
    </button>
  );

  // The shell asked for one section. No tab bar: the bottom bar is already
  // the navigation, and a second row of five pills under it is exactly the
  // "four rows, then five more" stacking that made this screen hard to read.
  if (section) {
    return (
      <div className="pt-fade pm-premium">
        {/* The management overview: rental income, what has been collected and
            what is due, the prompts, lease renewals and income per property.
            It was dropped when the shell lost its Property Management tab —
            the bottom bar already had a Home, so a second "Dashboard" tab
            looked like the duplicate it was. The DUPLICATE was the label, not
            this screen: Home is the owner's landing page, this is the
            management statement, and nothing else in the product shows rent
            collected against rent due across the portfolio. */}
        {section === 'dashboard' && (
          <>
            {airbnb.units.length > 0 && (
              <Segmented
                tabs={[
                  { key: 'longterm', label: 'pmLongTermTab', icon: Home },
                  { key: 'airbnb', label: 'pmAirbnbTab', icon: BedDouble },
                ]}
                value={rentalKind}
                onChange={setRentalKind}
              />
            )}
            {/* Holiday-let income from DreamHaven, on its own tab. */}
            {showAirbnb && (
              <AirbnbPortfolioSection units={units} demoMode={demoMode} onOpenUnit={(id) => openUnit(id, 'airbnb')} />
            )}
            {!showAirbnb && <>
            {categorySwitch}
            <DashboardTab units={catUnits!} onAsk={onAsk} refresh={refresh} />
            {/* The statement, then who is paying it. Tenancies had their own
                tab before the shell was cut to four; stacking them here keeps
                them reachable without a second row of tabs under the first.
                Documents are NOT here — they are read per property, on the
                unit's own Documents tab. */}
            <SectionDivider label={t('pmTenants')} />
            <TenantsTab units={catUnits!} onSelect={setSelected} />
            </>}
          </>
        )}
        {section === 'properties' && (
          <>
            {categorySwitch}
            <PortfolioStrip units={catUnits!} />
            {headerExtra}
            <PropertiesTab units={catUnits!} onSelect={setSelected} />
          </>
        )}
        {section === 'tenants' && <>{categorySwitch}<TenantsTab units={catUnits!} onSelect={setSelected} /></>}
        {section === 'maintenance' && <>{categorySwitch}<MaintenanceBoard units={catUnits!} onOpenUnit={(id) => openUnit(id, 'maintenance')} /></>}
        {section === 'documents' && <DocumentsTab units={units} ownerId={ownerId} demoMode={demoMode} />}
        {section === 'expenses' && <ExpensesTab units={units} demoMode={demoMode} />}
      </div>
    );
  }

  return (
    <div className="pt-fade pm-premium">
      {/* The tab pills get the row to themselves on every sub-tab; refresh
          lives in the headline beside the occupancy badge, next to the numbers
          it actually reloads. Leaving a second copy in this row on the other
          tabs just crowded it. */}
      <Segmented tabs={MAIN_TABS} value={tab} onChange={setTab} />
      {tab === 'dashboard' && (
        <>
          <DashboardTab units={units} onAsk={onAsk} refresh={refresh} />
          <AirbnbPortfolioSection units={units} demoMode={demoMode} onOpenUnit={(id) => openUnit(id, 'airbnb')} />
        </>
      )}
      {tab === 'properties' && <PropertiesTab units={units} onSelect={setSelected} />}
      {tab === 'tenants' && <TenantsTab units={units} onSelect={setSelected} />}
      {tab === 'maintenance' && <MaintenanceBoard units={units} onOpenUnit={(id) => openUnit(id, 'maintenance')} />}
      {tab === 'documents' && <DocumentsTab units={units} ownerId={ownerId} demoMode={demoMode} />}
      {tab === 'expenses' && <ExpensesTab units={units} demoMode={demoMode} />}
    </div>
  );
};

/**
 * A panel on the statement.
 *
 * The shadow is gone. Twelve panels each carrying the same soft blue-grey
 * drop shadow — rgba(16,24,40,…), a borrowed default that is not even in this
 * product's palette — is what made the module read as a stock dashboard.
 * A private-bank statement has no drop shadows: structure comes from rules
 * and from space.
 *
 * What is left is a hairline in the palette's own ink and a restrained radius,
 * so a panel reads as a ruled section of a document rather than a card
 * floating above one.
 */
const softCard: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  border: `1px solid ${M.hair}`,
};

/**
 * Money, and only money, is set in the serif.
 *
 * Cormorant Garamond is already the display face of the Portfolio Report and
 * of the brand itself, so a figure in it reads as this company's figure. It
 * also separates the amounts from the labels around them by shape rather than
 * by another colour, which the locked three-colour palette does not allow.
 */
const figure: React.CSSProperties = {
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  fontWeight: 600,
  letterSpacing: '-0.005em',
  // PROPORTIONAL, not tabular. `.pm-premium` sets tabular-nums for the whole
  // module, which is right for a sans column of amounts and wrong here:
  // Cormorant's tabular set pads every digit to the width of a zero, so "10"
  // came out spaced like "1 0". These figures are read as words, not compared
  // down a column, so they take the font's proportional lining set.
  fontVariantNumeric: 'lining-nums proportional-nums',
};

// Count-up animation for headline figures (premium micro-motion).
function useCountUp(target: number, ms = 900): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof performance === 'undefined' || typeof requestAnimationFrame === 'undefined') { setN(target); return; }
    let raf = 0; const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
}

// Sample occupancy history (no monthly history in the backend yet).
const SAMPLE_OCCUPANCY: Record<string, { label: string; value: number }[]> = {
  Monthly: [
    { label: 'Feb', value: 72 }, { label: 'Mar', value: 80 }, { label: 'Apr', value: 76 },
    { label: 'May', value: 88 }, { label: 'Jun', value: 84 }, { label: 'Jul', value: 100 },
  ],
  Quarterly: [
    { label: 'Q3 24', value: 68 }, { label: 'Q4 24', value: 79 },
    { label: 'Q1 25', value: 86 }, { label: 'Q2 25', value: 100 },
  ],
};

// One KPI stat tile (small tinted icon, label, big number + optional muted suffix).
/**
 * One figure on the statement.
 *
 * The 40px gradient-gold icon chip is gone. It said nothing the label did not
 * already say — four identical badges above four labels, which is the single
 * clearest tell of a generated dashboard — and it pushed the figure, the only
 * thing anybody opens this screen to read, into third place.
 *
 * What is left is the order a statement uses: what it is, then how much. The
 * label sits above a gold rule and the amount hangs beneath it in the serif,
 * so the eye lands on the number first and the four entries read as one
 * ruled table rather than four floating cards.
 *
 * `icon` is still accepted so the call sites keep working, and deliberately
 * unused — see the note above.
 */
const KpiTile: React.FC<{ icon?: React.ComponentType<any>; label: string; value: React.ReactNode; sub?: string; desc?: string; hint?: string; onClick?: () => void }> = ({ label, value, sub, desc, hint, onClick }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    className={onClick ? 'pt-tap' : undefined}
    style={{ padding: '14px 2px 0', borderTop: `1px solid ${M.hair}`, minWidth: 0, cursor: onClick ? 'pointer' : undefined }}>
    {/* Two lines reserved for the label so the figures below sit on a shared
        baseline. German runs long — "Eingegangene Miete" wraps where "Rent
        collected" does not — and without this the row came out ragged. */}
    <div style={{ ...eyebrow, fontSize: T.micro, color: M.ink2, fontWeight: 600, lineHeight: 1.35, minHeight: '2.7em' }}>{label}</div>
    {/* nowrap: the amount and its qualifier ("AED 615k / 825k") are one
        reading, and breaking between them left "/ 825k" stranded on its own
        line looking like a cut-off number. If the column is too narrow the
        figure scales down instead of wrapping. */}
    <div style={{ ...figure, fontSize: 26, color: M.black, marginTop: 2, whiteSpace: 'nowrap' }}>
      <span style={onClick ? { textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: M.gold, textUnderlineOffset: 5 } : undefined}>{value}</span>
      {sub && <span style={{ fontSize: T.secondary, color: M.ink4, fontWeight: 500 }}> {sub}</span>}
    </div>
    {/* What the figure is, in plain words — owners asked what "214k 8/19" meant.
        Kept to one short line so it fits a phone column. */}
    {desc && <div style={{ fontSize: 11, color: M.ink2, lineHeight: 1.3, marginTop: 3 }}>{desc}</div>}
    {hint && <div style={{ fontSize: 11, color: M.gold, fontWeight: 700, marginTop: 2 }}>{hint}</div>}
  </div>
);

// Occupancy bar chart with a working Monthly/Quarterly toggle (sample data).
const OccupancyChart: React.FC = () => {
  const { t } = useLanguage();
  const [period, setPeriod] = useState<'Monthly' | 'Quarterly'>('Monthly');
  const data = SAMPLE_OCCUPANCY[period];
  return (
    <div style={{ ...softCard, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmOccupancyRate')}</div>
        <button onClick={() => setPeriod((p) => (p === 'Monthly' ? 'Quarterly' : 'Monthly'))} className="pt-tap"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: M.wash, border: 'none', borderRadius: 99, padding: '7px 12px', fontSize: T.micro, fontWeight: 800, color: M.ink2, cursor: 'pointer' }}>
          {period === 'Monthly' ? t('monthly') : t('quarterly')} <ChevronDown size={15} />
        </button>
      </div>
      <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 4, marginBottom: 16 }}>{t('pmOccupancySub')}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 150 }}>
        {data.map((d, i) => {
          const last = i === data.length - 1;
          return (
            <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: T.micro - 1, fontWeight: 800, color: last ? M.gold : M.ink2 }}>{d.value}%</div>
              <div style={{ width: '100%', height: 104, display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ width: '100%', height: `${d.value}%`, borderRadius: '8px 8px 0 0', background: last ? `linear-gradient(180deg, ${M.gold}, ${M.goldDeep})` : 'linear-gradient(180deg, rgba(0,0,0,0.30), rgba(0,0,0,0.62))', transition: 'height 0.5s ease' }} />
              </div>
              <div style={{ fontSize: T.micro, color: M.ink2, fontWeight: 700 }}>{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Build a recent-activity feed from real rent/expense records.
// `t` is passed in — this is a plain helper, so it must not call the hook.
function buildActivity(units: any[], t: (k: string) => string) {
  const items: { kind: 'rent' | 'expense'; title: string; amount: number; date?: string }[] = [];
  units.forEach((u) => {
    (u.rentSchedule || []).forEach((r: any) => {
      if (r.status === 'approved' || r.status === 'paid') items.push({ kind: 'rent', title: `${t('pmRentReceived')} · ${u.name}`, amount: r.amount || 0, date: r.dueDate });
    });
    (u.expenses || []).forEach((e: any) => {
      if (e.status === 'paid') items.push({ kind: 'expense', title: `${e.type || t('pmExpense')} ${t('pmPaidSuffix')} · ${u.name}`, amount: e.amount || 0, date: e.date });
    });
  });
  // Newest first. A feed called "recent activity" that lists unit 1's whole
  // year, then unit 2's, is a ledger in storage order, not activity.
  return items.sort((a, b) => {
    const ta = a.date ? Date.parse(a.date) : NaN;
    const tb = b.date ? Date.parse(b.date) : NaN;
    if (isNaN(ta) && isNaN(tb)) return 0;
    if (isNaN(ta)) return 1;        // undated rows sink to the bottom
    if (isNaN(tb)) return -1;
    return tb - ta;
  });
}

const RecentActivity: React.FC<{ units: any[] }> = ({ units }) => {
  const { t } = useLanguage();
  const [all, setAll] = useState(false);
  const items = buildActivity(units, t);
  const shown = all ? items : items.slice(0, 4);
  return (
    <div style={{ ...softCard, padding: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 12px 8px' }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('recentActivity')}</div>
        {items.length > 4 && (
          <button onClick={() => setAll((a) => !a)} style={{ background: 'none', border: 'none', color: M.goldInk, fontSize: T.secondary, fontWeight: 800, cursor: 'pointer' }}>
            {all ? t('showLess') : t('viewAll')}
          </button>
        )}
      </div>
      {shown.length === 0 && <p style={{ fontSize: T.secondary, color: M.ink2, textAlign: 'center', padding: 16 }}>{t('pmNoActivity')}</p>}
      {shown.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderTop: i === 0 ? 'none' : `1px solid ${M.hair}` }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {it.kind === 'rent' ? <Wallet size={19} color={M.goldInk} /> : <Receipt size={19} color={M.goldInk} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T.body, fontWeight: 700, color: M.black, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
            {it.date && <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 1 }}>{fmt(it.date)}</div>}
          </div>
          <div style={{ fontSize: T.body, fontWeight: 800, color: it.kind === 'rent' ? M.okInk : M.black, flexShrink: 0 }}>
            {it.kind === 'rent' ? '+' : '−'}AED {it.amount.toLocaleString('en-AE')}
          </div>
        </div>
      ))}
    </div>
  );
};

// Unit names and unit numbers overlap in the CRM export — name
// "Seagate Building 3" with unitNumber "RY Seagate Building 3-2-209" used to
// render as "Seagate Building 3 #RY Seagate Building 3-2-209". Strip the
// repeated building name so only the unit part is shown after the #.
function unitLabel(u: any): string {
  const name = String(u?.name || '').trim();
  const raw = String(u?.unitNumber || '').trim().replace(/^RY[\s-]+/i, '');
  if (!raw) return name;
  const at = name ? raw.toLowerCase().indexOf(name.toLowerCase()) : -1;
  const num = (at >= 0 ? raw.slice(0, at) + raw.slice(at + name.length) : raw)
    .replace(/^[\s\-–—/·,]+|[\s\-–—/·,]+$/g, '')
    .trim();
  if (!num) return name;
  return name ? `${name} #${num}` : `#${num}`;
}

// Resolve a unit's real photo the same way Asset Desk does — per-unit image if
// present, else the shared project-name → image map.
function unitPhoto(u: any): string {
  if (u?.image || u?.projectImage) return u.image || u.projectImage;
  const search = `${u?.project || ''} ${u?.name || ''} ${u?.unitNumber || ''}`.toLowerCase();
  for (const [k, url] of Object.entries(FALLBACK_PROJECT_IMAGES)) { if (url && search.includes(k)) return url as string; }
  return '';
}

// A cheque counts as collected once paid/approved OR its due date has arrived
// (post-dated cheques count from their date onward).
const chequeCollected = (r: any): boolean =>
  r?.status === 'paid' || r?.status === 'approved' ||
  (!!r?.dueDate && new Date(r.dueDate).getTime() <= Date.now());

// ---------------- Dashboard tab ----------------
// What makes up the dashboard's "Expenses" figure — exactly its parts, so the
// total always matches: for every LET unit, each recorded expense (snagging,
// handover, DLD/transfer/trustee, management fee… booked from the CRM, or added
// by hand) plus that unit's service charge where it is not already booked.
const ExpenseBreakdown: React.FC<{ rows: { u: any; m: any }[]; total: number; de: boolean; onClose: () => void }> = ({ rows, total, de, onClose }) => {
  const aed = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  const scLabel = de ? 'Servicegebühr' : 'Service charge';
  const lines = rows.flatMap(({ u, m }) => [
    ...(u.expenses || []).map((e: any) => ({ unit: u, type: e.type || (de ? 'Sonstiges' : 'Other'), what: e.description || '', amount: Number(e.amount) || 0, paid: e.status === 'paid', date: e.date })),
    ...((m.serviceCharge || 0) > 0 ? [{ unit: u, type: scLabel, what: de ? 'Jährlich, geschätzt' : 'Annual, estimated', amount: m.serviceCharge, paid: false, date: null }] : []),
  ]);
  const byType = new Map<string, number>();
  lines.forEach((l) => byType.set(l.type, (byType.get(l.type) || 0) + l.amount));
  const types = [...byType.entries()].sort((a, b) => b[1] - a[1]);
  const units = rows.filter(({ u }) => lines.some((l) => l.unit === u));
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '22px 20px 28px', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: M.gold }}>{de ? 'Ausgaben' : 'Expenses'}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#000', marginTop: 2 }}>{aed(total)}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 2 }}>{de ? 'Vermietete Einheiten — gegen die Mieteinnahmen gerechnet' : 'Let units — set against the rental income'}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', fontSize: 22, color: 'rgba(0,0,0,0.5)', padding: 4 }}>×</button>
        </div>

        {!lines.length ? (
          <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13.5, color: 'rgba(0,0,0,0.55)' }}>{de ? 'Keine Ausgaben erfasst.' : 'No expenses recorded.'}</div>
        ) : (
          <>
            <div style={{ marginTop: 18, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
              {types.map(([type, sum]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 14 }}>
                  <span style={{ fontWeight: 600, color: '#000' }}>{type}</span>
                  <span style={{ fontWeight: 700, color: '#000' }}>{aed(sum)}</span>
                </div>
              ))}
            </div>
            {units.map(({ u }) => {
              const mine = lines.filter((l) => l.unit === u);
              return (
                <div key={u.id} style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: '#000' }}>{u.name}{u.unitNumber && !String(u.name || '').includes(u.unitNumber) ? ` #${u.unitNumber}` : ''}</div>
                  {mine.map((l, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: 13 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: '#000' }}>{l.type}</div>
                        {l.what && <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.what}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, color: '#000' }}>{aed(l.amount)}</div>
                        {l.type !== scLabel && <div style={{ fontSize: 11, fontWeight: 700, color: l.paid ? '#1f7a3d' : '#9a6700' }}>{l.paid ? (de ? 'Bezahlt' : 'Paid') : (de ? 'Offen' : 'Due')}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

// Net per year, worked out: the yearly rent of the let units, minus their
// recorded expenses and estimated service charge — the same three numbers as
// the card, then the same sum for each let unit so every figure can be traced.
const NetBreakdown: React.FC<{ rows: { u: any; m: any }[]; rent: number; expenses: number; net: number; de: boolean; onClose: () => void }> = ({ rows, rent, expenses, net, de, onClose }) => {
  const aed = (n: number) => `${n < 0 ? '−' : ''}AED ${Math.abs(Math.round(n || 0)).toLocaleString('en-AE')}`;
  const label = (u: any) => `${u.name || u.project || ''}${u.unitNumber && !String(u.name || '').includes(u.unitNumber) ? ` #${u.unitNumber}` : ''}`;
  const line = (k: string, v: string, bold?: boolean) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: bold ? 14 : 13, fontWeight: bold ? 800 : 500, color: '#000' }}>
      <span>{k}</span><span style={{ fontWeight: bold ? 800 : 700, flexShrink: 0 }}>{v}</span>
    </div>
  );
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '22px 20px 28px', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: M.gold }}>{de ? 'Netto pro Jahr · Berechnung' : 'Net per year · calculation'}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#000', marginTop: 2 }}>{aed(net)}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 2 }}>{de ? `Vermietete Einheiten (${rows.length})` : `Let units (${rows.length})`}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', fontSize: 22, color: 'rgba(0,0,0,0.5)', padding: 4 }}>×</button>
        </div>

        <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(214,177,96,0.10)', borderRadius: 10 }}>
          {line(de ? 'Jahresmiete laufender Verträge' : 'Yearly rent of current leases', aed(rent))}
          {line(de ? '− Ausgaben und geschätzte Servicegebühr' : '− Expenses and estimated service charge', aed(-expenses))}
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.15)', marginTop: 4 }} />
          {line(de ? '= Netto pro Jahr' : '= Net per year', aed(net), true)}
        </div>
        <div style={{ fontSize: 11.5, color: 'rgba(0,0,0,0.55)', marginTop: 8, lineHeight: 1.45 }}>
          {de
            ? 'Nur vermietete Einheiten: leere Einheiten bringen keine Miete. Die Servicegebühr ist geschätzt (Satz pro sqft × Größe); ist die Rechnung schon als Ausgabe erfasst, wird sie nicht doppelt abgezogen.'
            : 'Let units only: empty units earn no rent. The service charge is estimated (rate per sqft × size); where the invoice is already booked as an expense it is not deducted twice.'}
        </div>

        {rows.map(({ u, m }) => (
          <div key={u.id} style={{ marginTop: 16, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#000', overflowWrap: 'anywhere' }}>{label(u)}</div>
            {line(de ? 'Jahresmiete' : 'Yearly rent', aed(m.rent))}
            {line(de ? '− Ausgaben' : '− Expenses', aed(-(m.expenses || 0)))}
            {line(de ? '− Servicegebühr (geschätzt)' : '− Service charge (est.)', (m.serviceCharge || 0) > 0 || m.serviceChargeKnown ? aed(-(m.serviceCharge || 0)) : (de ? 'unbekannt' : 'not known'))}
            {line(de ? '= Netto' : '= Net', aed((m.rent || 0) - (m.expenses || 0) - (m.serviceCharge || 0)), true)}
          </div>
        ))}
      </div>
    </div>
  );
};

// Service charge, unit by unit — what the dashboard tile adds up. Every
// figure is an ESTIMATE: the building rate per sqft (service-charge index, an
// entered project rate, or the owner invoice) times the unit size. The actual
// bill comes from the building management (Mollak) and can differ. Units with
// no known rate are listed too, so the total is never read as the whole
// portfolio.
const ServiceChargeBreakdown: React.FC<{ units: any[]; total: number; de: boolean; t: (k: string) => string; onClose: () => void }> = ({ units, total, de, t, onClose }) => {
  const aed = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  const known = units.filter((u) => u.facts?.serviceCharge?.annual > 0)
    .sort((a, b) => (b.facts.serviceCharge.annual || 0) - (a.facts.serviceCharge.annual || 0));
  const missing = units.filter((u) => !(u.facts?.serviceCharge?.annual > 0));
  const label = (u: any) => `${u.name || u.project || ''}${u.unitNumber && !String(u.name || '').includes(u.unitNumber) ? ` #${u.unitNumber}` : ''}`;
  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '22px 20px 28px', fontFamily: 'Montserrat, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: M.gold }}>{de ? 'Servicegebühr · geschätzt' : 'Service charge · estimated'}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#000', marginTop: 2 }}>{aed(total)} <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,0,0,0.55)' }}>{de ? 'pro Jahr' : 'per year'}</span></div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginTop: 2 }}>
              {de ? `${known.length} von ${units.length} Einheiten mit bekanntem Satz` : `${known.length} of ${units.length} units with a known rate`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', fontSize: 22, color: 'rgba(0,0,0,0.5)', padding: 4 }}>×</button>
        </div>

        <div style={{ marginTop: 14, padding: '12px 14px', background: 'rgba(214,177,96,0.10)', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5, color: 'rgba(0,0,0,0.75)' }}>
          {de
            ? 'Geschätzt: Satz des Gebäudes pro sqft und Jahr × Größe der Einheit. Die tatsächliche Rechnung kommt von der Hausverwaltung (Mollak) und kann abweichen. Einheiten ohne bekannten Satz sind nicht in der Summe enthalten.'
            : 'Estimated: the building rate per sqft per year × the unit size. The actual bill comes from the building management (Mollak) and can differ. Units without a known rate are not included in the total.'}
        </div>

        {known.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            {known.map((u) => {
              const sc = u.facts.serviceCharge;
              const src = sourceLabel(sc.source, t);
              return (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 13 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: '#000', overflowWrap: 'anywhere' }}>{label(u)}</div>
                    <div style={{ color: 'rgba(0,0,0,0.55)', fontSize: 12 }}>
                      {sc.psf != null ? `AED ${sc.psf}/sqft` : ''}{sc.psf != null && sc.sqft ? ` × ${Math.round(sc.sqft).toLocaleString('en-AE')} sqft` : ''}{src ? ` · ${src}` : ''}
                    </div>
                    {sc.payableYet === false && (
                      <div style={{ color: '#9a6700', fontSize: 11.5, fontWeight: 700 }}>{de ? 'Fällig erst ab Übergabe' : 'Payable only from handover'}</div>
                    )}
                  </div>
                  <div style={{ fontWeight: 800, color: '#000', flexShrink: 0 }}>{aed(sc.annual)}</div>
                </div>
              );
            })}
          </div>
        )}

        {missing.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#000' }}>{de ? `Satz fehlt (${missing.length})` : `Rate missing (${missing.length})`}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', margin: '2px 0 6px' }}>
              {de ? 'Laden Sie eine Servicegebühr-Rechnung hoch, um diese Einheiten einzubeziehen.' : 'Upload a service-charge invoice to include these units.'}
            </div>
            {missing.map((u) => (
              <div key={u.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: 13, color: 'rgba(0,0,0,0.7)', overflowWrap: 'anywhere' }}>{label(u)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// The owner's "control room": three figures and six months of rent, from the
// lease cheques recorded for their own units — nothing estimated.
//   Rent / month  the yearly rent of the current leases ÷ 12
//   Received      of the cheques due in the last 12 months, the share received
//                 (same rule as the "Rent collected" tile, chequeCollected)
//   Next 30 days  cheques still to come in the next 30 days
//   Chart         per month: rent due (light) and received (dark)
const OwnerControlRoom: React.FC<{ units: any[]; metrics: any[]; de: boolean }> = ({ units, metrics, de }) => {
  const MON = de ? ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'] : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const c = React.useMemo(() => {
    const now = new Date();
    const today = now.getTime();
    const yearAgo = today - 365 * 86400000;
    const in30 = today + 30 * 86400000;
    // Net profit of the let units: yearly rent − their recorded expenses and
    // service charge (unitMetrics, the same figure as the Net tile), ÷ 12.
    let annual = 0, leases = 0, costs = 0;
    units.forEach((u, i) => { if (u.currentTenant && (metrics[i]?.rent || 0) > 0) { annual += metrics[i].rent; costs += (metrics[i].expenses || 0) + (metrics[i].serviceCharge || 0); leases++; } });
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
      const start = d.getTime();
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
      return { key: `${d.getFullYear()}-${d.getMonth()}`, label: MON[d.getMonth()], start, end, due: 0, got: 0, inc: 0, exp: 0, rec: 0, sc: 0, current: i === 5 };
    });
    const byKey = new Map(months.map((m) => [m.key, m]));
    // Monthly rental INCOME: each lease's yearly rent ÷ 12, in every month the
    // lease runs — not the cheque dates (one cheque a year made one month huge
    // and the rest empty).
    const dayOf = (raw: any) => (raw ? Date.parse(raw) : NaN);
    units.forEach((u, i) => {
      const rent = metrics[i]?.rent || 0;
      if (!u.currentTenant || !(rent > 0)) return;
      const s0 = dayOf(u.currentTenant.contractStart);
      const e0 = dayOf(u.currentTenant.contractEnd);
      months.forEach((m) => {
        if (!isNaN(s0) && s0 >= m.end) return;
        if (!isNaN(e0) && e0 < m.start) return;
        m.inc += rent / 12;
      });
    });
    // Service charge as a monthly cost: the yearly charge ÷ 12 for every unit
    // that pays it (not before handover; not twice when the invoice is
    // already booked as an expense).
    units.forEach((u) => {
      const sc = u.facts?.serviceCharge;
      if (!sc || !(sc.annual > 0) || sc.payableYet === false || sc.bookedAsExpense) return;
      months.forEach((m) => { m.sc += sc.annual / 12; m.exp += sc.annual / 12; });
    });
    // Expenses per month, from the expenses recorded on the units.
    units.forEach((u) => (u.expenses || []).forEach((e: any) => {
      const t = e?.date ? Date.parse(e.date) : NaN;
      if (isNaN(t)) return;
      const d = new Date(t);
      const m = byKey.get(`${d.getFullYear()}-${d.getMonth()}`);
      if (m) { m.exp += Number(e.amount) || 0; m.rec += Number(e.amount) || 0; }
    }));
    let dueSoFar = 0, received = 0, next = 0, nextAmt = 0;
    units.forEach((u) => (u.rentSchedule || []).forEach((r: any) => {
      const t = r?.dueDate ? Date.parse(r.dueDate) : NaN;
      if (isNaN(t)) return;
      const amt = Number(r.amount) || 0;
      const got = chequeCollected(r);
      if (t <= today && t >= yearAgo) { dueSoFar += amt; if (got) received += amt; }
      if (t > today && t <= in30 && !got) { next++; nextAmt += amt; }
      const d = new Date(t);
      const m = byKey.get(`${d.getFullYear()}-${d.getMonth()}`);
      if (m) { m.due += amt; if (got) m.got += amt; }
    }));
    return { monthly: annual / 12, netMonthly: (annual - costs) / 12, costsMonthly: costs / 12, leases, pct: dueSoFar > 0 ? (received / dueSoFar) * 100 : null, next, nextAmt, months };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, metrics, de]);
  // Per year: one calendar year at a time, from records only. Rent = the
  // cheques of the current leases in the month they are DUE (multi-year
  // step-ups at their own amount); costs = the expenses recorded on the units
  // in the month of their date. Nothing spread or estimated (owner,
  // 2026-11-05: "it should always be accurate").
  // Filter: null = the last 6 months, or a year = its 12 months.
  const [pickedYear, setPickedYear] = React.useState<number | null>(null);
  const y = React.useMemo(() => {
    const DAY = 86400000;
    const thisYear = new Date().getFullYear();
    type Slot = { inc: number; got: number; cheques: number; mon: number[]; exp: number[] };
    const acc = new Map<number, Slot>();
    const slot = (yr: number) => { let s = acc.get(yr); if (!s) { s = { inc: 0, got: 0, cheques: 0, mon: Array(12).fill(0), exp: Array(12).fill(0) }; acc.set(yr, s); } return s; };
    units.forEach((u) => {
      const ten = u.currentTenant;
      if (!ten) return;
      const s0 = ten.contractStart ? Date.parse(ten.contractStart) : NaN;
      (u.rentSchedule || []).forEach((r: any) => {
        const at = r?.dueDate ? Date.parse(r.dueDate) : NaN;
        const amount = Number(r?.amount) || 0;
        if (isNaN(at) || !(amount > 0) || (!isNaN(s0) && at < s0 - 31 * DAY)) return;
        const d = new Date(at);
        const s = slot(d.getFullYear());
        s.inc += amount;
        s.mon[d.getMonth()] += amount;
        s.cheques++;
        if (chequeCollected(r)) s.got += amount;
      });
    });
    units.forEach((u) => (u.expenses || []).forEach((e: any) => {
      const t = e?.date ? Date.parse(e.date) : NaN;
      if (isNaN(t)) return;
      const d = new Date(t);
      slot(d.getFullYear()).exp[d.getMonth()] += Number(e.amount) || 0;
    }));
    const last = Math.max(thisYear, ...Array.from(acc.keys()).filter((k) => (acc.get(k)?.inc || 0) >= 1));
    const years = Array.from({ length: Math.min(6, last - thisYear + 1) }, (_, k) => {
      const yr = thisYear + k;
      const s = acc.get(yr) || { inc: 0, got: 0, cheques: 0, mon: Array(12).fill(0), exp: Array(12).fill(0) };
      return { yr, ...s, current: yr === thisYear };
    });
    return { years, thisYear };
  }, [units]);
  // The next cheques still to come, soonest first.
  const upcoming = React.useMemo(() => {
    const today = Date.now();
    const rows: { at: number; unit: string; tenant: string; amount: number; no: string }[] = [];
    units.forEach((u) => (u.rentSchedule || []).forEach((r: any) => {
      const at = r?.dueDate ? Date.parse(r.dueDate) : NaN;
      if (isNaN(at) || at <= today || chequeCollected(r) || !(Number(r.amount) > 0)) return;
      rows.push({ at, unit: String(u.name || [u.project, u.unitNumber].filter(Boolean).join(' ') || ''), tenant: String(u.currentTenant?.name || ''), amount: Number(r.amount), no: String(r.chequeNo || '') });
    }));
    return rows.sort((a, b) => a.at - b.at);
  }, [units]);
  const [showAllUpcoming, setShowAllUpcoming] = React.useState(false);
  // NET RENTAL PROFIT PER MONTH (owner, 2026-11-05: every month a lease runs
  // has an amount). For each lease running in the month: that lease year's
  // rent ÷ 12 — the lease year's cheques when recorded, so step-ups count from
  // their year, else the yearly rent — minus the unit's costs (recorded
  // expenses + service charge, per year) ÷ 12, the same costs as the box on
  // top. Cash dates are in the Upcoming cheques list, not here.
  // The SAME formula as the net yield on Home (leaseBook, portfolioMetrics.ts;
  // owner 2026-11-05: "should be same on the formula"): per lease, one year's
  // rent (contract rent ÷ lease years, so a multi-year lease counts per year)
  // minus one year's service charge and the costs booked in the lease,
  // ÷ 12 per month, in every month the lease runs.
  const book = React.useMemo(() => leaseBook(units), [units]);
  const leases = React.useMemo(() => book.rows.map((r) => {
    const s0 = r.start ? Date.parse(r.start) : NaN;
    const e0 = r.end ? Date.parse(r.end) : NaN;
    const years = !isNaN(s0) && !isNaN(e0) && e0 > s0 ? ((e0 - s0) / 86400000 + 1) / 365 : 1;
    return { s0, e0, rent: r.rent / years / 12, costs: (r.booked + (r.serviceCharge || 0)) / years / 12 };
  }), [book]);
  const netOf = (yr: number, mo: number) => {
    const start = new Date(yr, mo, 1).getTime(), end = new Date(yr, mo + 1, 1).getTime();
    let rent = 0, exp = 0;
    leases.forEach((l) => {
      if (!isNaN(l.s0) && l.s0 >= end) return;
      if (!isNaN(l.e0) && l.e0 < start) return;
      rent += l.rent;
      exp += l.costs;
    });
    return { rent, exp };
  };
  const perMonth = { rent: leases.reduce((a, l) => a + l.rent, 0), exp: leases.reduce((a, l) => a + l.costs, 0) };
  if (!c.leases && !c.months.some((m) => m.due > 0)) return null;
  const aed = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  const kfmt = (n: number) => { const a = Math.abs(n); const s = a >= 1000 ? `${Math.round(a / 1000)}k` : a >= 1 ? String(Math.round(a)) : ''; return s && n < 0 ? `−${s}` : s; };
  const dfmt = (t: number) => new Date(t).toLocaleDateString(de ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const cell: React.CSSProperties = { padding: '14px 16px', minWidth: 0 };
  const lab: React.CSSProperties = { fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: M.ink2, fontWeight: 600 };
  const big: React.CSSProperties = { ...figure, fontSize: 26, color: M.black, marginTop: 4 };
  // One chart for both views: per month, NET = cheques due − expenses
  // recorded. A month with costs and no cheque goes below the line, in red.
  const NetBars: React.FC<{ rows: { key: string; label: string; rent: number; exp: number; current: boolean }[]; gap: number; small?: boolean }> = ({ rows, gap, small }) => {
    const nets = rows.map((r) => r.rent - r.exp);
    const up = Math.max(0, ...nets), down = Math.max(0, ...nets.map((n) => -n));
    const span = Math.max(1, up + down);
    const H = 150, zero = (up / span) * H;
    return (
      <div>
        <div style={{ position: 'relative', height: H + 18, display: 'flex', gap }}>
          {rows.map((r, i) => {
            const n = nets[i];
            const h = (Math.abs(n) / span) * H;
            return (
              <div key={r.key} title={`${r.label}: ${de ? 'Miete' : 'rent'} ${aed(r.rent)} − ${de ? 'Kosten' : 'costs'} ${aed(r.exp)} = ${de ? 'netto' : 'net'} ${aed(n)}`}
                style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                {n !== 0 && (
                  <>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: n > 0 ? 18 + zero - h : 18 + zero, height: Math.max(3, h), background: n < 0 ? '#b91c1c' : r.current ? M.black : M.gold, borderRadius: n > 0 ? '5px 5px 0 0' : '0 0 5px 5px' }} />
                    <div style={{ position: 'absolute', left: -4, right: -4, top: n > 0 ? zero - h : 18 + zero + h + 2, fontSize: small ? 9.5 : 10.5, fontWeight: 700, color: n < 0 ? '#b91c1c' : M.ink2, textAlign: 'center', whiteSpace: 'nowrap' }}>{kfmt(n)}</div>
                  </>
                )}
              </div>
            );
          })}
          <div style={{ position: 'absolute', left: 0, right: 0, top: 18 + zero, height: 1, background: M.hair }} />
        </div>
        <div style={{ display: 'flex', gap, marginTop: down > 0 ? 18 : 6 }}>
          {rows.map((r) => <div key={r.key} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: small ? 10.5 : 11.5, color: r.current ? M.black : M.ink2, fontWeight: r.current ? 800 : 500 }}>{r.label}</div>)}
        </div>
      </div>
    );
  };
  const sel = pickedYear !== null ? (y.years.find((r) => r.yr === pickedYear) || y.years[0]) : null;
  const nowM = new Date().getMonth();
  const recentRows = c.months.map((m) => { const [yy, mm] = m.key.split('-').map(Number); return { key: m.key, label: m.label, ...netOf(yy, mm), current: m.current }; });
  const yearRows = sel ? Array.from({ length: 12 }, (_, k) => ({ key: `${sel.yr}-${k}`, label: MON[k], ...netOf(sel.yr, k), current: sel.current && k === nowM })) : [];
  const rows = sel ? yearRows : recentRows;
  const rowsRent = rows.reduce((s, r) => s + r.rent, 0), rowsExp = rows.reduce((s, r) => s + r.exp, 0);
  const shown = showAllUpcoming ? upcoming : upcoming.slice(0, 5);
  return (
    <div style={{ ...softCard, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${M.hair}` }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: M.gold }} />
        <span style={{ fontSize: 15, fontWeight: 800, color: M.black }}>{de ? 'Kontrollzentrum' : 'Control room'}</span>
        <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: M.goldDeep, border: `1px solid ${M.gold}`, borderRadius: 6, padding: '2px 7px' }}>LIVE</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div style={cell}>
          <div style={lab}>{de ? 'Nettogewinn Miete / Monat' : 'Net rental profit / month'}</div>
          <div style={big}>{aed(perMonth.rent - perMonth.exp)}</div>
          <div style={{ fontSize: 12, color: M.ink2, marginTop: 2 }}>{de ? `Miete ${aed(perMonth.rent)} − Kosten ${aed(perMonth.exp)} · ${leases.length} Verträge` : `rent ${aed(perMonth.rent)} − costs ${aed(perMonth.exp)} · ${leases.length} lease${leases.length === 1 ? '' : 's'}`}</div>
          {/* The same leaseBook as Home, so the two can never disagree. */}
          {book.netYield !== null && (
            <div style={{ fontSize: 12, color: M.black, fontWeight: 700, marginTop: 2 }}>
              {de ? `= ${book.netYield.toFixed(1)} % Nettorendite pro Jahr` : `= ${book.netYield.toFixed(1)}% net yield a year`}
            </div>
          )}
        </div>
        <div style={cell}>
          <div style={lab}>{de ? 'Eingegangen' : 'Received'}</div>
          <div style={big}>{c.pct === null ? '—' : `${c.pct.toFixed(1)}%`}</div>
          <div style={{ fontSize: 12, color: c.pct !== null && c.pct >= 95 ? '#1f7a3d' : '#9a6700', marginTop: 2 }}>{c.pct === null ? (de ? 'noch nichts fällig' : 'nothing due yet') : (de ? 'der fälligen Miete, 12 Monate' : 'of rent due, last 12 months')}</div>
        </div>
        <div style={cell}>
          <div style={lab}>{de ? 'Nächste 30 Tage' : 'Next 30 days'}</div>
          <div style={big}>{c.next}</div>
          <div style={{ fontSize: 12, color: M.ink2, marginTop: 2 }}>{c.next ? (de ? `Schecks · ${aed(c.nextAmt)}` : `cheque${c.next === 1 ? '' : 's'} · ${aed(c.nextAmt)}`) : (de ? 'keine Schecks fällig' : 'no cheques due')}</div>
        </div>
      </div>
      {/* Filter: the last 6 months, or one year at a time. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 16px 0' }}>
        {[null, ...y.years.map((r) => r.yr)].map((yr) => {
          const on = pickedYear === yr;
          return (
            <button key={yr ?? 'recent'} type="button" onClick={() => setPickedYear(yr)}
              style={{ all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 999, border: `1px solid ${on ? M.black : M.hair}`, background: on ? M.black : 'transparent', color: on ? '#fff' : M.ink2 }}>
              {yr === null ? (de ? 'Letzte 6 Monate' : 'Last 6 months') : yr}
            </button>
          );
        })}
      </div>
      <div style={{ padding: '10px 16px 14px' }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: M.black, marginBottom: 8 }}>{de ? 'Monatlicher Nettogewinn aus Miete' : 'Monthly net rental profit'}</div>
        <div style={{ marginBottom: 10, padding: '10px 12px', background: 'rgba(214,177,96,0.10)', borderRadius: 10, display: 'flex', flexWrap: 'wrap', gap: '6px 20px', alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: M.black }}>{sel ? sel.yr : (de ? 'Letzte 6 Monate' : 'Last 6 months')}</span>
          <span style={{ fontSize: 13, color: M.black }}>{de ? 'Nettogewinn' : 'Net profit'} <b>{aed(rowsRent - rowsExp)}</b></span>
          <span style={{ fontSize: 12, color: M.ink2 }}>
            {de ? 'Miete' : 'rent'} {aed(rowsRent)} − {de ? 'Kosten' : 'costs'} {aed(rowsExp)}
            {sel ? (de ? ` · ${sel.cheques} Schecks fällig (${aed(sel.inc)})` : ` · ${sel.cheques} cheque${sel.cheques === 1 ? '' : 's'} due (${aed(sel.inc)})`) : ''}
          </span>
        </div>
        {sel
          ? <NetBars small gap={4} rows={yearRows} />
          : <NetBars gap={10} rows={recentRows} />}
        {(() => {
          // The formula, worked through with this month's own figures.
          const d = new Date();
          const now = netOf(d.getFullYear(), d.getMonth());
          return (
            <div style={{ marginTop: 10, padding: '10px 12px', border: `1px solid ${M.hair}`, borderRadius: 10, fontSize: 12, lineHeight: 1.6, color: M.ink2 }}>
              <div style={{ fontWeight: 800, color: M.black }}>{de ? 'So wird es berechnet' : 'How it is calculated'}</div>
              <div>
                <b style={{ color: M.black }}>{de ? 'Nettogewinn pro Monat' : 'Net profit per month'}</b> = {de ? '(Jahresmiete − Servicegebühren) ÷ 12' : '(annual rent − service charges) ÷ 12'}
              </div>
              <div>
                {MON[d.getMonth()]} {d.getFullYear()}: {aed(now.rent)} − {aed(now.exp)} = <b style={{ color: M.black }}>{aed(now.rent - now.exp)}</b>
              </div>
              <div>
                {de
                  ? 'Jahresmiete = Vertragsmiete ÷ Vertragsjahre (mehrjährige Verträge auf ein Jahr gerechnet), wie bei der Nettorendite. Gezählt wird jeder Vertrag, der in dem Monat läuft. Wann das Geld eingeht, steht unten bei den kommenden Schecks.'
                  : 'Annual rent = contract rent ÷ lease years (multi-year leases counted per year), the same as the net yield. Every lease running in the month counts. When the money arrives: see Upcoming cheques below.'}
              </div>
            </div>
          );
        })()}
      </div>
      {upcoming.length > 0 && (
        <div style={{ borderTop: `1px solid ${M.hair}`, padding: '12px 16px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: M.black }}>{de ? 'Kommende Schecks' : 'Upcoming cheques'}</span>
            <span style={{ fontSize: 12, color: M.ink2 }}>{upcoming.length} · {aed(upcoming.reduce((s, r) => s + r.amount, 0))}</span>
          </div>
          <div style={{ marginTop: 6 }}>
            {shown.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '8px 0', borderBottom: `1px solid ${M.hair}`, fontSize: 13 }}>
                <span style={{ width: 92, flexShrink: 0, fontWeight: 700, color: M.black }}>{dfmt(r.at)}</span>
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                  <span style={{ color: M.black, fontWeight: 600 }}>{r.unit}</span>
                  {r.tenant && <span style={{ color: M.ink2, fontSize: 12 }}> · {r.tenant}</span>}
                </span>
                <span style={{ flexShrink: 0, fontWeight: 800, color: M.black }}>{aed(r.amount)}</span>
              </div>
            ))}
          </div>
          {upcoming.length > 5 && (
            <button type="button" onClick={() => setShowAllUpcoming((v) => !v)}
              style={{ all: 'unset', cursor: 'pointer', marginTop: 8, fontSize: 12.5, fontWeight: 700, color: M.goldDeep }}>
              {showAllUpcoming ? (de ? 'Weniger anzeigen' : 'Show less') : (de ? `Alle ${upcoming.length} anzeigen` : `Show all ${upcoming.length}`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// A unit that has been sold is no longer part of the portfolio: it is not a
// property the owner holds, earns nothing and costs nothing. Status "sold" or
// a recorded sale date (soldAt / saleDate) both count.
const isSoldUnit = (u: any): boolean => /^sold$/i.test(String(u?.status || '').trim()) || !!u?.soldAt || !!u?.saleDate;

const DashboardTab: React.FC<{ units: any[]; onAsk?: (m: string) => void; refresh?: React.ReactNode }> = ({ units: allUnits, onAsk, refresh }) => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  // Every figure on this tab is about the units still held.
  const units = allUnits.filter((u) => !isSoldUnit(u));
  const soldCount = allUnits.length - units.length;
  const [showExpenses, setShowExpenses] = React.useState(false);
  const [showServiceCharge, setShowServiceCharge] = React.useState(false);
  const [showNet, setShowNet] = React.useState(false);
  const metrics = units.map(unitMetrics);
  // Total rental income = each unit's annual rent + one extra year for every past renewal.
  const totalRental = metrics.reduce((s, m, i) => s + m.rent + ((units[i].tenancyHistory || []).filter((h: any) => h.renewed).length) * m.rent, 0);
  const totalRent = metrics.reduce((s, m) => s + m.rent, 0);
  // Recorded expenses and the resolved service charges are shown as one
  // "costs" figure, because splitting them here would imply the owner had
  // booked service charges they have not — the invoice goes to them, not us.
  //
  // LET UNITS ONLY. This card sets costs against rent, and only a let unit
  // earns rent — summing the service charge of every empty, handed-over unit
  // as well put AED 176k of "expenses" against the rent of two units, when
  // the let units' own charge was ~25k. Holding costs on empty units are real,
  // but they are not a cost of the rental income shown here.
  const letMetrics = metrics.filter((_, i) => !!units[i].currentTenant);
  const totalExp = letMetrics.reduce((s, m) => s + m.expenses + (m.serviceCharge || 0), 0);
  const totalNet = totalRent - totalExp;
  // How much of the let units' net is genuinely after service charges, so
  // the figure is never presented as complete when it is not.
  const scCovered = letMetrics.filter((m) => m.serviceChargeKnown).length;
  // From facts.serviceCharge.annual, NOT netIncome.serviceCharge — that one is
  // deliberately 0 because the invoice is already one of the expense rows and
  // must not be deducted twice. This tile reports the charge itself.
  const totalServiceCharge = units.reduce(
    (sum, u: any) => sum + (u.facts?.serviceCharge?.annual || 0), 0,
  );
  const scUnitsKnown = units.filter((u: any) => u.facts?.serviceCharge?.annual > 0).length;
  const rented = units.filter((u) => u.status === 'Rented').length;
  // Portfolio value and yield read the price via unitValue(), which covers
  // purchaseValue (Asset Desk) as well as purchasePrice (PM-created). Some
  // units still carry no figure at all, so both are reported against the
  // number that do rather than implying they cover the whole portfolio.
  const totalValue = metrics.reduce((a, m) => a + m.value, 0);
  const pricedCount = units.filter((u) => unitValue(u) > 0).length;
  const yieldPct = grossYield(totalRent, totalValue);
  const dueSoon = dueWithin(units, 90);

  // KPI figures — all from real emulator data.
  const collected = units.reduce((s, u) => s + (u.rentSchedule || []).filter(chequeCollected).reduce((a: number, r: any) => a + (r.amount || 0), 0), 0);
  const k = (n: number) => `${Math.round(n / 1000)}k`;
  const animatedValue = useCountUp(totalRental);

  // Lease renewals — tenancy/Ejari expiring within 3 months (or already overdue).
  const toMs = (v: any) => { if (!v) return 0; if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? 0 : t; } const s = v._seconds ?? v.seconds; return typeof s === 'number' ? s * 1000 : 0; };
  const nowMs = Date.now();
  const renewals = units
    .map((u) => ({ u, t: u.currentTenant, end: toMs(u.currentTenant?.contractEnd) }))
    .filter((x) => x.t && x.end > 0)
    .map((x) => ({ ...x, days: Math.ceil((x.end - nowMs) / 86400000) }))
    .filter((x) => x.days <= 90) // within 3 months, or negative = overdue
    .sort((a, b) => a.days - b.days);
  const tenanted = units.filter((u) => u.currentTenant && toMs(u.currentTenant.contractEnd) > 0).length;

  if (units.length === 0) {
    return <EmptyState icon={Home} title={t('noPropertiesYet')} text={t('pmNoPropsText')} />;
  }

  return (
    <div className="pt-stagger" style={{ padding: '18px 16px 8px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* The statement head. Obsidian ground with a single gold rule across
          the top — the brand's own device, and the one place on the screen
          allowed to be dark.

          What came out: the drop shadow (a statement does not float) and the
          220px radial gold glow bled into the corner, which was decoration
          standing in for hierarchy. The figures do that work now. */}
      <div style={{ borderRadius: 14, padding: 26, backgroundColor: '#000', backgroundImage: M.blackGrad, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: M.gold }} />
        <div style={{ position: 'relative' }}>
          {/* Classes carry no styling of their own — they exist so film mode can
              reflow this row inside a 430px frame, where the German eyebrow
              plus the occupancy badge plus "Aktualisieren" overrun the card.
              See html.film-frame .pm-hero-head in index.css. */}
          <div className="pm-hero-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ ...eyebrow, fontSize: T.micro, fontWeight: 700, color: M.gold }}>{t('pmTotalRentalIncome')}</span>
            <div className="pm-hero-meta" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: T.micro, fontWeight: 800, background: 'rgba(214,177,96,0.18)', color: M.gold, padding: '5px 12px', borderRadius: 99, border: '1px solid rgba(214,177,96,0.28)' }}>{rented} {t('pmOf')} {units.length} {t('pmRentedLower')}</span>
              {refresh}
            </div>
          </div>
          <div style={{ ...figure, fontSize: T.money + 6, marginTop: 8 }}>
            <span style={{ color: M.gold, fontSize: T.money - 12, fontWeight: 500, letterSpacing: '0.02em' }}>AED </span>
            {animatedValue.toLocaleString('en-AE')}
          </div>
          {/* Slim occupancy bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, maxWidth: 560 }}>
            <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.14)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${units.length ? Math.round((rented / units.length) * 100) : 0}%`, height: '100%', background: `linear-gradient(90deg, ${M.gold}, ${M.gold})`, borderRadius: 99, transition: 'width 0.7s ease' }} />
            </div>
            <span style={{ fontSize: T.micro, fontWeight: 800, color: M.gold, whiteSpace: 'nowrap' }}>{units.length ? Math.round((rented / units.length) * 100) : 0}% {t('pmOccupiedSuffix')}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 20, borderTop: '1px solid rgba(214,177,96,0.22)', paddingTop: 16 }}>
            <SumStat label={t('pmRentPerYear')} value={totalRent} />
            <SumStat label={t('expenses')} value={totalExp} onClick={() => setShowExpenses(true)} hint={de ? 'Aufschlüsselung' : 'Breakdown'} />
            <SumStat label={scCovered === letMetrics.length && letMetrics.length > 0 ? t('pmNetAfterCosts') : t('pmNetPerYear')} value={totalNet} strong
              onClick={() => setShowNet(true)} hint={de ? 'Berechnung' : 'Calculation'} />
          </div>
          {scCovered > 0 && scCovered < letMetrics.length && (
            <div style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.68)', marginTop: 10, lineHeight: 1.5 }}>
              {t('pmServiceCharge')}: {scCovered}/{letMetrics.length} · {t('pmSourceIndex')}
            </div>
          )}
          {/* Portfolio value + gross yield — only when a purchase price is
              actually on file, and always saying how many units it covers. */}
          {totalValue > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '10px 26px', marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(214,177,96,0.22)' }}>
              <div>
                <div style={{ ...eyebrow, fontSize: T.micro - 1, color: 'rgba(255,255,255,0.66)', fontWeight: 700 }}>{t('pmPortfolioValue')}</div>
                <div style={{ ...figure, fontSize: T.statValue + 6, color: '#fff', marginTop: 4 }}>
                  <span style={{ color: M.gold, fontSize: T.body, fontWeight: 500 }}>AED </span>{totalValue.toLocaleString('en-AE')}
                </div>
              </div>
              {yieldPct !== null && (
                <div>
                  <div style={{ ...eyebrow, fontSize: T.micro - 1, color: 'rgba(255,255,255,0.66)', fontWeight: 700 }}>{t('pmGrossYield')}</div>
                  <div style={{ ...figure, fontSize: T.statValue + 6, color: M.gold, marginTop: 4 }}>{yieldPct.toFixed(1)}%</div>
                </div>
              )}
              {/* Only worth saying when the figures do NOT cover everything —
                  "2 across 2" is noise. */}
              {pricedCount < units.length && (
                <div style={{ marginLeft: 'auto', fontSize: T.micro, color: 'rgba(255,255,255,0.62)', textAlign: 'right' }}>
                  {pricedCount} {t('pmAcrossN')} {units.length}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <OwnerControlRoom units={units} metrics={metrics} de={de} />

      {/* KPI tiles */}
      <div className="pm-kpis">
        <KpiTile icon={Building2} label={t('properties')} value={units.length}
          sub={soldCount ? (de ? `+${soldCount} verkauft` : `+${soldCount} sold`) : undefined}
          desc={soldCount
            ? (de ? 'Im Bestand, ohne verkaufte' : 'Held now, sold not counted')
            : (de ? 'Einheiten im Bestand' : 'Units you hold')} />
        <KpiTile icon={Users} label={t('occupied')} value={rented} sub={`/ ${units.length}`}
          desc={de ? 'Einheiten mit Mieter' : 'Units with a tenant'} />
        <KpiTile icon={Wallet} label={t('pmRentCollected')} value={`AED ${k(collected)}`} sub={`/ ${k(totalRent)}`}
          desc={de ? 'Eingelöste Schecks / Jahresmiete' : 'Cheques cleared / yearly rent'} />
        <KpiTile icon={Clock} label={t('pmDueNext90')} value={`AED ${kAED(dueSoon)}`}
          desc={de ? 'Mietschecks, nächste 90 Tage' : 'Rent cheques, next 90 days'} />
        {/* The standing cost an owner asks about most. `sub` says how many
            units the figure actually covers, so a partial number is never
            read as the whole portfolio's. */}
        <KpiTile
          icon={Receipt}
          label={`${t('pmServiceCharge')} (${de ? 'geschätzt' : 'est.'})`}
          value={`AED ${k(totalServiceCharge)}`}
          sub={`${scUnitsKnown}/${units.length}`}
          desc={de
            ? `Pro Jahr, ${scUnitsKnown} von ${units.length} Einheiten bekannt`
            : `Per year, ${scUnitsKnown} of ${units.length} units known`}
          hint={de ? 'Aufschlüsselung ›' : 'Breakdown ›'}
          onClick={() => setShowServiceCharge(true)}
        />
      </div>

      {/* What the portfolio itself says could be improved — every line is a
          fact counted off the units, and each one offers the service that
          fixes it. No line renders when its count is zero. */}
      <GrowCard units={units} renewSoon={renewals.length} onAsk={onAsk} />

      {showNet && (
        <NetBreakdown
          rows={units.map((u, i) => ({ u, m: metrics[i] })).filter((x) => !!x.u.currentTenant)}
          rent={totalRent} expenses={totalExp} net={totalNet} de={de} onClose={() => setShowNet(false)}
        />
      )}

      {showServiceCharge && (
        <ServiceChargeBreakdown units={units} total={totalServiceCharge} de={de} t={t} onClose={() => setShowServiceCharge(false)} />
      )}

      {showExpenses && (
        <ExpenseBreakdown
          rows={units.map((u, i) => ({ u, m: metrics[i] })).filter((x) => !!x.u.currentTenant)}
          total={totalExp} de={de} onClose={() => setShowExpenses(false)}
        />
      )}

      {/* Two balanced columns on desktop, one stack on a phone */}
      <div className="pm-two-col">
       <div className="pm-stack">
      {/* Lease renewals — Ejari / tenancy expiring within 3 months (or overdue) */}
      {tenanted > 0 && (
        <div style={{ ...softCard, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: renewals.length ? 16 : 8 }}>
            <FileSignature size={20} color={M.goldInk} />
            <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmLeaseRenewals')}</div>
            {renewals.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: T.micro, fontWeight: 800, color: M.goldDeep, background: theme.warningBg, padding: '4px 11px', borderRadius: 99 }}>{renewals.length} {t('pmDueSuffix')}</span>
            )}
          </div>
          {renewals.length === 0 ? (
            <div style={{ fontSize: T.secondary, color: M.ink2, lineHeight: 1.5 }}>{t('pmAllTenanciesCurrent')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {renewals.map(({ u, t: tn, days }) => {
                const overdue = days < 0;
                const urgent = overdue || days <= 30;
                const col = urgent ? theme.danger : theme.warning;
                const bg = urgent ? theme.dangerBg : theme.warningBg;
                const label = overdue
                  ? `${t('pmExpiredPre')} ${Math.abs(days)}${t('pmDayShort')} ${t('pmAgo')}`
                  : days === 0
                    ? t('pmExpiresToday')
                    : `${days} ${days === 1 ? t('pmDay') : t('pmDays')} ${t('pmLeft')}`;
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: T.body, fontWeight: 800, color: M.black, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{unitLabel(u)}</div>
                      <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tn.name} · {t('pmEnds')} {fmtTs(tn.contractEnd)}</div>
                    </div>
                    <span style={{ flexShrink: 0, fontSize: T.micro - 1, fontWeight: 800, color: col, background: bg, padding: '5px 11px', borderRadius: 99, whiteSpace: 'nowrap' }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Recent activity (real) */}
      <RecentActivity units={units} />
       </div>
       <div className="pm-stack">
      {/* Rental income by property — earning units only, sorted (keeps it clean) */}
      {(() => {
        const earning = units.map((u, i) => ({ u, rent: metrics[i].rent })).filter((x) => x.rent > 0).sort((a, b) => b.rent - a.rent);
        const maxE = Math.max(1, ...earning.map((x) => x.rent));
        const idle = units.length - earning.length;
        return (
          <div style={{ ...softCard, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <TrendingUp size={20} color={M.goldInk} />
              <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmRentalIncomeByProperty')}</div>
            </div>
            {earning.length === 0 ? (
              <div style={{ fontSize: T.secondary, color: M.ink2, padding: '6px 2px' }}>{t('pmNoEarning')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {earning.map(({ u, rent }) => (
                  <div key={u.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, fontSize: T.secondary, marginBottom: 7 }}>
                      <span style={{ color: M.black, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{unitLabel(u)}</span>
                      <span style={{ color: M.black, fontWeight: 800, flexShrink: 0 }}>AED {rent.toLocaleString('en-AE')}</span>
                    </div>
                    <div style={{ height: 12, background: M.track, borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.max(6, Math.round((rent / maxE) * 100))}%`, height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${M.goldDeep}, ${M.gold})`, transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {idle > 0 && earning.length > 0 && (
              <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${M.hair}` }}>
                + {idle} {idle === 1 ? t('pmOtherProperty') : t('pmOtherProperties')} {t('pmNotYetEarning')}
              </div>
            )}
          </div>
        );
      })()}
       </div>
      </div>
    </div>
  );
};

// One prompt: a counted fact plus the service that answers it.
const GrowRow: React.FC<{ icon: React.ComponentType<any>; text: string; cta: string; onClick?: () => void; last: boolean }> = ({ icon: Icon, text, cta, onClick, last }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '13px 0', borderBottom: last ? 'none' : `1px solid ${M.tintEdge}` }}>
    <div style={{ width: 34, height: 34, borderRadius: 10, background: '#fff', border: `1px solid ${M.tintEdge}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={17} color={M.goldInk} />
    </div>
    <span style={{ flex: 1, minWidth: 150, fontSize: T.body, fontWeight: 700, color: M.black }}>{text}</span>
    {onClick && (
      <button onClick={onClick} className="pt-tap" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 7, background: M.black, color: M.gold, border: 'none', borderRadius: 99, padding: '10px 17px', fontSize: T.micro, fontWeight: 800, cursor: 'pointer' }}>
        {cta} <ArrowUpRight size={15} />
      </button>
    )}
  </div>
);

const GrowCard: React.FC<{ units: any[]; renewSoon: number; onAsk?: (m: string) => void }> = ({ units, renewSoon, onAsk }) => {
  const { t } = useLanguage();
  const idle = units.filter((u) => unitMetrics(u).rent <= 0).length;
  // The owner payload's `documents` is a FILTERED subset — pmRoutes applies
  // isPmDoc() then visibleTo('owner') — so "this unit has no documents" is not
  // a claim it can support: a unit can hold an SPA or payment plan that never
  // reaches this array. What IS safe to say is that a unit we show as rented
  // has no tenancy contract here, which is the document we would be chasing
  // anyway. Non-rented units are never flagged: they have no lease to file.
  const missingLease = units.filter((u) => {
    const isRented = u.status === 'Rented' || !!u.currentTenant;
    if (!isRented) return false;
    return !(u.documents || []).some((d: any) =>
      /tenancy|ejari|lease|contract/i.test(`${d?.documentType || ''} ${d?.type || ''} ${d?.name || ''}`));
  }).length;

  const rows = [
    idle > 0 && { icon: Home, text: `${idle} ${idle === 1 ? t('pmGrowIdle1') : t('pmGrowIdle')}`, cta: t('pmGrowIdleCta'), msg: t('pmChatIdle') },
    renewSoon > 0 && { icon: FileSignature, text: `${renewSoon} ${renewSoon === 1 ? t('pmGrowRenew1') : t('pmGrowRenew')}`, cta: t('pmGrowRenewCta'), msg: t('pmChatRenew') },
    missingLease > 0 && { icon: FileText, text: `${missingLease} ${missingLease === 1 ? t('pmGrowDocs1') : t('pmGrowDocs')}`, cta: t('pmGrowDocsCta'), msg: t('pmChatDocs') },
  ].filter(Boolean) as { icon: React.ComponentType<any>; text: string; cta: string; msg: string }[];

  return (
    <div style={{ borderRadius: 20, padding: '18px 20px 20px', background: M.tint, border: `1px solid ${M.tintEdge}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <Sparkles size={19} color={M.goldInk} />
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmGrowTitle')}</div>
      </div>
      <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 4, marginBottom: rows.length ? 6 : 0 }}>{t('pmGrowSub')}</div>
      {rows.length === 0 ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
          <CheckCircle2 size={19} color={M.okInk} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: T.secondary, color: M.ink2, lineHeight: 1.5 }}>{t('pmGrowAllGood')}</span>
        </div>
      ) : rows.map((r, i) => (
        <GrowRow key={r.cta} icon={r.icon} text={r.text} cta={r.cta} last={i === rows.length - 1}
          onClick={onAsk ? () => onAsk(r.msg) : undefined} />
      ))}
    </div>
  );
};

// ---------------- Properties tab ----------------
/**
 * Which bucket a unit falls in.
 *
 * Case-insensitive on purpose: PM-created units carry title-case statuses
 * ('Rented', 'Vacant', 'Off-Plan') while the Asset Desk payload uses lowercase
 * ('active', 'selling', 'sold', 'rented'), and this list now renders either
 * shape — see the `units` memo in PropertyManagementView.
 *
 * "Active" means everything still owned, which is how the Asset Desk has always
 * counted it: a unit being marketed is still yours until it is sold.
 */
const statusBucket = (u: any): 'sold' | 'selling' | 'rented' | 'active' => {
  const s = (u?.status || '').toLowerCase();
  if (s === 'sold') return 'sold';
  if (s === 'selling') return 'selling';
  if (s === 'rented') return 'rented';
  return 'active';
};

const STATUS_FILTERS = [
  { key: 'active', label: 'pmStatusActive' },
  { key: 'rented', label: 'pmStatusRented' },
  { key: 'selling', label: 'pmStatusSelling' },
  { key: 'sold', label: 'pmStatusSold' },
] as const;

const PropertiesTab: React.FC<{ units: any[]; onSelect: (id: string) => void }> = ({ units, onSelect }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<string>('active');
  if (units.length === 0) {
    return <EmptyState icon={Home} title={t('noPropertiesYet')} text={t('pmNoPropsManagedText')} />;
  }
  // Counts are over the WHOLE portfolio, not the current filter — a tab that
  // reported the count of what it is already showing would be useless.
  const counts = {
    // Active is every unit not sold, so a rented unit appears under both. That
    // matches how the Asset Desk has always counted it, and an owner reading
    // "Active (18)" means "18 still mine", not "18 sitting empty".
    active: units.filter((u) => statusBucket(u) !== 'sold').length,
    rented: units.filter((u) => statusBucket(u) === 'rented').length,
    selling: units.filter((u) => statusBucket(u) === 'selling').length,
    sold: units.filter((u) => statusBucket(u) === 'sold').length,
  } as Record<string, number>;

  const visible = units.filter((u) =>
    filter === 'active' ? statusBucket(u) !== 'sold' : statusBucket(u) === filter);

  // Rented units first (that's what an owner cares about most), then vacant,
  // then everything else — stable within each group.
  const rank = (s?: string) => (s === 'Rented' ? 0 : s === 'Vacant' ? 1 : 2);
  const sorted = [...visible].sort((a, b) => rank(a.status) - rank(b.status));
  return (
    <div className="pt-fade" style={{ padding: '20px 16px 8px' }}>
      {/* The old header row — "Your Properties · 10" beside a "5 rented" chip —
          is gone. The filter below now carries both counts, and printing them
          twice, 40px apart, in two different treatments was the screen telling
          you the same thing twice. */}
      {/* Status filter — the same control as every other row of tabs in the
          shell, carrying its counts rather than a second bar style. */}
      <Segmented
        bare
        value={filter}
        onChange={setFilter}
        tabs={STATUS_FILTERS.map(({ key, label }) => ({ key, label, count: counts[key] }))}
      />
      {sorted.length === 0 && (
        <EmptyState icon={Home} title={t('pmNoneInStatus')} text={t('pmNoneInStatusText')} />
      )}
      <div className="pm-props">
        {sorted.map((u) => {
          const tn = u.currentTenant;
          const label = unitLabel(u);
          const rent = Number(tn?.annualRent) || (u.rentSchedule || []).reduce((a: number, r: any) => a + (r.amount || 0), 0);
          const rented = u.status === 'Rented';
          const accent = rented ? M.gold : 'rgba(0,0,0,0.16)';
          const tint = rented ? M.tint : M.wash;
          // A photo of the unit's own Dubai Sell listing, or of the project,
          // beats the curated project map only when the map has no entry —
          // hence this order (see pmRoutes: facts.photo carries its kind).
          const f = factsOf(u);
          const photo = unitPhoto(u) || f.photo?.url || '';
          const leaseEnd = tn?.contractEnd ? fmtTs(tn.contractEnd) : '';
          const building = isUnderConstruction(u);
          const handover = quarterOf(f.construction?.handover);
          const expected = !rent && f.expectedRent?.available ? f.expectedRent : null;
          const sc = f.serviceCharge;
          // Compact spec line — bedrooms / size — so each card carries more detail.
          const spec = [u.propertyType || (u.bedrooms ? `${u.bedrooms} BR` : ''), u.sizeSqft ? `${Number(u.sizeSqft).toLocaleString('en-AE')} sqft` : u.areaSqm ? `${u.areaSqm} sqm` : '']
            .filter(Boolean).join(' · ');
          const openMaint = (u.maintenance || []).filter((m: any) => maintStatus(m.status).label !== 'Completed').length;
          const y = grossYield(rent, unitValue(u));
          return (
            <button key={u.id} onClick={() => onSelect(u.id)} className="pt-card pt-tap pm-prop-card" style={{ position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', width: '100%', border: 'none', textAlign: 'left', padding: '18px 18px 18px 22px', borderRadius: 20 }}>
              {/* Status accent stripe — a calm, premium signal of rented/vacant */}
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: accent }} />

              {/* --- Identity: photo, name, where and what it is ----------- */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 64, height: 64, borderRadius: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)', ...(photo ? { backgroundColor: 'rgba(0,0,0,0.06)', backgroundImage: `url(${photo})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: tint }) }}>
                  {!photo && <Building2 size={26} color={accent} />}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* THE NAME WINS. The status pill used to be flexShrink: 0
                      beside a flex: 1 title, so a wide pill — "Under
                      construction · 97%" runs about 175px — took what it
                      needed first and left the title whatever remained. In a
                      two-up grid that was roughly 33px, and two cards rendered
                      with no readable name at all.

                      The row wraps now and the title holds a 150px floor, so
                      when both cannot sit on one line it is the PILL that
                      drops beneath — the name is how you identify the
                      property, the status is a detail. */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', rowGap: 6 }}>
                    <div style={{ flex: '1 1 auto', minWidth: 150, fontSize: T.primary, fontWeight: 800, color: M.black, letterSpacing: '-0.2px', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', userSelect: 'text' }}>{label}</div>
                    <span style={{ flexShrink: 0 }}><StatusPill status={u.status} unit={u} /></span>
                  </div>
                  {/* Two lines rather than one ellipsis. "Mina Rashid ·
                      Apartment · 1,412 sqft" needs up to 340px and had 216;
                      cut to "Mina Rashid · Apartment · 1,…" it lost the size,
                      which is the part an owner is checking. */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, fontSize: T.secondary, color: M.ink2, marginTop: 5, minWidth: 0 }}>
                    <MapPin size={13} style={{ flexShrink: 0, color: M.ink3, marginTop: 3 }} />
                    <span style={{ minWidth: 0, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {u.community || u.location || '—'}{spec ? ` · ${spec}` : ''}
                    </span>
                  </div>
                </div>

                <ChevronRight size={20} color={M.ink4} style={{ flexShrink: 0 }} />
              </div>

              {/* --- The one line that matters ------------------------------
                  A let unit is judged on what it earns and who is in it. A
                  unit still being built is judged on how far along it is and
                  when the keys arrive. Same slot, two different truths — and
                  nothing else competing with them.

                  Everything that used to sit here as chips (mortgage, service
                  charge, net, open jobs, letting mode) moved to the property's
                  own screen: eleven facts on a list card is a spreadsheet, and
                  none of them answer "how is this one doing?". */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${M.hair}` }}>
                {building ? (
                  <>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{t('pmConstruction')}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 6 }}>
                        <div style={{ flex: 1, minWidth: 40, height: 5, borderRadius: 99, background: M.track, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(100, Math.max(0, Math.round(f.construction?.percent ?? 0)))}%`, height: '100%', background: M.gold }} />
                        </div>
                        <span style={{ fontSize: T.secondary, fontWeight: 800, color: M.black }}>{Math.round(f.construction?.percent ?? 0)}%</span>
                      </div>
                    </div>
                    {handover && (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{t('pmExpectedHandover')}</div>
                        <div style={{ fontSize: T.primary, fontWeight: 800, color: M.black, marginTop: 4 }}>{handover}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{t('pmTenant')}</div>
                      <div style={{ fontSize: T.body, fontWeight: 800, color: tn ? M.black : M.ink3, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tn ? tn.name : (u.__tenancyUnknown ? '—' : t('vacant'))}</div>
                      {leaseEnd && <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 3 }}>{t('pmLeaseEnds')} {leaseEnd}</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>
                        {rent ? t('pmRentPerYear') : expected ? t('pmExpectedRent') : t('pmRentPerYear')}
                      </div>
                      <div style={{ fontSize: T.statValue, fontWeight: 800, color: rent ? M.black : expected ? M.ink2 : M.ink4, marginTop: 4, letterSpacing: '-0.3px' }}>
                        {rent
                          ? <><span style={{ fontSize: T.secondary, color: M.goldInk, fontWeight: 800 }}>AED </span>{rent.toLocaleString('en-AE')}</>
                          : expected
                            ? <><span style={{ fontSize: T.secondary, color: M.goldInk, fontWeight: 800 }}>~AED </span>{Math.round(expected.annual || 0).toLocaleString('en-AE')}</>
                            : '—'}
                      </div>
                      {/* `rent > 0`, not `rent`: JSX renders a bare 0. */}
                      {rent > 0 && y !== null && (
                        <div style={{ fontSize: T.micro, fontWeight: 800, color: M.goldInk, marginTop: 3 }}>{y.toFixed(1)}% {t('pmYield')}</div>
                      )}
                      {!rent && expected && (
                        <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 3 }}>{t('pmMarketEstimate')}</div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* One exception to the clear-out: an open maintenance job is
                  something the owner may need to act on, so it stays — as a
                  single quiet line, not a chip in a row of chips. */}
              {openMaint > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: T.micro, fontWeight: 700, color: M.goldInk }}>
                  <Wrench size={13} style={{ flexShrink: 0 }} />
                  <span>{openMaint} {openMaint === 1 ? t('pmMaintJob') : t('pmMaintJobs')} {t('pmInProgressLower')}</span>
                </div>
              )}
            </button>

          );
        })}
      </div>
    </div>
  );
};

// Fetch a PM file WITH the owner's auth token and return the raw blob + content
// type. Keeping it a blob means the backend URL is never shown, and the request
// carries the Bearer token (the file endpoints are HMAC-token-signed).
async function fetchDocBlob(href: string): Promise<{ blob: Blob; contentType: string }> {
  let token: string | null = null;
  try { token = localStorage.getItem('authToken') || localStorage.getItem('token'); } catch { /* ignore */ }
  const res = await fetch(href, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return { blob, contentType: res.headers.get('content-type') || blob.type || 'application/octet-stream' };
}

// Trigger a browser download of a blob (owner web app).
function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name || 'document';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

// ---------------- Documents tab (all properties, grouped) ----------------
const DocumentsTab: React.FC<{ units: any[]; ownerId: string; demoMode?: boolean }> = ({ units, ownerId, demoMode }) => {
  const { t } = useLanguage();
  // Documents come with the owner's units payload — no extra per-unit calls.
  const groups = units.map((u) => ({
    unit: u,
    docs: (u.documents || []).map((d: any, j: number) => ({
      id: d.id || `doc-${u.id}-${j}`, name: d.name, type: d.documentType || d.type || t('pmDocument'),
      date: d.date ? fmt(d.date) : undefined,
      // On the sample portfolio the fixtures carry no file. Marking them as
      // present lets the row stay live; openDoc builds the page itself.
      fileUrl: d.fileUrl || (demoMode ? 'demo' : undefined),
    })) as DocVM[],
  }));
  const total = groups.reduce((s, g) => s + g.docs.length, 0);

  // Hold the fetched blob so DocumentViewerModal — the SAME pdf.js canvas viewer
  // used in the Asset Desk Properties tab — renders it (no iframe, no CSP frame
  // dependency, with page navigation). Backend URL is never exposed.
  const [viewer, setViewer] = useState<{ blob: Blob; name: string; type: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Fetch the blob, and if the signed URL has gone stale (rare — URLs refresh
  // every 30s, tokens last 12h), silently re-fetch a FRESH signed URL for THIS
  // exact file and retry once. This keeps viewing/downloading working long-term,
  // never a "link expired" dead end.
  const loadBlob = async (d: DocVM) => {
    try {
      return await fetchDocBlob(`${MEDIA_BASE}${d.fileUrl}`);
    } catch (e) {
      const base = (d.fileUrl || '').split('?')[0]; // path identifies the file; query holds the expiring token
      const fresh = await portalFetch<any>(`/pm/owner/${ownerId}/units`).catch(() => null);
      const fd = (fresh?.units || [])
        .flatMap((u: any) => u.documents || [])
        .find((x: any) => (x.fileUrl || '').split('?')[0] === base);
      if (fd?.fileUrl) return await fetchDocBlob(`${MEDIA_BASE}${fd.fileUrl}`);
      throw e;
    }
  };

  const openDoc = async (d: DocVM, mode: 'view' | 'download') => {
    if (!d.fileUrl) return;
    setBusyId(d.id);
    try {
      // The sample portfolio has no files behind its documents, so View and
      // Download did nothing at all. Build a real one-page PDF instead: View
      // renders it in the same pdf.js viewer a live document uses, Download
      // saves a file that opens anywhere. Watermarked SAMPLE DOCUMENT.
      if (demoMode) {
        const owner = groups.find((g) => g.docs.some((x) => x.id === d.id))?.unit;
        const tn = owner?.currentTenant;
        const blob = buildSamplePdf(d.name || t('pmDocument'), unitLabel(owner || {}), [
          { label: t('pmDocument'), value: d.type || '' },
          { label: t('date'), value: d.date || '' },
          ...(tn?.name ? [{ label: t('pmTenant'), value: String(tn.name) }] : []),
          ...(tn?.annualRent ? [{ label: t('pmRentPerYear'), value: `AED ${Number(tn.annualRent).toLocaleString('en-AE')}` }] : []),
          ...(tn?.ejariNo ? [{ label: t('pmEjariNo'), value: String(tn.ejariNo) }] : []),
          ...(owner?.dewaPremisesNo ? [{ label: t('pmDewaPremises'), value: String(owner.dewaPremisesNo) }] : []),
        ]);
        if (mode === 'view') setViewer({ blob, name: d.name || t('pmDocument'), type: 'application/pdf' });
        else downloadBlob(blob, (d.name || 'document').replace(/\.pdf$/i, '') + '.pdf');
        return;
      }
      const { blob, contentType } = await loadBlob(d);
      if (mode === 'view') setViewer({ blob, name: d.name || t('pmDocument'), type: contentType });
      else downloadBlob(blob, d.name || 'document');
    } catch {
      alert(t('pmDocOpenError'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="pt-fade" style={{ padding: '18px 16px 8px' }}>
      {total === 0 && <EmptyState icon={FileText} title={t('noDocuments')} text={t('pmNoDocsText')} />}
      <div className="pm-groups">
      {groups.map((g) => g.docs.length > 0 && (
        <div key={g.unit.id}>
          <GroupHeading label={unitLabel(g.unit)} count={g.docs.length} />
          <div className="pt-card" style={{ padding: 8 }}>
            {g.docs.map((d, i) => (
              <DocRow key={d.id} d={d} last={i === g.docs.length - 1} busy={busyId === d.id}
                onView={() => openDoc(d, 'view')} onDownload={() => openDoc(d, 'download')} />
            ))}
          </div>
        </div>
      ))}
      </div>
      {viewer && (
        <DocumentViewerModal
          blob={viewer.blob}
          fileName={viewer.name}
          contentType={viewer.type}
          onClose={() => setViewer(null)}
          onDownload={() => downloadBlob(viewer.blob, viewer.name)}
        />
      )}
    </div>
  );
};

/**
 * One unit's documents, inside its detail screen.
 *
 * Same viewer, same sample-PDF behaviour and the same stale-URL retry as the
 * portfolio-wide DocumentsTab — but scoped to this unit, and refreshing signed
 * URLs from /pm/unit/:id rather than re-pulling the whole portfolio.
 */
const UnitDocumentsTab: React.FC<{
  docs: DocVM[]; unitId: string; unitLabelText: string; demoMode?: boolean; facts?: { label: string; value: string }[];
  investor?: any;
  onSmartUpload?: (e: React.ChangeEvent<HTMLInputElement>, unitId?: string) => void;
  smartUploading?: boolean;
}> = ({ docs, unitId, unitLabelText, demoMode, facts, investor, onSmartUpload, smartUploading }) => {
  const { t } = useLanguage();
  const [viewer, setViewer] = useState<{ blob: Blob; name: string; type: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // The sample portfolio carries no files, so mark its rows live and let
  // openDoc build the page — otherwise every row would sit disabled in a demo.
  const rows = docs.map((d) => ({ ...d, fileUrl: d.fileUrl || (demoMode ? 'demo' : undefined) }));

  const loadBlob = async (d: DocVM) => {
    try {
      return await fetchDocBlob(`${MEDIA_BASE}${d.fileUrl}`);
    } catch (e) {
      // Signed URLs refresh every 30s and tokens last 12h, so a stale link is
      // rare — but it must never be a dead end. Re-fetch THIS unit and match
      // on the path, which identifies the file; the query holds the token.
      const base = (d.fileUrl || '').split('?')[0];
      const fresh = await portalFetch<any>(`/pm/unit/${unitId}`).catch(() => null);
      const fd = (fresh?.documents || []).find((x: any) => (x.fileUrl || '').split('?')[0] === base);
      if (fd?.fileUrl) return await fetchDocBlob(`${MEDIA_BASE}${fd.fileUrl}`);
      throw e;
    }
  };

  const openDoc = async (d: DocVM, mode: 'view' | 'download') => {
    if (!d.fileUrl) return;
    setBusyId(d.id);
    try {
      if (demoMode) {
        const blob = buildSamplePdf(d.name || t('pmDocument'), unitLabelText, [
          { label: t('pmDocument'), value: d.type || '' },
          { label: t('date'), value: d.date || '' },
          ...(facts || []),
        ]);
        if (mode === 'view') setViewer({ blob, name: d.name || t('pmDocument'), type: 'application/pdf' });
        else downloadBlob(blob, (d.name || 'document').replace(/\.pdf$/i, '') + '.pdf');
        return;
      }
      const { blob, contentType } = await loadBlob(d);
      if (mode === 'view') setViewer({ blob, name: d.name || t('pmDocument'), type: contentType });
      else downloadBlob(blob, d.name || 'document');
    } catch {
      alert(t('pmDocOpenError'));
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Email the files in. The subject and body carry the account id because the
   * team matches an inbound mail to an account by hand — stripping it means
   * the documents arrive unattributable.
   */
  const emailDocuments = () => {
    const id = investor?.id || '';
    const addr = investor?.email || '';
    const subject = encodeURIComponent(`Documents from ${investor?.name || 'Investor'} [ID:${id}] — ${unitLabelText}`);
    const body = encodeURIComponent(
      `Dear Asset Desk Team,

Please find my documents attached.

---
Property: ${unitLabelText}
Investor: ${investor?.name || ''}
ID: ${id}
Email: ${addr}
(Please do not remove this information)`,
    );
    const cc = addr ? `&cc=${encodeURIComponent(addr)}` : '';
    window.location.href = `mailto:documents@assetdesk.ae?subject=${subject}&body=${body}${cc}`;
  };

  return (
    <div className="pt-fade" style={{ padding: '18px 16px 8px' }}>
      {/* One way in, stated once: the AI upload is the button, email is the
          fallback written out as the address. Two equal cards left owners
          unsure which to tap. Hidden in the showcase, where uploads are off. */}
      {!demoMode && (
        <div className="pm-docactions" style={{ marginBottom: 16 }}>
          {onSmartUpload && (
            <label
              htmlFor={`pm-smart-upload-${unitId}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                padding: '14px 16px', borderRadius: 14,
                background: smartUploading ? '#b8942e' : `linear-gradient(135deg, ${M.gold}, #b8942e)`,
                color: '#fff', fontSize: T.secondary, fontWeight: 800,
                cursor: smartUploading ? 'not-allowed' : 'pointer',
              }}
            >
              {smartUploading ? (
                <><RefreshCw size={18} className="pt-rotate" /> {t('pmSmartUploading')}</>
              ) : (
                <><Upload size={18} /> {t('pmSmartUpload')}</>
              )}
              <input
                id={`pm-smart-upload-${unitId}`}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => onSmartUpload(e, unitId)}
                disabled={smartUploading}
                style={{ display: 'none' }}
              />
            </label>
          )}
          <p style={{ margin: onSmartUpload ? '8px 0 0' : 0, textAlign: 'center', fontSize: T.micro, color: M.ink3 }}>
            {onSmartUpload ? t('pmOrEmailDocs') : t('pmEmailDocs')}{' '}
            <a
              href="#"
              onClick={e => { e.preventDefault(); emailDocuments(); }}
              style={{ color: '#8A6C22', fontWeight: 700, textDecoration: 'underline' }}
            >
              documents@assetdesk.ae
            </a>
          </p>
        </div>
      )}
      {rows.length === 0 ? (
        /* "None here", not "none exist": the owner payload filters documents
           through isPmDoc() and visibleTo('owner'), so this list can never
           support the claim that the unit has no paperwork. */
        <EmptyState icon={FileText} title={t('noDocuments')} text={t('pmNoDocsText')} />
      ) : (
        <div className="pt-card" style={{ padding: 8 }}>
          {rows.map((d, i) => (
            <DocRow
              key={d.id}
              d={d}
              last={i === rows.length - 1}
              busy={busyId === d.id}
              onView={() => openDoc(d, 'view')}
              onDownload={() => openDoc(d, 'download')}
            />
          ))}
        </div>
      )}
      {viewer && (
        <DocumentViewerModal
          blob={viewer.blob}
          fileName={viewer.name}
          contentType={viewer.type}
          onClose={() => setViewer(null)}
          onDownload={() => downloadBlob(viewer.blob, viewer.name)}
        />
      )}
    </div>
  );
};

const DocRow: React.FC<{ d: DocVM; last: boolean; busy?: boolean; onView: () => void; onDownload: () => void }> = ({ d, last, busy, onView, onDownload }) => {
  const { t } = useLanguage();
  const off = !d.fileUrl;
  return (
    <div className="pm-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px', borderRadius: 12, borderBottom: last ? 'none' : `1px solid ${M.hair}` }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: docBg(d.type), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {docIcon(d.type)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T.secondary, fontWeight: 700, color: M.black, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
        <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{trData(d.type)}{d.date ? ` · ${d.date}` : ''}</div>
      </div>
      {/* Both actions as icons. A labelled "View" button plus a 42px download
          tile took ~145px of a 430px row: the filename was squeezed to nothing
          and the pair pushed off the right edge, so the owner had to scroll
          sideways to reach their own document. An eye and a download arrow are
          understood without the word, and both carry a title + aria-label. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, opacity: off ? 0.45 : 1, pointerEvents: off ? 'none' : 'auto' }}>
        {/* View — renders the document inside the app (no backend URL shown) */}
        <button onClick={onView} disabled={busy} title={t('pmView')} aria-label={t('pmView')} className="pt-tap" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, border: 'none', background: M.tint, color: M.goldInk, borderRadius: 10, cursor: busy ? 'default' : 'pointer' }}>
          {busy ? <RefreshCw size={16} className="pt-rotate" /> : <Eye size={17} />}
        </button>
        {/* Download — saves a copy (also via blob, so no backend URL) */}
        <button onClick={onDownload} disabled={busy} title={t('pmDownload')} aria-label={t('pmDownload')} className="pt-tap" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, border: 'none', background: M.wash, cursor: busy ? 'default' : 'pointer' }}>
          <Download size={17} color={M.ink2} />
        </button>
      </div>
    </div>
  );
};

/**
 * Add an expense, from the property it belongs to.
 *
 * Until now this tab could only report what the managing team had entered —
 * but an owner pays for things directly (a plumber, a chiller top-up, a new
 * AC filter) and had nowhere to put them, so their own net income was wrong
 * by whatever they had spent. The form sits under the property it affects,
 * which is the only place the entry is unambiguous.
 *
 * On the sample portfolio the row is added locally, so the demo behaves like
 * the product without writing anything anywhere.
 */
const AddExpenseForm: React.FC<{
  unitId: string;
  demoMode?: boolean;
  onAdd: (e: Expense) => void;
  onClose: () => void;
}> = ({ unitId, demoMode, onAdd, onClose }) => {
  const { t } = useLanguage();
  const today = new Date().toISOString().slice(0, 10);
  const [type, setType] = useState('Maintenance');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today);
  const [saving, setSaving] = useState(false);

  const TYPES = ['Maintenance', 'Service charge', 'Management fee', 'Utilities', 'Other'];
  const value = Number(amount) || 0;
  const ready = value > 0 && description.trim().length > 0;

  const field: React.CSSProperties = {
    width: '100%', padding: '11px 12px', borderRadius: 10, border: `1px solid ${M.hair}`,
    background: '#fff', color: M.black, fontSize: T.secondary, fontFamily: 'inherit', fontWeight: 600,
  };

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    const row: Expense = {
      id: `exp-${Date.now().toString(36)}`,
      type, description: description.trim(), amount: value,
      // Dated today or earlier means it is already settled — the same rule the
      // rest of this screen reads off a date.
      status: new Date(date).getTime() < new Date().setHours(0, 0, 0, 0) ? 'paid' : 'due',
      date: fmt(date),
    } as Expense;
    try {
      if (!demoMode) {
        await portalFetch(`/pm/unit/${unitId}/expense`, {
          method: 'POST',
          body: JSON.stringify({ type, description: description.trim(), amount: value, date, addedBy: 'owner' }),
        }, 15000);
      }
      onAdd(row);
      onClose();
    } catch {
      alert(t('pmExpenseSaveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderTop: `1px solid ${M.hair}`, marginTop: 14, paddingTop: 14, display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
        <input
          id={`exp-desc-${unitId}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('pmExpenseWhat')}
          style={field}
        />
        <input
          id={`exp-amt-${unitId}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
          inputMode="decimal"
          placeholder="AED"
          style={{ ...field, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 10 }}>
        <select id={`exp-type-${unitId}`} value={type} onChange={(e) => setType(e.target.value)} style={field}>
          {TYPES.map((x) => <option key={x} value={x}>{trData(x)}</option>)}
        </select>
        <input id={`exp-date-${unitId}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="pt-tap" style={{ border: `1px solid ${M.hair}`, background: '#fff', color: M.ink2, borderRadius: 10, padding: '10px 16px', fontSize: T.secondary, fontWeight: 700, cursor: 'pointer' }}>
          {t('cancel')}
        </button>
        <button
          onClick={submit}
          disabled={!ready || saving}
          className="pt-tap"
          style={{ border: 'none', background: ready ? M.black : M.track, color: ready ? '#fff' : M.ink3, borderRadius: 10, padding: '10px 18px', fontSize: T.secondary, fontWeight: 800, cursor: ready ? 'pointer' : 'default' }}
        >
          {saving ? t('loading') : t('pmAddExpense')}
        </button>
      </div>
    </div>
  );
};

/**
 * An expense's state, inferred from its date when the record does not say.
 *
 * A charge dated months ago is one the owner has already settled — leaving it
 * as "due" says the portfolio owes money it does not, and an owner reading a
 * statement full of stale dues stops trusting the column. Anything dated today
 * or later stays due; an explicit status on the record always wins.
 */
function expenseStatus(e: any): string {
  if (e?.status) return e.status;
  const t0 = e?.date ? new Date(e.date).getTime() : NaN;
  if (isNaN(t0)) return 'due';
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  return t0 < startOfToday ? 'paid' : 'due';
}

// ---------------- Expenses tab (all properties, grouped) ----------------
const ExpensesTab: React.FC<{ units: any[]; demoMode?: boolean }> = ({ units, demoMode }) => {
  const { t } = useLanguage();
  // Rows added from this screen, kept per unit until the next refresh brings
  // them back from the server (or, on the sample portfolio, for the session).
  const [added, setAdded] = useState<Record<string, Expense[]>>({});
  const [adding, setAdding] = useState<string | null>(null);

  const groups = units.map((u) => ({
    unit: u,
    items: [
      ...((u.expenses || []) as any[]).map((e, i) => ({
        // `type` from the admin console, `category` when seeded on the unit.
        id: e.id || `e${i}`, type: e.type || e.category || t('other'), description: e.description || '',
        amount: Number(e.amount) || 0, status: expenseStatus(e), date: e.date ? fmt(e.date) : undefined,
      })),
      ...(added[u.id] || []),
    ] as Expense[],
  }));
  const all = groups.flatMap((g) => g.items);
  const total = all.reduce((a, e) => a + e.amount, 0);
  const paid = all.filter((e) => e.status === 'paid').reduce((a, e) => a + e.amount, 0);
  const due = total - paid;
  const hasAny = all.length > 0;
  const pct = total ? Math.round((paid / total) * 100) : 0;

  return (
    <div className="pt-fade" style={{ padding: '18px 16px 8px' }}>
      {/* Total plus a paid / outstanding split, so the headline number has
          context instead of sitting alone in a very wide empty card. */}
      <div className="pm-card" style={{ borderRadius: 20, padding: 22, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{t('pmTotalExpensesYear')}</div>
            <div style={{ fontSize: T.money, fontWeight: 800, color: M.black, marginTop: 5, letterSpacing: '-0.5px' }}>
              <span style={{ fontSize: T.money - 10, color: M.gold, fontWeight: 800 }}>AED </span>{total.toLocaleString('en-AE')}
            </div>
          </div>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(145deg, rgba(214,177,96,0.24), rgba(214,177,96,0.09))', boxShadow: 'inset 0 0 0 1px rgba(214,177,96,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Receipt size={26} color={M.gold} />
          </div>
        </div>
        {hasAny && (
          <>
            <div style={{ height: 8, background: M.track, borderRadius: 99, overflow: 'hidden', marginTop: 18, maxWidth: 620 }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${M.goldDeep}, ${M.gold})`, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${M.hair}`, maxWidth: 620 }}>
              <div>
                <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{t('paid')}</div>
                <div style={{ fontSize: T.statValue, fontWeight: 800, color: theme.success, marginTop: 3 }}>AED {paid.toLocaleString('en-AE')}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{t('pmDue')}</div>
                <div style={{ fontSize: T.statValue, fontWeight: 800, color: due ? theme.warning : M.ink2, marginTop: 3 }}>AED {due.toLocaleString('en-AE')}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Where the money went, two ways.
          A single total answers "how much?" and nothing else. The two
          questions an owner asks next are "on what?" and "which property is
          costing me?" — so: spend by category, and properties ranked by what
          they consumed. Both are shares of the same total, so the bars are
          directly comparable across the two lists. */}
      {hasAny && (() => {
        const byCategory = new Map<string, number>();
        all.forEach((e) => byCategory.set(e.type, (byCategory.get(e.type) || 0) + e.amount));
        const categories = [...byCategory.entries()]
          .map(([label, amount]) => ({ label, amount }))
          .sort((a, b) => b.amount - a.amount);

        const properties = groups
          .map((g) => ({ label: unitLabel(g.unit), amount: g.items.reduce((a, e) => a + e.amount, 0) }))
          .filter((r) => r.amount > 0)
          .sort((a, b) => b.amount - a.amount);

        const Bar: React.FC<{ rows: { label: string; amount: number }[]; heading: string; note?: string }> = ({ rows, heading, note }) => (
          <div className="pt-card" style={{ padding: 20 }}>
            <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 800 }}>{heading}</div>
            {note && <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 4 }}>{note}</div>}
            <div style={{ display: 'grid', gap: 13, marginTop: 15 }}>
              {rows.slice(0, 6).map((r, i) => {
                const share = total ? Math.round((r.amount / total) * 100) : 0;
                return (
                  <div key={r.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                      <span style={{ fontSize: T.secondary, color: M.ink2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
                      <span style={{ fontSize: T.secondary, fontWeight: 800, color: M.black, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        AED {r.amount.toLocaleString('en-AE')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 6 }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 99, background: M.track, overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.max(2, share)}%`, height: '100%', borderRadius: 99,
                          // The biggest line gets the solid gold; the rest step
                          // back, so the ranking reads without counting bars.
                          background: i === 0 ? M.gold : 'rgba(214,177,96,0.45)',
                        }} />
                      </div>
                      <span style={{ fontSize: T.micro, fontWeight: 800, color: M.ink3, width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{share}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

        return (
          <div className="pm-two-col" style={{ gap: 16, marginBottom: 18 }}>
            <Bar rows={categories} heading={t('pmSpendByCategory')} />
            <Bar
              rows={properties}
              heading={t('pmSpendByProperty')}
              note={properties.length > 1 ? `${properties[0].label} — ${t('pmCostliest')}` : undefined}
            />
          </div>
        );
      })()}

      {!hasAny && <EmptyState icon={Receipt} title={t('pmNoExpenses')} text={t('pmNoExpensesText')} />}
      <div className="pm-groups">
      {groups.map((g) => g.items.length > 0 && (
        <div key={g.unit.id}>
          <GroupHeading
            label={unitLabel(g.unit)}
            count={g.items.length}
            action={(
              <button
                onClick={() => setAdding(adding === g.unit.id ? null : g.unit.id)}
                title={t('pmAddExpense')}
                aria-label={`${t('pmAddExpense')} — ${unitLabel(g.unit)}`}
                className="pt-tap"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${M.tintEdge}`, background: M.tint, color: M.goldInk, borderRadius: 99, padding: '5px 12px 5px 9px', fontSize: T.micro, fontWeight: 800, cursor: 'pointer' }}
              >
                <PlusCircle size={14} /> {t('pmAddExpense')}
              </button>
            )}
          />
          <div className="pt-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {g.items.map((e) => <ExpenseRow key={e.id} e={e} />)}
            </div>
            {adding === g.unit.id && (
              <AddExpenseForm
                unitId={g.unit.id}
                demoMode={demoMode}
                onAdd={(row) => setAdded((prev) => ({ ...prev, [g.unit.id]: [...(prev[g.unit.id] || []), row] }))}
                onClose={() => setAdding(null)}
              />
            )}
          </div>
        </div>
      ))}
      </div>
    </div>
  );
};

// ---------------- Tenants tab (everyone, across the portfolio) ----------------
/**
 * Every tenancy in one list, with the state of its rent.
 *
 * The portfolio held this already, one property at a time — so "who owes me
 * money?" meant opening eight properties and adding it up yourself. The unit
 * of thought here is the tenancy, not the building: name, where they live,
 * what they pay, and whether a cheque has gone past its date unbanked.
 */
const TenantsTab: React.FC<{ units: any[]; onSelect: (id: string) => void }> = ({ units, onSelect }) => {
  const { t } = useLanguage();

  const rows = units
    .filter((u) => u.currentTenant && u.currentTenant.name)
    .map((u) => {
      const tn = u.currentTenant;
      const schedule = (u.rentSchedule || []) as any[];
      const annual = Number(tn.annualRent) || schedule.reduce((a, r) => a + (r.amount || 0), 0);
      const collected = schedule.filter(chequeCollected).reduce((a, r) => a + (r.amount || 0), 0);
      const now = Date.now();
      // Overdue is a cheque whose date has passed that nobody has banked and
      // that is not sitting with the owner for approval — that last state is
      // the tenant's cheque in our hands, not a debt.
      const overdue = schedule.filter((r) => {
        if (chequeCollected(r) || r.status === 'awaiting_approval') return false;
        const d = r.dueDate ? Date.parse(r.dueDate) : NaN;
        return !isNaN(d) && d < now;
      });
      const overdueAmount = overdue.reduce((a, r) => a + (r.amount || 0), 0);
      const daysLate = overdue.length
        ? Math.max(...overdue.map((r) => Math.floor((now - Date.parse(r.dueDate)) / 86400000)))
        : 0;
      const next = schedule.find((r) => !chequeCollected(r));
      return { u, tn, annual, collected, overdueAmount, daysLate, next };
    })
    // Anything owed first — that is the reason to open this screen.
    .sort((a, b) => b.overdueAmount - a.overdueAmount);

  if (rows.length === 0) {
    return <EmptyState icon={Users} title={t('pmNoTenants')} text={t('pmNoTenantsText')} />;
  }

  const owed = rows.reduce((a, r) => a + r.overdueAmount, 0);

  return (
    <div className="pt-fade" style={{ padding: '20px 16px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 2px 16px', gap: 12 }}>
        <span style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{t('pmTenants')} · {rows.length}</span>
        <span style={{
          fontSize: T.micro, fontWeight: 800, borderRadius: 99, padding: '5px 12px',
          color: owed ? theme.danger : theme.success,
          background: owed ? theme.dangerBg : theme.successBg,
        }}>
          {owed ? `${t('pmOverdue')} AED ${owed.toLocaleString('en-AE')}` : t('pmAllRentOnTime')}
        </span>
      </div>

      <div className="pm-props">
        {rows.map(({ u, tn, annual, collected, overdueAmount, daysLate, next }) => {
          const pct = annual ? Math.round((collected / annual) * 100) : 0;
          return (
            <button
              key={u.id}
              onClick={() => onSelect(u.id)}
              className="pt-card pt-tap pm-prop-card"
              style={{ position: 'relative', overflow: 'hidden', display: 'block', width: '100%', border: 'none', textAlign: 'left', padding: '18px 18px 18px 22px', borderRadius: 20 }}
            >
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: overdueAmount ? theme.danger : M.gold }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 46, height: 46, borderRadius: 99, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'inherit', fontSize: T.body, fontWeight: 800, color: M.goldInk }}>
                  {String(tn.name).split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T.primary, fontWeight: 800, color: M.black, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tn.name}</div>
                  <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{unitLabel(u)}</div>
                </div>
                {overdueAmount > 0 ? (
                  <span style={{ flexShrink: 0, fontSize: T.micro, fontWeight: 800, borderRadius: 99, padding: '5px 11px', color: theme.danger, background: theme.dangerBg }}>
                    {t('pmOverdue')} · {daysLate} {t('pmDaysLower')}
                  </span>
                ) : (
                  <span style={{ flexShrink: 0, fontSize: T.micro, fontWeight: 800, borderRadius: 99, padding: '5px 11px', color: theme.success, background: theme.successBg }}>
                    {t('pmOnTime')}
                  </span>
                )}
              </div>

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${M.hair}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontSize: T.secondary, color: M.ink2 }}>
                    {t('pmRentCollected')} <strong style={{ color: M.black, fontWeight: 800 }}>AED {collected.toLocaleString('en-AE')}</strong>
                    <span style={{ color: M.ink3 }}> / {annual.toLocaleString('en-AE')}</span>
                  </span>
                  <span style={{ fontSize: T.secondary, fontWeight: 800, color: M.goldInk }}>{pct}%</span>
                </div>
                <div style={{ height: 5, borderRadius: 99, background: M.track, overflow: 'hidden', marginTop: 8 }}>
                  <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: overdueAmount ? theme.danger : M.gold }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10, fontSize: T.micro, color: M.ink3 }}>
                  <span>{tn.contractEnd ? `${t('pmLeaseEnds')} ${fmtTs(tn.contractEnd)}` : ''}</span>
                  {next && <span>{t('pmNextCheque')} {fmtTs(next.dueDate)} · AED {Number(next.amount).toLocaleString('en-AE')}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Shared heading above each per-property group in Documents / Expenses.
const GroupHeading: React.FC<{ label: string; count: number; action?: React.ReactNode }> = ({ label, count, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 2px 10px' }}>
    <Building2 size={16} color={M.gold} style={{ flexShrink: 0 }} />
    <span style={{ minWidth: 0, fontSize: T.secondary, fontWeight: 800, color: M.black, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: T.micro - 1, fontWeight: 800, color: M.ink2, background: M.track, padding: '3px 9px', borderRadius: 99 }}>{count}</span>
    {action && <span style={{ flexShrink: 0 }}>{action}</span>}
  </div>
);

const ExpenseRow: React.FC<{ e: Expense }> = ({ e }) => {
  const { t } = useLanguage();
  return (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Receipt size={21} color={M.goldInk} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: T.body, fontWeight: 700, color: M.black }}>{e.description}</div>
      <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 2 }}>{trData(e.type)}{e.date ? ` · ${e.date}` : ''}</div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: T.body, fontWeight: 800, color: M.black }}>AED {e.amount.toLocaleString('en-AE')}</div>
      <span style={{ display: 'inline-block', marginTop: 4, fontSize: T.micro - 1, fontWeight: 800, borderRadius: 99, padding: '3px 10px', color: e.status === 'paid' ? theme.success : theme.warning, background: e.status === 'paid' ? theme.successBg : theme.warningBg }}>
        {e.status === 'paid' ? t('paid') : t('pmDue')}
      </span>
    </div>
  </div>
  );
};

/**
 * Rent, expenses, net — the three lines under the headline.
 *
 * `strong` marks the net, and it is the ONLY figure on the screen allowed to
 * be gold. With the palette limited to three colours, reserving gold for the
 * amount the owner actually keeps is what makes it mean something; spreading
 * it across every figure is what made it mean nothing.
 */
const SumStat: React.FC<{ label: string; value: number; strong?: boolean; onClick?: () => void; hint?: string }> = ({ label, value, strong, onClick, hint }) => {
  const body = (
    <>
      <div style={{ ...eyebrow, fontSize: T.micro, color: 'rgba(255,255,255,0.66)', fontWeight: 500 }}>{label}</div>
      <div style={{ ...figure, fontSize: strong ? T.statValue + 8 : T.statValue + 4, marginTop: 3, color: strong ? M.gold : '#fff' }}>
        {value >= 1000 ? `${Math.round(value / 1000)}k` : value.toLocaleString('en-AE')}
      </div>
      {onClick && hint && <div style={{ fontSize: T.micro - 1, color: M.gold, marginTop: 3, fontWeight: 600 }}>{hint} ›</div>}
    </>
  );
  // Tappable when there is something behind the figure (the expense breakdown).
  return onClick
    ? <button type="button" onClick={onClick} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>{body}</button>
    : <div>{body}</div>;
};

/**
 * One unit's costs, with its own add form.
 *
 * Reuses ExpenseRow and AddExpenseForm from the portfolio Expenses screen, so
 * a cost booked here and a cost booked there are the same record written the
 * same way — and the running total the Overview shows keeps agreeing with the
 * rows behind it.
 */
const UnitExpensesTab: React.FC<{
  unitId: string; expenses: Expense[]; demoMode?: boolean; onAdded: (row: Expense) => void; facts?: UnitFacts | any;
}> = ({ unitId, expenses, demoMode, onAdded, facts }) => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  const [adding, setAdding] = useState(false);
  // Two books (owner, 2026-11-05): RENTAL = running costs, subtracted from the
  // rent; PURCHASE = one-off costs of buying, added to the investment the net
  // yield divides by. Kept apart so a DLD fee can never reduce rental profit.
  const [book, setBook] = useState<'rental' | 'purchase'>('rental');
  const total = expenses.reduce((a, e) => a + e.amount, 0);
  const paid = expenses.filter((e) => e.status === 'paid').reduce((a, e) => a + e.amount, 0);
  const sc = facts?.serviceCharge;
  // The yearly service charge, shown as its own row unless the invoice is
  // already one of the expense rows (then it is in the list, not twice).
  const acq = facts?.acquisition;
  // Listed accounts only, like the Purchase book (acq is null for everyone else).
  const scRow = acq && sc && Number(sc.annual) > 0 && !sc.bookedAsExpense ? Number(sc.annual) : 0;
  const aedFmt = (n: number) => `AED ${Math.round(n || 0).toLocaleString('en-AE')}`;
  const sourceLabel = (s: string) => (s === 'auto'
    ? (de ? 'Berechnet nach DLD-Regel – durch Beleg ersetzen' : 'Calculated by DLD rule – replace with the receipt')
    : s === 'sheet' ? (de ? 'Aus der Transfer-Kostenaufstellung' : 'From the transfer cost sheet')
      : (de ? 'Erfasst' : 'Recorded'));

  return (
    <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
      {/* The Purchase book exists only where the backend sends facts.acquisition
          (listed accounts, pmFacts.ACQUISITION_ACCOUNTS); everyone else sees
          the expense list exactly as before. */}
      {acq && <div style={{ marginBottom: 14 }}>
        <Segmented
          tabs={[
            { key: 'rental', label: 'pmExpRental', icon: Receipt },
            { key: 'purchase', label: 'pmExpPurchase', icon: Briefcase },
          ]}
          value={book}
          onChange={(k: string) => { setBook(k as any); setAdding(false); }}
        />
      </div>}

      {book === 'rental' || !acq ? (
        <>
          <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
            <div style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{t('pmTotalExpensesYear')}</div>
            <div style={{ fontSize: T.money, fontWeight: 800, color: M.black, marginTop: 5, letterSpacing: '-0.5px' }}>
              <span style={{ fontSize: T.money - 10, color: M.gold, fontWeight: 800 }}>AED </span>
              {(total + scRow).toLocaleString('en-AE')}
            </div>
            {scRow > 0 && (
              <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 4 }}>
                {de ? `inkl. Servicegebühr ${aedFmt(scRow)} pro Jahr` : `incl. service charge ${aedFmt(scRow)} a year`}
              </div>
            )}
            {total > 0 && (
              <div style={{ display: 'flex', gap: 22, marginTop: 14 }}>
                <SumStat label={t('pmPaid')} value={paid} />
                <SumStat label={t('pmOutstanding')} value={total - paid} strong />
              </div>
            )}
          </div>

          <div className="pt-card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: (expenses.length || scRow) ? 16 : 0 }}>
              <span style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{t('expenses')} · {expenses.length + (scRow ? 1 : 0)}</span>
              <button
                onClick={() => setAdding(!adding)}
                className="pt-tap"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${M.tintEdge}`, background: M.tint, color: M.goldInk, borderRadius: 99, padding: '5px 12px 5px 9px', fontSize: T.micro, fontWeight: 800, cursor: 'pointer' }}
              >
                <PlusCircle size={14} /> {t('pmAddExpense')}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {scRow > 0 && (
                <CostRow
                  title={de ? 'Servicegebühr' : 'Service charge'}
                  sub={`${de ? 'Pro Jahr' : 'Per year'}${sc.psf ? ` · AED ${sc.psf}/sqft${sc.sqft ? ` × ${Math.round(sc.sqft).toLocaleString('en-AE')} sqft` : ''}` : ''}${sc.payableYet === false ? (de ? ' · fällig ab Übergabe' : ' · payable from handover') : ''}`}
                  amount={scRow}
                />
              )}
              {expenses.map((e) => <ExpenseRow key={e.id} e={e} />)}
            </div>
            {expenses.length === 0 && !scRow && !adding && (
              <div style={{ fontSize: T.secondary, color: M.ink2, lineHeight: 1.5 }}>{t('pmNoExpensesYet')}</div>
            )}
            {adding && (
              <AddExpenseForm unitId={unitId} demoMode={demoMode} onAdd={onAdded} onClose={() => setAdding(false)} />
            )}
          </div>
        </>
      ) : (
        <PurchaseCostsBook unitId={unitId} demoMode={demoMode} acq={acq} sourceLabel={sourceLabel} />
      )}
    </div>
  );
};

/** One computed cost line (service charge, purchase price, a purchase cost). */
const CostRow: React.FC<{ title: string; sub?: string; amount: number; strong?: boolean; onRemove?: () => void }> = ({ title, sub, amount, strong, onRemove }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Receipt size={21} color={M.goldInk} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: T.body, fontWeight: 700, color: M.black }}>{title}</div>
      {sub && <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 2 }}>{sub}</div>}
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: T.body, fontWeight: strong ? 900 : 800, color: M.black }}>AED {Math.round(amount).toLocaleString('en-AE')}</div>
      {onRemove && (
        <button type="button" onClick={onRemove} style={{ all: 'unset', cursor: 'pointer', fontSize: T.micro - 1, color: M.ink3, fontWeight: 700, marginTop: 4 }}>✕</button>
      )}
    </div>
  </div>
);

/**
 * The PURCHASE book: price + one-off costs = total investment, the figure the
 * net yield divides by (facts.acquisition from the backend, so this screen and
 * every yield agree). Rows the team entered can be removed; a row calculated
 * by DLD rule disappears once a receipt of the same type is entered.
 */
const PurchaseCostsBook: React.FC<{ unitId: string; demoMode?: boolean; acq: any; sourceLabel: (s: string) => string }> = ({ unitId, demoMode, acq, sourceLabel }) => {
  const { t, language } = useLanguage();
  const de = language === 'de';
  const [rows, setRows] = useState<any[]>(acq?.rows || []);
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('Agency commission');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(acq?.rows || []); }, [acq]);
  const price = Number(acq?.price) || 0;
  const costs = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const TYPES = ['DLD fee', 'Trustee fee', 'Agency commission', 'Other purchase cost'];
  const typeLabel = (x: string) => (de ? ({ 'DLD fee': 'DLD-Gebühr', 'Trustee fee': 'Treuhändergebühr', 'Agency commission': 'Maklerprovision', 'Other purchase cost': 'Sonstige Kaufnebenkosten', 'Purchase costs': 'Kaufnebenkosten' } as any)[x] || x : x);
  const field: React.CSSProperties = {
    width: '100%', padding: '11px 12px', borderRadius: 10, border: `1px solid ${M.hair}`,
    background: '#fff', color: M.black, fontSize: T.secondary, fontFamily: 'inherit', fontWeight: 600,
  };

  const add = async () => {
    const value = Number(amount) || 0;
    if (!(value > 0) || saving) return;
    setSaving(true);
    try {
      let row: any = { id: `acq-${Date.now().toString(36)}`, type, amount: value, description: note.trim(), date, source: 'recorded' };
      if (!demoMode) {
        const r: any = await portalFetch(`/pm/unit/${unitId}/acquisition-cost`, { method: 'POST', body: JSON.stringify({ type, amount: value, description: note.trim(), date }) }, 15000);
        if (r && r.row) row = { ...r.row, source: 'recorded' };
      }
      // A receipt replaces the DLD-rule figure of the same type, and any
      // recorded row replaces the one-line transfer-sheet total.
      setRows((prev) => [...prev.filter((x) => !(x.source === 'auto' && x.type === type) && x.source !== 'sheet'), row]);
      setAdding(false); setAmount(''); setNote('');
    } catch {
      alert(t('pmExpenseSaveError'));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm(de ? 'Diese Position löschen?' : 'Remove this cost?')) return;
    try {
      if (!demoMode) await portalFetch(`/pm/unit/${unitId}/acquisition-cost/${encodeURIComponent(id)}`, { method: 'DELETE' }, 15000);
      setRows((prev) => prev.filter((x) => x.id !== id));
    } catch {
      alert(t('pmExpenseSaveError'));
    }
  };

  return (
    <>
      <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
        <div style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{de ? 'Gesamtinvestition' : 'Total investment'}</div>
        <div style={{ fontSize: T.money, fontWeight: 800, color: M.black, marginTop: 5, letterSpacing: '-0.5px' }}>
          <span style={{ fontSize: T.money - 10, color: M.gold, fontWeight: 800 }}>AED </span>
          {Math.round(price + costs).toLocaleString('en-AE')}
        </div>
        <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 4, lineHeight: 1.5 }}>
          {de
            ? `Kaufpreis ${Math.round(price).toLocaleString('en-AE')} + Kaufnebenkosten ${Math.round(costs).toLocaleString('en-AE')} (ohne MwSt.). Die Nettorendite wird auf diesen Betrag berechnet.`
            : `Price ${Math.round(price).toLocaleString('en-AE')} + purchase costs ${Math.round(costs).toLocaleString('en-AE')} (excl. VAT). The net yield is calculated on this amount.`}
        </div>
      </div>

      <div className="pt-card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <span style={{ ...eyebrow, fontSize: T.micro, color: M.ink3, fontWeight: 800 }}>{de ? 'Kauf' : 'Purchase'} · {rows.length + 1}</span>
          <button
            onClick={() => setAdding(!adding)}
            className="pt-tap"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${M.tintEdge}`, background: M.tint, color: M.goldInk, borderRadius: 99, padding: '5px 12px 5px 9px', fontSize: T.micro, fontWeight: 800, cursor: 'pointer' }}
          >
            <PlusCircle size={14} /> {de ? 'Kaufkosten hinzufügen' : 'Add purchase cost'}
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <CostRow title={de ? 'Kaufpreis' : 'Purchase price'} amount={price} />
          {rows.map((r) => (
            <CostRow key={r.id} title={typeLabel(r.type)}
              sub={[r.description, sourceLabel(r.source), r.date].filter(Boolean).join(' · ')}
              amount={Number(r.amount) || 0}
              onRemove={r.source === 'recorded' ? () => remove(r.id) : undefined} />
          ))}
        </div>
        {adding && (
          <div style={{ borderTop: `1px solid ${M.hair}`, marginTop: 14, paddingTop: 14, display: 'grid', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10 }}>
              <select value={type} onChange={(e) => setType(e.target.value)} style={field}>
                {TYPES.map((x) => <option key={x} value={x}>{typeLabel(x)}</option>)}
              </select>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" placeholder="AED" style={{ ...field, textAlign: 'right' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 10 }}>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={de ? 'Notiz (optional)' : 'Note (optional)'} style={field} />
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={field} />
            </div>
            <div style={{ fontSize: T.micro, color: M.ink2 }}>{de ? 'Ohne MwSt. eintragen.' : 'Enter excl. VAT.'}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setAdding(false)} className="pt-tap" style={{ border: `1px solid ${M.hair}`, background: '#fff', color: M.ink2, borderRadius: 10, padding: '10px 16px', fontSize: T.secondary, fontWeight: 700, cursor: 'pointer' }}>{t('cancel')}</button>
              <button onClick={add} disabled={!(Number(amount) > 0) || saving} className="pt-tap"
                style={{ border: 'none', background: Number(amount) > 0 ? M.black : M.track, color: Number(amount) > 0 ? '#fff' : M.ink3, borderRadius: 10, padding: '10px 18px', fontSize: T.secondary, fontWeight: 800, cursor: Number(amount) > 0 ? 'pointer' : 'default' }}>
                {saving ? t('loading') : (de ? 'Hinzufügen' : 'Add')}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ---------------- Unit detail (with sub-tabs) ----------------
export const UnitDetail: React.FC<{
  unitId: string; reloadKey?: number; demoMode?: boolean; onBack?: () => void;
  ownerName?: string; onAsk?: (m: string) => void; investor?: any;
  onSmartUpload?: (e: React.ChangeEvent<HTMLInputElement>, unitId?: string) => void; smartUploading?: boolean;
  /**
   * This unit as the investor payload already knows it, and its documents.
   *
   * PM enrichment (tenancy, inspections, maintenance, media) is an upgrade,
   * not a prerequisite. /pm/unit/:id is reached through PORTAL_API, which in
   * development is the dev API on :4002 while the portfolio itself comes from
   * the live backend — so a developer with real properties on screen would
   * open one and be told it could not be loaded. Same in production whenever
   * PM cannot answer. The property is right there; show it, and let the
   * management sections fill in when the call succeeds.
   */
  fallbackUnit?: any;
  fallbackDocuments?: any[];
  /**
   * Which sub-tab to open on. Defaults to the property's front page; the
   * portfolio maintenance board passes 'maintenance' so a tap on a request
   * arrives at the request instead of leaving the owner to find it again.
   */
  initialSub?: string;
}> = ({ unitId, reloadKey, demoMode, onBack, ownerName, onAsk, investor, onSmartUpload, smartUploading, fallbackUnit, fallbackDocuments, initialSub }) => {
  const { t } = useLanguage();

  // On the sample portfolio this screen used to fall back to MOCK_VM — a
  // different unit entirely, with an empty `details` / `rentalIndex`, which is
  // why a rented property showed no DEWA premises number, Ejari number, Makani
  // number, contract dates or annual rent. Build the same view-model the live
  // screen builds, from the sample unit.
  const demoUnit = demoMode ? DEMO_PM_UNITS.find((u: any) => u.id === unitId) : undefined;
  const demoVm = React.useMemo(
    () => (demoUnit
      ? buildVM(demoUnit, undefined, demoUnit.currentTenant, demoUnit.documents, t, demoUnit.maintenance, [])
      : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [demoUnit],
  );

  // MOCK_VM is a DIFFERENT unit — another project, another tenant. Falling
  // back to it in the showcase meant opening a property with no sample
  // management record showed someone else's tenancy as if it were this one.
  // The sample set covers only some units, so say so instead.
  const demoUncovered = !!demoMode && !demoUnit;
  // null until this unit's own record is in hand. `vm` reads through a blank
  // placeholder so the render below (and its hooks) stay simple, but nothing
  // is shown while vmState is null — see the guard after the hooks.
  const [vmState, setVmState] = useState<UnitVM | null>(demoVm || null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Built from what the shell already holds. Real data about the right unit —
  // just without the management record on top.
  const fallbackVm = React.useMemo(
    () => (fallbackUnit
      ? buildVM(fallbackUnit, undefined, fallbackUnit.currentTenant, fallbackDocuments || fallbackUnit.documents || [], t, fallbackUnit.maintenance, [])
      : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fallbackUnit, fallbackDocuments],
  );
  const resolved = vmState || fallbackVm || null;
  const vm = resolved || EMPTY_VM;
  // Read once, as the initial value only: re-syncing it to the prop would
  // yank the owner back to Maintenance every time this screen re-rendered
  // after they had moved to another sub-tab.
  const [sub, setSub] = useState<string>(initialSub || 'overview');
  // An Airbnb apartment gets its own sub-tab, right after Overview. Every
  // other unit keeps exactly the tabs it had.
  const airbnb = useAirbnbIncome([unitId], demoMode);
  const airbnbUnit = airbnb.units.find((u) => u.unitId === unitId) || null;
  const unitTabs = React.useMemo(
    () => (airbnbUnit ? [UNIT_TABS[0], { key: 'airbnb', label: 'pmAirbnbTab', icon: BedDouble }, ...UNIT_TABS.slice(1)] : UNIT_TABS),
    [airbnbUnit],
  );
  // Rows added from the Expenses tab, held until the next reload brings them
  // back from the server.
  const [addedExpenses, setAddedExpenses] = useState<Expense[]>([]);


  // One place that refetches this unit, shared by the payment approval flow
  // and the photo upload so both show the server's version of the truth.
  const reload = useCallback(async () => {
    if (demoMode) return;
    try {
      const { unit, owner, currentTenant, documents, maintenance, media } = await portalFetch<any>(`/pm/unit/${unitId}`);
      if (unit) { setVmState(buildVM(unit, owner, currentTenant, documents, t, maintenance, media)); setLoadFailed(false); }
    } catch {
      // Keep whatever real record is already on screen; if there is none, say
      // the unit could not be loaded rather than inventing one.
      setLoadFailed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId, demoMode]);

  useEffect(() => { if (demoVm) setVmState(demoVm); }, [demoVm]);
  useEffect(() => { reload(); }, [reload, reloadKey]);

  const [uploading, setUploading] = useState(false);
  const uploadMedia = async (file: File) => {
    setUploading(true);
    try {
      const dataUrl = await fileToUploadableDataUrl(file);
      await portalFetch(`/pm/unit/${unitId}/media`, {
        method: 'POST',
        body: JSON.stringify({
          type: file.type.startsWith('video') ? 'video' : 'photo',
          name: file.name || 'photo',
          fileType: file.type.startsWith('video') ? file.type : 'image/jpeg',
          fileData: dataUrl,
        }),
      }, 45000);
      await reload();
    } catch {
      alert(t('pmPhotoUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const steps = vm.steps;
  const doneCount = steps.filter((s) => s.status === 'done').length;
  const activeIndex = steps.findIndex((s) => s.status === 'active');
  const progressPct = Math.round(((doneCount + (activeIndex >= 0 ? 0.5 : 0)) / steps.length) * 100);
  const totalExpenses = vm.expenses.reduce((s, e) => s + e.amount, 0);
  // Net income after ALL costs. The service charge is the one running cost
  // that is almost never booked as an expense (the invoice goes to the owner,
  // not to us), so the backend resolves it from the building's published rate
  // and reports it separately — and reports zero when the invoice IS already
  // among the expenses, so it can never be deducted twice.
  const facts = factsOf(vm.unit);
  const scAnnual = facts.netIncome ? facts.netIncome.serviceCharge : 0;
  const scKnown = facts.netIncome ? facts.netIncome.serviceChargeKnown : false;
  const net = facts.netIncome ? facts.netIncome.net : vm.annualRent - totalExpenses;

  const awaiting = vm.payments.filter((p) => p.status === 'awaiting_approval');
  const np = vm.nextPayment;
  // With no payment outstanding the card shows the last one that came in
  // instead of re-labelling a paid cheque as the next one.
  const settledPayments = vm.payments.filter((p) => p.status === 'approved' || p.status === 'paid');
  const lastSettled = settledPayments[settledPayments.length - 1];

  // Per-property summary KPIs (reference-style tiles).
  const rentCollected = vm.payments.filter(chequeCollected).reduce((s, p) => s + p.amount, 0);
  const rentDue = vm.payments.filter((p) => p.status !== 'approved' && p.status !== 'paid').reduce((s, p) => s + p.amount, 0);
  const openReq = SAMPLE_MAINTENANCE_STATUS.filter((s) => s === 'in_progress').length;
  const kk = (n: number) => `${Math.round(n / 1000)}k`;
  const animatedNet = useCountUp(net);

  // AFTER every hook. This early return used to sit above useCountUp, which
  // made the hook order depend on whether the showcase had a record for this
  // unit — the rules-of-hooks violation that fails the build.
  // Nothing real to show yet. Never the blank placeholder, and never MOCK_VM.
  if (!resolved && !demoUncovered) {
    return (
      <div className="pt-fade pm-premium" style={{ padding: '18px 16px' }}>
        {onBack && (
          <button
            onClick={onBack}
            className="pt-tap"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '4px 0 16px', color: M.goldInk, fontSize: T.secondary, fontWeight: 700, cursor: 'pointer' }}
          >
            <ChevronLeft size={17} /> {t('backToProperties')}
          </button>
        )}
        {loadFailed ? (
          <div style={{ textAlign: 'center', padding: '28px 16px' }}>
            <div style={{ fontSize: T.cardTitle, color: M.black, marginBottom: 6, fontWeight: 700 }}>{t('pmLoadFailedTitle')}</div>
            <div style={{ fontSize: T.secondary, color: M.ink2, marginBottom: 18, lineHeight: 1.5 }}>{t('pmLoadFailedBody')}</div>
            <button
              onClick={() => { setLoadFailed(false); reload(); }}
              className="pt-tap"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: M.gold, border: 'none', borderRadius: 99, padding: '9px 20px', color: '#fff', fontSize: T.secondary, fontWeight: 800, cursor: 'pointer' }}
            >
              <RefreshCw size={15} /> {t('pmRefresh')}
            </button>
          </div>
        ) : (
          <>
            <div className="pt-skel" style={{ height: 150, marginBottom: 16 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              {[0, 1, 2, 3].map((i) => <div key={i} className="pt-skel" style={{ height: 104 }} />)}
            </div>
            <div className="pt-skel" style={{ height: 200 }} />
          </>
        )}
      </div>
    );
  }

  if (demoUncovered) {
    return (
      <div className="pt-fade pm-premium" style={{ padding: '18px 16px' }}>
        {onBack && (
          <button
            onClick={onBack}
            className="pt-tap"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '4px 0 16px', color: M.goldInk, fontSize: T.secondary, fontWeight: 700, cursor: 'pointer' }}
          >
            <ChevronLeft size={17} /> {t('backToProperties')}
          </button>
        )}
        <EmptyState icon={ClipboardCheck} title={t('pmNoSampleUnit')} text={t('pmNoSampleUnitText')} />
      </div>
    );
  }

  return (
    <div className="pt-fade pm-premium" style={{ paddingBottom: 24 }}>
      {/* Unit hero — full-bleed image with a premium dark overlay */}
      <div style={{ position: 'relative' }}>
        <div className="pm-hero" style={{ height: 250, background: `#000 url(${vm.image}) center/cover no-repeat` }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.20) 34%, rgba(0,0,0,0.72) 72%, rgba(0,0,0,0.94) 100%)' }} />
        {onBack && (
          <button onClick={onBack} className="pt-tap" style={{ position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.92)', border: 'none', color: M.black, fontSize: T.secondary, fontWeight: 800, cursor: 'pointer', padding: '9px 14px 9px 10px', borderRadius: 99, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
            <ChevronLeft size={18} /> {t('allProperties')}
          </button>
        )}
        <div style={{ position: 'absolute', top: 14, right: 14 }}><StatusPill status={vm.status} unit={vm.unit} /></div>
        <div style={{ position: 'absolute', left: 16, right: 16, bottom: 16, color: '#fff' }}>
          {/* Selectable AND copyable: this is the name owners quote to banks,
              Ejari and their accountants. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <h2 className="pm-hero-title" style={{ fontSize: 25, fontWeight: 800, margin: 0, letterSpacing: '-0.3px', userSelect: 'text', WebkitUserSelect: 'text' }}>{vm.name}</h2>
            <CopyNameButton name={vm.name} onDark />
          </div>
          {vm.photoKind === 'project' && (
            <div style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.72)', marginTop: 5 }}>{t('pmProjectPhoto')}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: T.body, color: 'rgba(255,255,255,0.88)', marginTop: 5 }}>
            <MapPin size={16} /> {vm.community}
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: T.secondary, color: 'rgba(255,255,255,0.9)', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Bed size={17} /> {vm.propertyType}</span>
            {vm.size && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Maximize2 size={17} /> {vm.size}</span>}
            {vm.price && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Tag size={17} /> {vm.price}</span>}
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <Segmented tabs={unitTabs} value={sub === 'airbnb' && !airbnbUnit ? 'overview' : sub} onChange={setSub} />

      {/* 'airbnb' before the Airbnb figures have arrived reads as Overview. */}
      {(sub === 'overview' || (sub === 'airbnb' && !airbnbUnit)) && (
        <>
          {/* Where the building itself is: booking, construction, completion,
              and the DLD project number the dates come from. Renders nothing
              for a completed unit that carries no construction record. */}
          <div style={{ padding: '16px 16px 0' }}>
            {/* The PM payload states construction under `facts.construction`
                (percent, handover) while the Asset Desk unit carries the flat
                DLD columns the timeline was written against. Fill the flat
                fields from facts when they are missing so one component reads
                both shapes rather than rendering nothing on a PM unit. */}
            <ProjectTimeline
              unit={vm.unit && {
                ...vm.unit,
                constructionCompletionPercent:
                  vm.unit.constructionCompletionPercent ?? facts.construction?.percent,
                completionDateResolved:
                  vm.unit.completionDateResolved ?? facts.construction?.handover,
                occupancyStatus:
                  vm.unit.occupancyStatus ?? (facts.construction?.offplan ? 'Offplan' : undefined),
              }}
            />
          </div>

          {/* Summary KPI tiles */}
          <Section title={t('summary')}>
            <div className="pm-kpis">
              <KpiTile icon={Wallet} label={t('pmRentCollected')} value={`AED ${kk(rentCollected)}`} />
              <KpiTile icon={Clock} label={t('pmRentDue')} value={`AED ${kk(rentDue)}`} />
              <KpiTile icon={Users} label={t('pmOccupancy')} value={vm.status === 'Rented' ? '100%' : '0%'} />
              <KpiTile icon={Wrench} label={t('pmOpenRequests')} value={openReq} />
            </div>
          </Section>

          {/* A unit still being built has no tenant, no rent and no yield —
              what it has is a completion percentage and a handover date, and
              that is the headline for this property until the keys arrive. */}
          {isUnderConstruction(vm.unit) && (
            <Section title={t('pmConstruction')}>
              <ConstructionCard facts={facts} />
            </Section>
          )}

          <div className="pm-two-col pm-sections">
          {/* A unit with no rent recorded is the one worth offering to manage.
              Framed as an offer, not a claim: the payload carries no
              "managed by" flag, so we cannot assert who manages it. */}
          {onAsk && vm.annualRent <= 0 && (
            <Section title={t('pmOfferTitle')}>
              <div style={{ borderRadius: 14, padding: 24, backgroundColor: '#000', backgroundImage: M.blackGrad, color: '#fff', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: M.gold }} />
                <p style={{ fontSize: T.body, lineHeight: 1.6, margin: 0, color: 'rgba(255,255,255,0.9)', maxWidth: 560 }}>{t('pmOfferBody')}</p>
                <button onClick={() => onAsk(`${t('pmChatManage')} ${vm.name}`)} className="pt-tap"
                  style={{ marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 8, background: M.gold, color: '#000', border: 'none', borderRadius: 99, padding: '13px 22px', fontSize: T.primary, fontWeight: 800, cursor: 'pointer' }}>
                  {t('pmOfferCta')} <ArrowUpRight size={18} />
                </button>
              </div>
            </Section>
          )}

          {/* Current tenant + Emirates ID */}
          {vm.tenant && (
            <Section title={t('pmCurrentTenant')} subtitle={t('pmMaintainedBy')}>
              <div className="pt-card" style={{ padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 99, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Users size={26} color={M.goldInk} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: T.primary, fontWeight: 800, color: M.black }}>{vm.tenant.name}</div>
                    {vm.tenant.contractEnd && <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 2 }}>{t('pmLeaseUntil')} {vm.tenant.contractEnd}</div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 14 }}>
                  {/* All three always render. They used to be conditional, so a
                      tenant with no email or phone on file showed a card with
                      nothing but an Emirates ID and no hint that the contact
                      details were simply missing rather than not collected.
                      An em-dash says "not on file" without inventing one. */}
                  <InfoRow icon={CreditCard} label={t('emiratesId')} value={vm.tenant.emiratesId || '—'} copy={!!vm.tenant.emiratesId} />
                  <InfoRow
                    icon={Mail}
                    label={t('email')}
                    value={vm.tenant.email || '—'}
                    href={vm.tenant.email ? `mailto:${vm.tenant.email}` : undefined}
                    copy={!!vm.tenant.email}
                  />
                  <InfoRow
                    icon={Phone}
                    label={t('pmPhone')}
                    value={vm.tenant.phone || '—'}
                    href={vm.tenant.phone ? `tel:${vm.tenant.phone.replace(/\s/g, '')}` : undefined}
                    copy={!!vm.tenant.phone}
                  />
                </div>
              </div>
            </Section>
          )}

          {/* Rental income — black payout card */}
          <Section title={t('rentalIncome')}>
            <div style={{ borderRadius: 14, padding: 24, backgroundColor: '#000', backgroundImage: M.blackGrad, color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: T.primary, fontWeight: 700 }}>{np || !lastSettled ? t('pmNextPayment') : t('pmRentCollectedFull')}</span>
                <Clock size={18} color="rgba(255,255,255,0.9)" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12 }}>
                <div>
                  <div style={{ fontSize: T.money, fontWeight: 800, letterSpacing: '-0.5px' }}>AED {((np || lastSettled)?.amount || 0).toLocaleString('en-AE')}</div>
                  <div style={{ fontSize: T.secondary, color: 'rgba(255,255,255,0.9)', marginTop: 3 }}>{(np || lastSettled)?.method}</div>
                </div>
                <div style={{ textAlign: 'right', fontSize: T.secondary, color: 'rgba(255,255,255,0.9)' }}>
                  <div>{np || !lastSettled ? t('dueDate') : t('pmLastPayment')}</div>
                  <div style={{ fontSize: T.primary, fontWeight: 700, color: '#fff', marginTop: 2 }}>{(np || lastSettled)?.dueDate || '—'}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20 }}>
                {vm.payments.map((p, i) => {
                  const paid = p.status === 'approved' || p.status === 'paid';
                  return (
                    <React.Fragment key={p.id}>
                      <div style={{ width: 16, height: 16, borderRadius: 99, background: paid ? '#fff' : 'rgba(255,255,255,0.35)', border: '2px solid #fff', flexShrink: 0 }} />
                      {i < vm.payments.length - 1 && <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.35)', borderRadius: 99 }} />}
                    </React.Fragment>
                  );
                })}
              </div>
              {/* No approve button: cheques are recorded by the management team
                  in the admin portal, and this screen reports what they
                  recorded. Offering the owner an action that is not theirs to
                  take invites a double-entry nobody reconciles. */}
              {awaiting.length > 0 && np && (
                <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, fontSize: T.secondary, fontWeight: 700, color: 'rgba(255,255,255,0.97)' }}>
                  <Clock size={18} /> {t('pmWithTeam')}
                </div>
              )}
              {awaiting.length === 0 && (
                <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, fontSize: T.secondary, fontWeight: 700, color: 'rgba(255,255,255,0.97)' }}>
                  <CheckCircle2 size={19} /> {t('pmAllPaymentsConfirmed')}
                </div>
              )}
            </div>
          </Section>

          </div>

          <div className="pm-two-col pm-sections">
          {/* Financial overview — black card */}
          <Section title={t('pmFinancialOverview')}>
            <div style={{ borderRadius: 14, padding: 26, backgroundColor: '#000', backgroundImage: M.blackGrad, color: '#fff', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${M.gold}, ${M.gold})` }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <span style={{ ...eyebrow, fontSize: T.micro, fontWeight: 700, color: M.gold }}>
                  {scKnown ? t('pmNetAfterCosts') : t('pmNetIncomeYear')}
                </span>
                <span style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.75)', flexShrink: 0 }}>{t('pmEstimated')}</span>
              </div>
              <div style={{ fontSize: T.money, fontWeight: 800, marginTop: 8, letterSpacing: '-0.5px' }}>
                <span style={{ color: M.gold, fontWeight: 700 }}>AED</span> {animatedNet.toLocaleString('en-AE')}
              </div>
              <div style={{ borderTop: '1px solid rgba(214,177,96,0.22)', marginTop: 18, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.body, color: 'rgba(255,255,255,0.92)' }}><ArrowUpRight size={18} /> {t('rentalIncome')}</span>
                  <span style={{ fontSize: T.body, fontWeight: 700 }}>AED {vm.annualRent.toLocaleString('en-AE')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.body, color: 'rgba(255,255,255,0.92)' }}><ArrowDownRight size={18} /> {t('expenses')}</span>
                  <span style={{ fontSize: T.body, fontWeight: 700 }}>AED {totalExpenses.toLocaleString('en-AE')}</span>
                </div>
                {scAnnual > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.body, color: 'rgba(255,255,255,0.92)' }}>
                      <Receipt size={18} /> {t('pmServiceCharge')}
                      {facts.serviceCharge?.psf != null && (
                        <span style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.6)' }}>
                          {facts.serviceCharge.psf} {t('pmPerSqftYear')}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: T.body, fontWeight: 700 }}>AED {scAnnual.toLocaleString('en-AE')}</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: T.micro, color: 'rgba(255,255,255,0.68)', marginTop: 14, lineHeight: 1.5 }}>
                {facts.serviceCharge?.payableYet === false
                  ? t('pmServiceChargeNotYet')
                  : !scKnown
                    ? t('pmServiceChargeUnknown')
                    : facts.serviceCharge?.bookedAsExpense
                      ? t('pmServiceChargeBooked')
                      : `${t('pmServiceCharge')} · ${sourceLabel(facts.serviceCharge?.source, t)}`}
              </div>
            </div>
          </Section>

          {/* Ownership progress */}
          <Section title={t('pmOwnershipProgress')}>
            <div className="pt-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: T.body, fontWeight: 700, color: M.ink2 }}>{t('pmLifecycle')}</span>
                <span style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.goldInk }}>{progressPct}%</span>
              </div>
              <div className="pt-progress-track" style={{ marginBottom: 22 }}>
                <div className="pt-progress-fill" style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${M.goldDeep}, ${M.gold})` }} />
              </div>
              {steps.map((step, i) => <StepRow key={step.key} step={step} isLast={i === steps.length - 1} />)}
            </div>
          </Section>
          </div>

          {/* Photos & video — always shown, because "this property has no
              photos" is itself something the owner needs to see, together with
              the way to fix it. Uploads land in the unit's own media, which
              then outranks any project photo on the card and in reports. */}
          <Section title={t('pmPhotosVideo')}>
            <div className="pt-card" style={{ padding: 12 }}>
              {vm.media.length > 0 ? (
                <div className="pm-media-grid" style={{ display: 'grid', gap: 10 }}>
                  {vm.media.map((m: any) => (
                    <a key={m.id} href={m.src} target="_blank" rel="noreferrer" style={{ display: 'block', borderRadius: 12, overflow: 'hidden', textDecoration: 'none' }}>
                      {m.type === 'video'
                        ? <video src={m.src} style={{ width: '100%', height: 124, objectFit: 'cover', background: '#000', borderRadius: 12 }} muted playsInline />
                        : <div style={{ height: 124, borderRadius: 12, background: `rgba(0,0,0,0.06) url('${m.src}') center/cover` }} />}
                    </a>
                  ))}
                </div>
              ) : (
                /* No photos is a one-line fact, not a panel. It used to occupy a
                   full-width card with a heading, a paragraph and a button
                   stacked vertically — the largest block on the page for the
                   least information on it. Now it reads as a single row, with
                   the upload sitting on the same line wherever there is room. */
                <div style={{ padding: '8px 10px 4px' }}>
                  <div style={{ fontSize: T.body, color: M.ink2, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: M.black }}>{t('pmNoPhotosYet')}</span>{' '}{t('pmPhotoHint')}
                  </div>
                </div>
              )}
              {vm.source === 'live' && (
                <label className="pt-tap" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, margin: '14px 2px 4px',
                  background: M.tint, color: M.goldDeep, border: `1px solid ${M.tintEdge}`,
                  borderRadius: 99, padding: '11px 18px', fontSize: T.secondary, fontWeight: 800,
                  cursor: uploading ? 'default' : 'pointer',
                }}>
                  {uploading ? <RefreshCw size={17} className="pt-rotate" /> : <Upload size={17} />}
                  {uploading ? t('pmUploading') : t('pmAddPhoto')}
                  <input type="file" accept="image/*,video/*" disabled={uploading} style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) uploadMedia(f); }} />
                </label>
              )}
            </div>
          </Section>

          <div className="pm-two-col pm-sections">
          {/* Owner-declared facts — mortgage + letting mode. Editable only on
              live data: writing back a sample unit would 404. */}
          <Section title={t('pmPropertyFacts')} subtitle={t('pmPropertyFactsSub')}>
            <PropertyFactsCard
              unitId={vm.id}
              facts={facts}
              live={vm.source === 'live'}
              onSaved={(f) => setVmState((prev) => (prev ? { ...prev, unit: { ...(prev.unit || {}), facts: f } } : prev))}
            />
          </Section>

          {/* Property details.
              An owner opening a rented unit wants one thing first: what it
              earns and how long that is secured for. The registry identifiers
              — DEWA premises, Makani, plot, property number — matter only when
              filling somebody's form, so they sit behind a disclosure instead
              of competing with the rent for attention. Twelve equal rows is a
              data dump; this is a headline, a term, and a drawer. */}
          {(vm.rentalIndex.length > 0 || vm.details.length > 0) && (() => {
            const known = vm.rentalIndex.filter((d) => d.value);
            const missing = vm.rentalIndex.filter((d) => !d.value);
            const pick = (re: RegExp) => known.find((d) => re.test(d.label));

            const rent = pick(/Annual Rent|Jahresmiete/i);
            const start = pick(/Contract Start|Vertragsbeginn/i);
            const end = pick(/Contract End|Vertragsende/i);
            const ejari = pick(/Ejari/i);

            // Lease term progress, off the dates already on screen.
            const ms = (v?: string) => (v ? new Date(v).getTime() : NaN);
            const t0 = ms(start?.value), t1 = ms(end?.value), now = Date.now();
            const hasTerm = !isNaN(t0) && !isNaN(t1) && t1 > t0;
            const elapsed = hasTerm ? Math.min(1, Math.max(0, (now - t0) / (t1 - t0))) : 0;
            const daysLeft = hasTerm ? Math.max(0, Math.round((t1 - now) / 86400000)) : 0;
            const monthsLeft = Math.round(daysLeft / 30);

            // Everything that is not the headline: registry + the static facts.
            const registry = [
              ...known.filter((d) => d !== rent && d !== start && d !== end && d !== ejari),
              ...vm.details,
            ];
            const isRef = (label: string) => /DEWA|Ejari|Makani|Property No|Plot/i.test(label);

            return (
              <Section title={t('propertyDetails')}>
                <div className="pt-card" style={{ padding: 0, overflow: 'hidden' }}>
                  {/* ---- Headline: what it earns, and for how long ---- */}
                  {rent && (
                    <div style={{ padding: '20px 20px 18px', borderBottom: `1px solid ${M.hair}` }}>
                      <div style={{ ...eyebrow, fontSize: T.micro - 1, color: M.ink3, fontWeight: 700 }}>
                        {rent.label}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                        <span style={{ fontSize: 30, fontWeight: 800, color: M.black, letterSpacing: '-0.6px' }}>
                          {rent.value}
                        </span>
                        {hasTerm && daysLeft > 0 && (
                          <span style={{
                            fontSize: T.micro, fontWeight: 800, color: M.goldInk, background: M.tint,
                            border: `1px solid ${M.hair}`, borderRadius: 99, padding: '4px 10px', whiteSpace: 'nowrap',
                          }}>
                            {monthsLeft >= 2 ? `${monthsLeft} ${t('pmMonthsLeft')}` : `${daysLeft} ${t('tpDaysLeft')}`}
                          </span>
                        )}
                      </div>

                      {hasTerm && (
                        <div style={{ marginTop: 16 }}>
                          <div style={{ height: 6, borderRadius: 99, background: M.track, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.round(elapsed * 100)}%`, height: '100%', background: M.gold, borderRadius: 99 }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                            <span style={{ fontSize: T.secondary, color: M.ink2 }}>{start?.value}</span>
                            <span style={{ fontSize: T.secondary, color: M.ink2 }}>{end?.value}</span>
                          </div>
                        </div>
                      )}

                      {ejari && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
                          <span style={{ fontSize: T.secondary, color: M.ink3 }}>{ejari.label}</span>
                          <span style={{ fontSize: T.secondary, fontWeight: 700, color: M.black, fontVariantNumeric: 'tabular-nums' }}>
                            {ejari.value}
                          </span>
                          <button
                            onClick={() => { copyText(ejari.value); }}
                            title={t('pmCopyRef')}
                            aria-label={`${t('pmCopyRef')} — ${ejari.label}`}
                            className="pt-tap"
                            style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: M.ink3, lineHeight: 0 }}
                          >
                            <Copy size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ---- The drawer: identifiers, for when a form asks ---- */}
                  {registry.length > 0 && (
                    <details>
                      <summary
                        className="pt-tap"
                        style={{
                          listStyle: 'none', cursor: 'pointer', padding: '15px 20px',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          fontSize: T.body, fontWeight: 700, color: M.black,
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                          <Landmark size={16} color={M.goldInk} />
                          {t('pmRegistryDetails')}
                        </span>
                        <span style={{ fontSize: T.secondary, fontWeight: 700, color: M.ink3 }}>{registry.length}</span>
                      </summary>

                      <div style={{ padding: '0 20px 18px' }}>
                        {registry.map((d) => (
                          <div
                            key={d.label}
                            style={{
                              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                              gap: 16, padding: '10px 0', borderTop: `1px solid ${M.hair}`,
                            }}
                          >
                            <span style={{ fontSize: T.secondary, color: M.ink2, flexShrink: 0 }}>{d.label}</span>
                            <span style={{
                              fontSize: T.secondary, fontWeight: 700, color: M.black, textAlign: 'right',
                              fontVariantNumeric: isRef(d.label) ? 'tabular-nums' : 'normal',
                              wordBreak: 'break-word', display: 'inline-flex', alignItems: 'center', gap: 8,
                            }}>
                              {d.value}
                              {isRef(d.label) && (
                                <button
                                  onClick={() => { copyText(d.value); }}
                                  title={t('pmCopyRef')}
                                  aria-label={`${t('pmCopyRef')} — ${d.label}`}
                                  className="pt-tap"
                                  style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: M.ink3, lineHeight: 0, flexShrink: 0 }}
                                >
                                  <Copy size={13} />
                                </button>
                              )}
                            </span>
                          </div>
                        ))}

                        {/* A gap is information — but it belongs with the
                            identifiers, not next to the rent. */}
                        {missing.length > 0 && (
                          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${M.hair}` }}>
                            <div style={{ fontSize: T.micro, color: M.ink3, marginBottom: 9 }}>{t('pmStillNeeded')}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                              {missing.map((d) => (
                                <span key={d.label} style={{ fontSize: T.secondary, color: M.ink2, background: M.wash, border: `1px solid ${M.hair}`, borderRadius: 99, padding: '6px 12px' }}>{d.label}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* Tenant analytics */}
          {vm.tenancyHistory.length > 0 && (() => {
            const hist = vm.tenancyHistory;
            const retained = hist.filter((h) => h.renewed).length;
            const rate = hist.length ? Math.round((retained / hist.length) * 100) : 0;
            const reasonMap: Record<string, number> = {};
            hist.filter((h) => !h.renewed).forEach((h) => { const r = h.reason || t('other'); reasonMap[r] = (reasonMap[r] || 0) + 1; });
            const reasons = Object.entries(reasonMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
            const total = reasons.reduce((s, r) => s + r.value, 0) || 1;
            const colors = [M.gold, M.goldInk, M.goldDeep, 'rgba(0,0,0,0.55)', 'rgba(0,0,0,0.25)'];
            const R = 48, cx = 58, cy = 58; let ang = -Math.PI / 2;
            const arcs = reasons.map((r, i) => {
              const frac = r.value / total; const a2 = ang + frac * 2 * Math.PI; const large = frac > 0.5 ? 1 : 0;
              const x1 = cx + R * Math.cos(ang), y1 = cy + R * Math.sin(ang), x2 = cx + R * Math.cos(a2), y2 = cy + R * Math.sin(a2); ang = a2;
              return { full: frac >= 0.999, d: `M${cx} ${cy} L${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`, color: colors[i % colors.length] };
            });
            const cell = (label: string, value: string, color: string) => (
              <div style={{ background: M.wash, borderRadius: 12, padding: '12px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: M.ink3 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color, marginTop: 2 }}>{value}</div>
              </div>
            );
            return (
              <Section title={t('pmTenantAnalytics')}>
                <div className="pt-card" style={{ padding: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 18 }}>
                    {cell(t('pmTenants'), String(hist.length + (vm.tenant ? 1 : 0)), M.black)}
                    {cell(t('pmRetained'), String(retained), theme.success)}
                    {cell(t('pmRetention'), `${rate}%`, M.goldInk)}
                  </div>
                  <div style={{ fontSize: T.secondary, fontWeight: 700, color: M.black, marginBottom: 12 }}>{t('pmWhyTenantsLeft')}</div>
                  {reasons.length ? (
                    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                      <svg width={116} height={116} viewBox="0 0 116 116" style={{ flexShrink: 0 }}>
                        {arcs.map((a, i) => a.full ? <circle key={i} cx={cx} cy={cy} r={R} fill={a.color} /> : <path key={i} d={a.d} fill={a.color} />)}
                        <circle cx={cx} cy={cy} r={26} fill="#fff" />
                      </svg>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        {reasons.map((r, i) => (
                          <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.secondary, color: M.ink3, marginBottom: 7 }}>
                            <span style={{ width: 11, height: 11, borderRadius: 3, background: colors[i % colors.length] }} />
                            <span style={{ flex: 1 }}>{r.label}</span>
                            <b style={{ color: M.black }}>{r.value}</b>
                            <span style={{ color: M.ink4 }}>({Math.round((r.value / total) * 100)}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <div style={{ fontSize: T.secondary, color: theme.success, fontWeight: 600 }}>{t('pmAllRenewed')}</div>}
                </div>
              </Section>
            );
          })()}

          </div>

          {/* Privacy note */}
          <div style={{ margin: '22px 16px 0', display: 'flex', gap: 12, padding: 16, borderRadius: 14, background: M.tint, color: M.goldDeep, border: `1px solid ${M.tintEdge}` }}>
            <ShieldCheck size={22} style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: T.secondary, margin: 0, lineHeight: 1.55 }}>
              {t('pmPrivacyNote')}
            </p>
          </div>
        </>
      )}

      {sub === 'documents' && (
        <UnitDocumentsTab
          docs={vm.documents}
          unitId={unitId}
          unitLabelText={vm.name}
          demoMode={demoMode}
          investor={investor}
          onSmartUpload={onSmartUpload}
          smartUploading={smartUploading}
          facts={[
            ...(vm.tenant?.name ? [{ label: t('pmTenant'), value: String(vm.tenant.name) }] : []),
            ...(vm.annualRent ? [{ label: t('pmRentPerYear'), value: `AED ${Number(vm.annualRent).toLocaleString('en-AE')}` }] : []),
            ...(vm.tenant?.ejariNo ? [{ label: t('pmEjariNo'), value: String(vm.tenant.ejariNo) }] : []),
          ]}
        />
      )}
      {sub === 'expenses' && (
        <UnitExpensesTab
          unitId={unitId}
          expenses={[...vm.expenses, ...addedExpenses]}
          facts={factsOf(vm.unit)}
          demoMode={demoMode}
          onAdded={(row) => setAddedExpenses((prev) => [...prev, row])}
        />
      )}
      {sub === 'airbnb' && airbnbUnit && <AirbnbUnitTab unit={airbnbUnit} sample={airbnb.sample} lastSyncAt={airbnb.lastSyncAt} />}
      {sub === 'inspection' && <InspectionTab reports={vm.inspectionReports} demoMode={demoMode} />}
      {sub === 'maintenance' && <MaintenanceTab items={vm.maintenance} demoMode={demoMode} />}
      {sub === 'inventory' && <InventoryTab items={(vm.unit as any)?.inventory} demoMode={demoMode} />}

      <p style={{ textAlign: 'center', fontSize: T.micro, color: M.ink3, marginTop: 18 }}>
        {vm.source === 'mock'
          ? `${t('pmSignedInAs')} ${ownerName || t('pmOwner')} · ${t('pmPreviewData')}`
          : `${t('pmOwner')}: ${vm.ownerName}`}
      </p>
    </div>
  );
};

// ---------------- Inspection sub-tab (real data, sample fallback) ----------------
const InspectionTab: React.FC<{ reports?: any[]; demoMode?: boolean }> = ({ reports, demoMode }) => {
  const { t } = useLanguage();
  const live = !!(reports && reports.length);
  // Sample inspection reports — with dates, inspectors and pass/fail results —
  // used to stand in whenever a unit had none. On a real account that is an
  // inspection history invented for a property nobody has inspected. The
  // showcase keeps it; a real account is told there is nothing yet.
  if (!live && !demoMode) {
    return (
      <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
        <EmptyState icon={ClipboardCheck} title={t('pmNoInspections')} text={t('pmNoInspectionsText')} />
      </div>
    );
  }
  const data = live ? reports! : sampleInspectionReports(t);
  const passed = data.filter((r) => (r.result || '').toLowerCase() === 'pass').length;
  const attention = data.length - passed;
  return (
    <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
      {!live && <SampleNote />}
      <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmInspectionReports')}</div>
        <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 3 }}>{t('pmInspectionSub')}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <StatBox label={t('pmReports')} value={data.length} color={M.goldInk} />
          <StatBox label={t('pmPassed')} value={passed} color={theme.success} />
          <StatBox label={t('pmAttention')} value={attention} color={theme.warning} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {data.map((r, i) => {
          const pass = (r.result || '').toLowerCase() === 'pass';
          return (
            <div key={r.id || i} style={{ ...softCard, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(214,177,96,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ClipboardCheck size={21} color={M.gold} />
                  </div>
                  <div>
                    <div style={{ fontSize: T.body, fontWeight: 800, color: M.black }}>{r.type || t('pmInspection')}</div>
                    <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 1 }}>{fmtTs(r.date)}</div>
                  </div>
                </div>
                <span style={{ fontSize: T.micro - 1, fontWeight: 800, borderRadius: 99, padding: '4px 10px', whiteSpace: 'nowrap', color: pass ? theme.success : theme.warning, background: pass ? theme.successBg : theme.warningBg }}>
                  {pass ? t('pmPass') : (r.result || t('pmAttention'))}
                </span>
              </div>
              {r.notes && <p style={{ fontSize: T.secondary, color: M.ink2, margin: '12px 0 0', lineHeight: 1.5 }}>{r.notes}</p>}
              {(r.hasFile || r.fileUrl) && (
                <a href={`${MEDIA_BASE}${r.fileUrl}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: T.secondary, fontWeight: 800, color: M.goldInk, textDecoration: 'none' }}>
                  <Download size={17} /> {r.fileName || t('pmViewReport')}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Maintenance sub-tab (real data, sample fallback) ----------------
const MaintenanceTab: React.FC<{ items?: any[]; demoMode?: boolean }> = ({ items, demoMode }) => {
  const { t } = useLanguage();
  const live = !!(items && items.length);
  // Same as Inspection: invented jobs, costs and dates are not a placeholder
  // for "no maintenance has been raised on this unit".
  if (!live && !demoMode) {
    return (
      <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
        <EmptyState icon={Wrench} title={t('pmNoMaintenance')} text={t('pmNoMaintenanceText')} />
      </div>
    );
  }
  const data: any[] = live ? items!.map((m) => normMaint(m, t)) : sampleMaintenance(t);
  const done = data.filter((m) => maintStatus(m.status).label === 'Completed').length;
  const open = data.length - done;
  return (
    <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
      {!live && <SampleNote />}
      <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmMaintenance')}</div>
        <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 3 }}>{t('pmMaintSub')}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <StatBox label={t('requests')} value={data.length} color={M.goldInk} />
          <StatBox label={t('pmOpen')} value={open} color={theme.warning} />
          <StatBox label={t('completed')} value={done} color={theme.success} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {data.map((m, i) => {
          const st = maintStatus(m.status);
          return (
            <div key={(m as any).id || i} style={{ ...softCard, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ fontSize: T.body, fontWeight: 800, color: M.black }}>{m.title}</div>
                <span style={{ fontSize: T.micro - 1, fontWeight: 800, borderRadius: 99, padding: '4px 10px', whiteSpace: 'nowrap', color: st.color, background: st.bg }}>{t(st.key)}</span>
              </div>
              {(m as any).detail && <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 6 }}>{(m as any).detail}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: T.secondary, color: M.ink2, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wrench size={15} /> {trData(m.category)}</span>
                {m.date && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={15} /> {m.date}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 14, borderTop: `1px solid ${M.hair}` }}>
                <span style={{ fontSize: T.micro, color: M.ink2 }}>{(m as any).handledBy || t('pmInternalTeam')}</span>
                <span style={{ fontSize: T.body, fontWeight: 800, color: M.black }}>{m.cost ? `AED ${m.cost.toLocaleString('en-AE')}` : '—'}</span>
              </div>
              {(m as any).aiReport && (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 12, background: M.wash, border: `1px solid ${M.hair}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: T.micro, fontWeight: 800, color: M.goldInk, marginBottom: 6 }}>
                    <Sparkles size={14} /> {t('pmMaintReport')}{(m as any).aiGenerated ? ' · AI' : ''}
                  </div>
                  <div style={{ fontSize: T.secondary, color: M.ink2, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{(m as any).aiReport}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------- Maintenance across the whole portfolio ----------------
/**
 * EVERY request on EVERY property, in one list, filtered by status.
 *
 * Maintenance was only ever readable one property at a time, so an owner with
 * ten units had to open ten properties to learn whether anything was waiting
 * on them — and a request raised by a tenant could sit unseen for as long as
 * nobody happened to open that unit. This is the screen that makes "track a
 * request from start to finish" true: raised, in progress, done, all visible
 * without navigating.
 *
 * The rows are REAL. A tenant's complaint and an owner-side maintenance job
 * are the same record — the backend writes both to `complaints` (see
 * pmRoutes: tenantRouter POST /:tenantId/complaints and pmRouter POST
 * /unit/:unitId/maintenance) — so what a tenant reports appears here without
 * anything extra being wired. There is deliberately no sample fallback: an
 * invented repair on a real portfolio is worse than an empty list.
 */
type MaintFilter = 'all' | 'open' | 'inProgress' | 'completed';

const MaintenanceBoard: React.FC<{ units: any[]; onOpenUnit: (unitId: string) => void }> = ({ units, onOpenUnit }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<MaintFilter>('all');

  const rows = React.useMemo(() => {
    const out: any[] = [];
    (units || []).forEach((u: any) => {
      (u.maintenance || []).forEach((m: any) => {
        out.push({
          ...normMaint(m, t),
          unitId: u.id,
          unitName: u.name || u.project || t('pmProperty'),
          // The bucket this row falls in, resolved once from the same mapper
          // the badge uses, so a chip and the badge beside it can never
          // disagree about what "in progress" means.
          bucket: maintStatus(m.status).key === 'completed' ? 'completed'
            : maintStatus(m.status).key === 'inProgress' ? 'inProgress'
            : 'open',
          ts: tsOf(m.createdAt || m.date),
        });
      });
    });
    return out.sort((a, b) => b.ts - a.ts);
  }, [units, t]);

  const count = (f: MaintFilter) => (f === 'all' ? rows.length : rows.filter((r) => r.bucket === f).length);
  const shown = filter === 'all' ? rows : rows.filter((r) => r.bucket === filter);

  if (rows.length === 0) {
    return (
      <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
        <EmptyState icon={Wrench} title={t('pmNoMaintenance')} text={t('pmBoardEmptyText')} />
      </div>
    );
  }

  const FILTERS: { key: MaintFilter; label: string }[] = [
    { key: 'all', label: 'all' },
    { key: 'open', label: 'pmStatusOpen' },
    { key: 'inProgress', label: 'inProgress' },
    { key: 'completed', label: 'completed' },
  ];

  const openCount = count('open') + count('inProgress');

  return (
    // The bottom nav is fixed, so the last row needs clearance or it sits
    // under it with nothing to say it is there.
    <div className="pt-fade" style={{ padding: '18px 16px 96px' }}>
      <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmBoardTitle')}</div>
        <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 3 }}>
          {/* "still open" collided with the Open chip directly below it: the
              header counted everything not yet completed (2) while the chip
              counted the Open bucket alone (1), so the same word gave two
              numbers. "still to close" names the total outstanding without
              borrowing a status label, and reads grammatically at n = 1. */}
          {openCount > 0
            ? t('pmBoardOpenSub').replace('{n}', String(openCount)).replace('{total}', String(rows.length))
            : t('pmBoardClearSub')}
        </div>
      </div>

      <Segmented
        bare
        tabs={FILTERS.map((f) => ({ key: f.key, label: f.label, count: count(f.key) }))}
        value={filter}
        onChange={(k) => setFilter(k as MaintFilter)}
      />

      {shown.length === 0 ? (
        <p style={{ fontSize: T.secondary, color: M.ink2, textAlign: 'center', padding: '26px 16px' }}>
          {t('pmBoardNoneInFilter')}
        </p>
      ) : (
        <div style={{ ...softCard, padding: 8 }}>
          {shown.map((r, i) => {
            const st = maintStatus(r.status);
            return (
              <button
                key={r.id || i}
                onClick={() => onOpenUnit(r.unitId)}
                className="pt-tap"
                style={{
                  // index.css gives every button align-items:center with no
                  // !important, which a column layout INHERITS — children then
                  // size to fit-content and overflow. Both axes are stated.
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
                  gap: 12, width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: 'none', border: 'none',
                  padding: '13px 12px',
                  borderTop: i === 0 ? 'none' : `1px solid ${M.hair}`,
                }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 11, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Wrench size={19} color={M.goldInk} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* The badge shares the TITLE's line and nothing else. Sat
                      outside this column it reserved its width down the whole
                      row, so the line that names the property was squeezed to
                      "Seagate Building 2 · 12 Sept 20…" — the date, on a list
                      sorted by date. */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    {/* WRAPS. On one nowrap line beside a status badge a phone
                        had ~150px for the title and every request read
                        "Bathroom extractor fa…" — precisely the word that says
                        what is broken. Height is the cheap dimension here, the
                        same argument the tab control makes. */}
                    <div style={{ flex: 1, minWidth: 0, fontSize: T.body, fontWeight: 700, color: M.black, lineHeight: 1.3 }}>
                      {r.title}
                    </div>
                    {/* The chevron went with the truncation: the whole row is
                        the target, and the badge already sits where an
                        affordance would. */}
                    <span style={{ fontSize: T.micro - 1, fontWeight: 800, borderRadius: 99, padding: '4px 9px', whiteSpace: 'nowrap', color: st.color, background: st.bg, flexShrink: 0 }}>
                      {t(st.key)}
                    </span>
                  </div>
                  {/* The property is the point of this screen — without it a
                      portfolio-wide list is just a pile of repairs. */}
                  <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.unitName}{r.date ? ` · ${r.date}` : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------- Inventory sub-tab (sample — no backend yet) ----------------
const InventoryTab: React.FC<{ items?: any[]; demoMode?: boolean }> = ({ items, demoMode }) => {
  const { t } = useLanguage();
  const live = !!(items && items.length);

  // There is no inventory field on a unit — not in the payload, not in the
  // admin console, nowhere. This tab therefore printed sampleInventory() for
  // EVERY unit on EVERY account: three rooms and ten items, a sofa, a fridge,
  // a king bed, each with a condition grade, for a property nobody had ever
  // recorded anything against. The note above it said "sample", but the card
  // below still counted "3 Rooms · 10 Items" as though they were facts, and an
  // owner has no way to tell which half to believe.
  //
  // The sample now stays in the showcase, where everything on screen is
  // declared sample. A real account is told the inventory is not recorded yet,
  // which is true, instead of being shown furniture it does not own.
  if (!live && !demoMode) {
    return (
      <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
        <EmptyState icon={Package} title={t('pmNoInventory')} text={t('pmNoInventoryText')} />
      </div>
    );
  }

  const inventory = live ? items! : sampleInventory(t);
  const totalItems = inventory.reduce((s: number, r: any) => s + r.items.reduce((a: number, it: any) => a + it.qty, 0), 0);
  return (
    <div className="pt-fade" style={{ padding: '18px 16px 0' }}>
      {!live && <SampleNote />}
      <div style={{ ...softCard, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.black }}>{t('pmAssetInventory')}</div>
        <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: 3 }}>{t('pmInventorySub')}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <StatBox label={t('pmRooms')} value={inventory.length} color={M.goldInk} />
          <StatBox label={t('pmItems')} value={totalItems} color={M.gold} />
        </div>
      </div>
      {inventory.map((room: any) => (
        <div key={room.room} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: T.secondary, fontWeight: 800, color: M.black, margin: '2px 2px 10px' }}>{room.room}</div>
          <div style={{ ...softCard, padding: 8 }}>
            {room.items.map((it: any, i: number) => (
              <div key={it.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', borderBottom: i < room.items.length - 1 ? `1px solid ${M.hair}` : 'none' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: M.tint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Package size={18} color={M.goldInk} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T.body, fontWeight: 700, color: M.black }}>{it.name}</div>
                  <div style={{ fontSize: T.micro, color: M.ink2, marginTop: 1 }}>{t('pmQty')} {it.qty}</div>
                </div>
                <span style={{ fontSize: T.micro - 1, fontWeight: 800, borderRadius: 99, padding: '3px 10px', color: condColor(it.condition), background: condBg(it.condition) }}>{t(condKey(it.condition))}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// One gold icon family: document kinds are distinguished by their glyph,
// not by giving each its own hue (the palette has one accent).
function docIcon(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('emirates')) return <CreditCard size={22} color={M.goldInk} />;
  if (t.includes('contract') || t.includes('tenancy')) return <FileSignature size={22} color={M.goldInk} />;
  if (t.includes('ejari')) return <ShieldCheck size={22} color={M.goldInk} />;
  if (t.includes('inventory')) return <ClipboardCheck size={22} color={M.goldInk} />;
  return <FileText size={22} color={M.goldInk} />;
}
function docBg(_type: string) {
  return M.tint;
}

// ---------------- Construction card (off-plan units) ----------------
//
// Answers the three questions an owner has about a unit that does not exist
// yet: how far along is it, when do I get the keys, and what will it rent
// for. Every figure names its source — the DLD project registry and the
// developer's own weekly report are not equally authoritative, and an owner
// deciding when to plan a handover trip is entitled to know which one they
// are reading.
const ConstructionCard: React.FC<{ facts: UnitFacts }> = ({ facts }) => {
  const { t } = useLanguage();
  const c = facts.construction || ({} as NonNullable<UnitFacts['construction']>);
  const pct = c.percent != null ? Math.max(0, Math.min(100, Math.round(c.percent))) : null;
  const handover = quarterOf(c.handover);
  const rent = facts.expectedRent && facts.expectedRent.available ? facts.expectedRent : null;
  return (
    <div className="pt-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.body, fontWeight: 700, color: M.ink2 }}>
          <Hammer size={17} color={M.goldInk} /> {t('pmUnderConstruction')}
        </span>
        {pct != null && <span style={{ fontSize: T.cardTitle, fontWeight: 800, color: M.goldInk }}>{pct}%</span>}
      </div>
      {pct != null && (
        <>
          <div className="pt-progress-track" style={{ marginBottom: 8 }}>
            <div className="pt-progress-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${M.goldDeep}, ${M.gold})` }} />
          </div>
          <div style={{ fontSize: T.micro, color: M.ink3, marginBottom: 16 }}>
            {pct}% {t('pmBuilt')}
            {sourceLabel(c.percentSource, t) ? ` · ${sourceLabel(c.percentSource, t)}` : ''}
            {c.lastSync ? ` · ${fmt(c.lastSync)}` : ''}
          </div>
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <div>
          <div style={{ fontSize: T.secondary, color: M.ink2 }}>{t('pmExpectedHandover')}</div>
          <div style={{ fontSize: T.body, fontWeight: 700, color: handover ? M.black : M.ink4, marginTop: 3 }}>
            {handover || t('pmNotStated')}
          </div>
          {handover && sourceLabel(c.handoverSource, t) && (
            <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>{sourceLabel(c.handoverSource, t)}</div>
          )}
        </div>
        <div>
          <div style={{ fontSize: T.secondary, color: M.ink2 }}>{t('pmExpectedRent')}</div>
          <div style={{ fontSize: T.body, fontWeight: 700, color: rent ? M.black : M.ink4, marginTop: 3 }}>
            {rent ? `~${AED(rent.annual || 0)}${t('pmPerYear')}` : t('pmNotStated')}
          </div>
          {rent && (
            <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>
              {t('pmExpectedRentFrom')} {rent.sampleSize} {t('pmRentalContracts')}
              {confidenceLabel(rent.confidence, t) ? ` · ${confidenceLabel(rent.confidence, t)}` : ''}
            </div>
          )}
        </div>
        {facts.serviceCharge?.annual != null && (
          <div>
            <div style={{ fontSize: T.secondary, color: M.ink2 }}>{t('pmServiceCharge')}</div>
            <div style={{ fontSize: T.body, fontWeight: 700, color: M.black, marginTop: 3 }}>
              {AED(facts.serviceCharge.annual)}{t('pmPerYear')}
            </div>
            <div style={{ fontSize: T.micro, color: M.ink3, marginTop: 2 }}>
              {t('pmFromHandover')}
              {sourceLabel(facts.serviceCharge.source, t) ? ` · ${sourceLabel(facts.serviceCharge.source, t)}` : ''}
            </div>
          </div>
        )}
        {c.developer && (
          <div>
            <div style={{ fontSize: T.secondary, color: M.ink2 }}>{t('developer')}</div>
            <div style={{ fontSize: T.body, fontWeight: 700, color: M.black, marginTop: 3 }}>{c.developer}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Property facts card (owner-declared) ----------------
//
// Mortgage status and letting mode are the two facts no registry publishes
// per unit: DLD's mortgage data is anonymised, and short-let bookings happen
// outside this system entirely. So the owner states them once here and every
// screen and report reads them back. Undeclared stays undeclared — a blank is
// honest, a defaulted "No mortgage" is not.
const PropertyFactsCard: React.FC<{ unitId: string; facts: UnitFacts; live: boolean; onSaved: (f: UnitFacts) => void }> = ({ unitId, facts, live, onSaved }) => {
  const { t } = useLanguage();
  const m = facts.mortgage || {};
  const l = facts.letting || {};
  const [mortgage, setMortgage] = useState<'yes' | 'no' | ''>(m.status || '');
  const [lender, setLender] = useState(m.lender || '');
  const [outstanding, setOutstanding] = useState(m.outstanding != null ? String(m.outstanding) : '');
  const [mode, setMode] = useState<'long_term' | 'short_term' | ''>(l.mode || '');
  const [brand, setBrand] = useState(l.brand || '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Re-seed when the unit's saved facts arrive or change underneath us.
  useEffect(() => {
    setMortgage((facts.mortgage && facts.mortgage.status) || '');
    setLender((facts.mortgage && facts.mortgage.lender) || '');
    setOutstanding(facts.mortgage && facts.mortgage.outstanding != null ? String(facts.mortgage.outstanding) : '');
    setMode((facts.letting && facts.letting.mode) || '');
    setBrand((facts.letting && facts.letting.brand) || '');
  }, [facts]);

  const dirty = mortgage !== (m.status || '') || lender !== (m.lender || '')
    || outstanding !== (m.outstanding != null ? String(m.outstanding) : '')
    || mode !== (l.mode || '') || brand !== (l.brand || '');

  const save = async () => {
    setState('saving');
    try {
      const res = await portalFetch<{ facts: UnitFacts }>(`/pm/unit/${unitId}/owner-facts`, {
        method: 'POST',
        body: JSON.stringify({
          mortgageStatus: mortgage,
          mortgageLender: mortgage === 'yes' ? lender : '',
          mortgageOutstanding: mortgage === 'yes' ? outstanding : '',
          lettingMode: mode,
          shortLetBrand: mode === 'short_term' ? brand : '',
        }),
      }, 8000);
      if (res && res.facts) onSaved(res.facts);
      setState('saved');
      window.setTimeout(() => setState('idle'), 2000);
    } catch {
      setState('error');
    }
  };

  const choice = (active: boolean) => ({
    flex: 1, minWidth: 84, padding: '11px 12px', borderRadius: 12, cursor: 'pointer',
    fontSize: T.secondary, fontWeight: 800,
    border: `1px solid ${active ? M.tintEdge : M.hair}`,
    background: active ? M.tint : '#fff',
    color: active ? M.goldDeep : M.ink2,
  } as React.CSSProperties);

  const field: React.CSSProperties = {
    width: '100%', marginTop: 8, padding: '11px 13px', borderRadius: 12,
    border: `1px solid ${M.hair}`, fontSize: T.body, color: M.black, background: '#fff',
  };

  return (
    <div className="pt-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.secondary, fontWeight: 800, color: M.black }}>
        <Landmark size={17} color={M.goldInk} /> {t('pmMortgage')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" disabled={!live} onClick={() => setMortgage('yes')} className="pt-tap" style={choice(mortgage === 'yes')}>{t('pmYes')}</button>
        <button type="button" disabled={!live} onClick={() => setMortgage('no')} className="pt-tap" style={choice(mortgage === 'no')}>{t('pmNo')}</button>
        <button type="button" disabled={!live} onClick={() => setMortgage('')} className="pt-tap" style={choice(mortgage === '')}>{t('pmNotStated')}</button>
      </div>
      {mortgage === 'yes' && (
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <label style={{ fontSize: T.micro, color: M.ink3, fontWeight: 700 }}>
            {t('pmMortgageLender')}
            <input value={lender} disabled={!live} onChange={(e) => setLender(e.target.value)} style={field} />
          </label>
          <label style={{ fontSize: T.micro, color: M.ink3, fontWeight: 700 }}>
            {t('pmMortgageOutstanding')}
            <input value={outstanding} disabled={!live} inputMode="numeric" onChange={(e) => setOutstanding(e.target.value)} style={field} />
          </label>
        </div>
      )}

      <div style={{ height: 1, background: M.hair, margin: '20px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: T.secondary, fontWeight: 800, color: M.black }}>
        <BedDouble size={17} color={M.goldInk} /> {t('pmLettingMode')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" disabled={!live} onClick={() => setMode('long_term')} className="pt-tap" style={choice(mode === 'long_term')}>{t('pmLongLet')}</button>
        <button type="button" disabled={!live} onClick={() => setMode('short_term')} className="pt-tap" style={choice(mode === 'short_term')}>{t('pmShortLet')}</button>
        <button type="button" disabled={!live} onClick={() => setMode('')} className="pt-tap" style={choice(mode === '')}>{t('pmNotStated')}</button>
      </div>
      {mode === 'short_term' && (
        <label style={{ display: 'block', marginTop: 14, fontSize: T.micro, color: M.ink3, fontWeight: 700 }}>
          {t('pmShortLetBrand')}
          <input value={brand} disabled={!live} onChange={(e) => setBrand(e.target.value)} style={field} />
        </label>
      )}

      {live && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button type="button" onClick={save} disabled={!dirty || state === 'saving'} className="pt-tap"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', borderRadius: 99,
              padding: '12px 22px', fontSize: T.body, fontWeight: 800,
              background: dirty ? M.gold : M.track, color: dirty ? '#000' : M.ink4,
              cursor: dirty && state !== 'saving' ? 'pointer' : 'default',
            }}>
            {state === 'saving' ? <RefreshCw size={17} className="pt-rotate" /> : <Check size={17} />} {t('pmSave')}
          </button>
          {state === 'saved' && <span style={{ fontSize: T.secondary, fontWeight: 700, color: M.okInk }}>{t('pmSaved')}</span>}
          {state === 'error' && <span style={{ fontSize: T.secondary, fontWeight: 700, color: theme.danger }}>{t('pmSaveFailed')}</span>}
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ icon: React.ComponentType<any>; label: string; value: string; href?: string; copy?: boolean }> = ({ icon: Icon, label, value, href, copy }) => {
  const { t } = useLanguage();
  const body = (
    <span style={{ fontSize: T.body, fontWeight: 700, color: href ? M.goldInk : M.black, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere', fontVariantNumeric: copy ? 'tabular-nums' : 'normal' }}>
      {value}
    </span>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 14px', background: M.wash, borderRadius: 12 }}>
      <Icon size={19} color={M.goldInk} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: T.secondary, color: M.ink2, flex: 1, minWidth: 0 }}>{label}</span>
      {href ? <a href={href} style={{ textDecoration: 'none', minWidth: 0 }}>{body}</a> : body}
      {copy && (
        <button
          onClick={() => { copyText(value); }}
          title={t('pmCopyRef')}
          aria-label={`${t('pmCopyRef')} — ${label}`}
          className="pt-tap"
          style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: M.ink3, lineHeight: 0, flexShrink: 0 }}
        >
          <Copy size={13} />
        </button>
      )}
    </div>
  );
};

// Section headings are LABELS, not content. They used to be 19px/800 black —
// the same weight as the numbers inside the cards — so eight of them down a
// page all shouted at once and nothing receded. Worse, most of them repeat
// what the card already says ("Financial Overview" above a card headed NET
// INCOME AFTER ALL COSTS; "Current Tenant" above a card showing the tenant's
// name). Demoted to the editorial eyebrow already used inside the cards: still
// 14px for older eyes, but quiet enough that the content carries the page.
const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section style={{ padding: '26px 16px 0' }}>
    <h2 style={{ ...eyebrow, fontSize: 14, fontWeight: 700, color: M.ink3, margin: subtitle ? '0 0 4px' : '0 0 11px' }}>{title}</h2>
    {subtitle && <p style={{ fontSize: T.micro, color: M.ink4, margin: '0 0 11px' }}>{subtitle}</p>}
    {children}
  </section>
);

const StepRow: React.FC<{ step: LifecycleStep; isLast: boolean }> = ({ step, isLast }) => {
  const { t } = useLanguage();
  const keys = STEP_LABEL_KEYS[step.key];
  const Icon = STEP_ICONS[step.key] || Circle;
  const isDone = step.status === 'done';
  const isActive = step.status === 'active';
  const color = isDone ? theme.success : isActive ? M.goldInk : M.ink4;
  const bg = isDone ? theme.successBg : isActive ? M.tint : M.wash;
  // Tightened: 42px tiles with a 26px connector and a description on every row
  // made five pending steps ~270px tall — a lot of page to say "nothing has
  // started". The description is what costs the height and it only earns its
  // place on the step actually in play, so pending steps keep just their name.
  const showDesc = isActive || isDone;
  return (
    <div style={{ display: 'flex', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: 34, height: 34, borderRadius: 11, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: isActive ? `2px solid ${M.goldInk}` : '2px solid transparent' }}>
          {isDone ? <Check size={17} color={color} /> : <Icon size={17} color={color} />}
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 14, background: isDone ? theme.success : M.hair, margin: '2px 0' }} />}
      </div>
      <div style={{ paddingBottom: isLast ? 0 : 12, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 34 }}>
          <span style={{ fontSize: T.body, fontWeight: 700, color: isActive || isDone ? M.black : M.ink2 }}>{keys ? t(keys.label) : step.label}</span>
          {isActive && <span style={{ fontSize: T.micro - 2, fontWeight: 800, color: M.goldInk, background: M.tint, padding: '3px 9px', borderRadius: 99 }}>{t('pmInProgressBadge')}</span>}
          {step.date && <span style={{ fontSize: T.micro, color: M.ink3, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={13} /> {step.date}</span>}
        </div>
        {showDesc && <div style={{ fontSize: T.secondary, color: M.ink2, marginTop: -4 }}>{keys ? t(keys.description) : step.description}</div>}
      </div>
    </div>
  );
};

export default PropertyManagementView;
